// src/pages/Geoportal.js
import React, { useEffect, useRef, useState, useCallback } from 'react';
import Map from 'ol/Map';
import View from 'ol/View';
import TileLayer from 'ol/layer/Tile';
import ImageLayer from 'ol/layer/Image';
import VectorLayer from 'ol/layer/Vector';
import VectorSource from 'ol/source/Vector';
import OSM from 'ol/source/OSM';
import XYZ from 'ol/source/XYZ';
import ImageWMS from 'ol/source/ImageWMS';
import TileWMS from 'ol/source/TileWMS';
import GeoJSON from 'ol/format/GeoJSON';
import { transform, transformExtent } from 'ol/proj';
import { register } from 'ol/proj/proj4';

import { defaults as defaultInteractions } from 'ol/interaction';
import { Select, Draw, Modify } from 'ol/interaction';
import { Fill, Stroke, Style, Circle as CircleStyle, Text, Icon } from 'ol/style';
import Feature from 'ol/Feature';
import Point from 'ol/geom/Point';
import LineString from 'ol/geom/LineString';
import Polygon from 'ol/geom/Polygon';
import { buffer as bufferExtent, getCenter } from 'ol/extent';
import Overlay from 'ol/Overlay';
import proj4 from 'proj4';
import './Geoportal.css';
import Uputstvo from './Uputstvo';
import MailVerificationDialog from '../components/MailVerificationDialog/MailVerificationDialog';

// UTM 34N (Srbija) - glavni koordinatni sistem
proj4.defs('EPSG:32634', '+proj=utm +zone=34 +datum=WGS84 +units=m +no_defs');
register(proj4);

const GEOSERVER_URL = process.env.REACT_APP_GEOSERVER_URL;

// Test GeoServer connectivity
fetch(`${GEOSERVER_URL}/wms?service=WMS&version=1.1.1&request=GetCapabilities`)
  .then(response => {
    if (response.ok) {
      // GeoServer is accessible
    } else {
      console.error('GeoServer returned error:', response.status, response.statusText);
    }
  })
  .catch(error => {
    console.error('GeoServer connection failed:', error);
  });

// Aproksimativne granice Srbije u WGS84 (lon/lat) za inicijalni prikaz bez "blink"-a
const SERBIA_BOUNDS_CONFIG = process.env.REACT_APP_SERBIA_BOUNDS.split(',').map(Number);
const SRBIJA_BOUNDS_WGS84 = [[SERBIA_BOUNDS_CONFIG[0], SERBIA_BOUNDS_CONFIG[1]], [SERBIA_BOUNDS_CONFIG[2], SERBIA_BOUNDS_CONFIG[3]]];
const DESIRED_INITIAL_SCALE = Number(process.env.REACT_APP_INITIAL_SCALE); // 1:1,500,000
// Extent Srbije u EPSG:32634 za fit() - dinamički se prilagođava ekranu
const SERBIA_EXTENT_32634 = transformExtent(
  [SERBIA_BOUNDS_CONFIG[0], SERBIA_BOUNDS_CONFIG[1], SERBIA_BOUNDS_CONFIG[2], SERBIA_BOUNDS_CONFIG[3]],
  'EPSG:4326',
  'EPSG:32634'
);
// OGC standardna vrednost piksela: 0.28mm po pikselu
const OGC_MM_PER_PX = Number(process.env.REACT_APP_OGC_MM_PER_PX);
const OGC_METERS_PER_PX = OGC_MM_PER_PX / 1000; // 0.00028 m/px

const getBoundsCenter = (b) => {
  const [[minX, minY], [maxX, maxY]] = b;
  return [(minX + maxX) / 2, (minY + maxY) / 2];
};


// Scale-based pristup - direktno postavlja razmeru za UTM CRS
const setScaleForView = (view, scaleDenominator) => {
  const metersPerPixel = scaleDenominator * OGC_METERS_PER_PX;
  // Za UTM (EPSG:32634) nema potrebe za cosLat korekciju jer je projektovan CRS
  const resolution = metersPerPixel;
  
  // Postavi resolution direktno
  view.setResolution(resolution);
};

// Scale-based fit - postavlja extent sa određenom razmerom
const fitExtentWithScale = (view, extent, scaleDenominator, duration = 600) => {
  // Centriraj na extent
  const center = getCenter(extent);
  view.animate({
    center: center,
    duration: duration
  }, () => {
    // Postavi razmeru nakon animacije
    setScaleForView(view, scaleDenominator);
  });
};

// Jednaki intervali: vrati niz granica dužine n+1 (min..max podeljeni na n delova)
const equalIntervalBreaks = (minVal, maxVal, numClasses) => {
  const width = (maxVal - minVal) / numClasses;
  const arr = [];
  for (let i = 0; i <= numClasses; i += 1) arr.push(minVal + i * width);
  return arr;
};

const toFixed2 = (x) => {
  const v = Math.round(x * 10) / 10; // 1 decimala
  return (Math.abs(v) < 1e-6 ? 0 : v).toFixed(1);
};

// Helperi za legende (Od/Do) sa jednakim intervalima
const buildOdDoLabel = (from, to) => `Od ${toFixed2(from)} do ${toFixed2(to)}`;
const buildEqualIntervalLegendItems = (minVal, maxVal, colors) => {
  const breaks = equalIntervalBreaks(minVal, maxVal, colors.length);
  const items = [];
  for (let i = 0; i < colors.length; i += 1) {
    const from = breaks[i];
    const to   = breaks[i + 1];
    items.push({ title: buildOdDoLabel(from, to), color: colors[i] });
  }
  return items;
};

// Standardna plava rampa (5 nijansi)
const BLUE_RAMP_5 = ['#f7f7f7', '#cfe1ee', '#9cc5de', '#6fa9ce', '#2166ac'];

// Marker SVG icon (red pin with white circle and green leaf)
const MARKER_SVG = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 120" width="100" height="120">
  <defs>
    <filter id="shadow" x="-20%" y="-10%" width="140%" height="140%">
      <feDropShadow dx="0" dy="4" stdDeviation="4" flood-color="#b03030" flood-opacity="0.3"/>
    </filter>
    <filter id="circle-shadow" x="-20%" y="-20%" width="140%" height="140%">
      <feDropShadow dx="0" dy="2" stdDeviation="3" flood-color="#888" flood-opacity="0.2"/>
    </filter>
    <radialGradient id="pinGradient" cx="38%" cy="30%" r="65%">
      <stop offset="0%" stop-color="#f87171"/>
      <stop offset="60%" stop-color="#ef4444"/>
      <stop offset="100%" stop-color="#dc2626"/>
    </radialGradient>
    <radialGradient id="circleGradient" cx="40%" cy="35%" r="60%">
      <stop offset="0%" stop-color="#ffffff"/>
      <stop offset="100%" stop-color="#f5f5f5"/>
    </radialGradient>
  </defs>
  <path d="M50 5 C28 5 10 23 10 45 C10 68 50 115 50 115 C50 115 90 68 90 45 C90 23 72 5 50 5 Z" fill="url(#pinGradient)" filter="url(#shadow)"/>
  <circle cx="50" cy="42" r="26" fill="url(#circleGradient)" filter="url(#circle-shadow)"/>
  <line x1="50" y1="60" x2="50" y2="40" stroke="#2d8a4e" stroke-width="3.2" stroke-linecap="round"/>
  <path d="M49 48 C44 44 32 42 28 36 C34 32 46 38 49 48 Z" fill="#2d8a4e"/>
  <path d="M51 48 C56 44 68 42 72 36 C66 32 54 38 51 48 Z" fill="#2d8a4e"/>
  <path d="M50 40 C46 34 46 26 50 22 C54 26 54 34 50 40 Z" fill="#2d8a4e"/>
</svg>`;
const MARKER_ICON_SRC = 'data:image/svg+xml;charset=utf-8,' + encodeURIComponent(MARKER_SVG);
const MARKER_ICON_SCALE = 0.45;


// ----------------------------- WMS LAYERS ------------------------------
// Uvek prisutni WMS slojevi (ne pojavljuju se u meniju/legendi)
const EXTRA_WMS_NAMES = ['Agris:srbija'];

// Dodaj uvek prikazan sloj (npr. administrativna granica Srbije)
const addAlwaysOnLayer = (mapObj) => {
  if (!mapObj) return;
  
  const srbijaLayer = new ImageLayer({
    source: new ImageWMS({
      url: GEOSERVER_URL + '/wms',
      params: {
        'LAYERS': 'Agris:srbija',
        'TILED': true,
        'VERSION': '1.1.1',
        'SRS': 'EPSG:32634',
        'FORMAT': 'image/png',
        'TRANSPARENT': true
      },
      serverType: 'geoserver',
      crossOrigin: 'anonymous',
      projection: 'EPSG:32634',
      transition: 0
    }),
    visible: true,
    opacity: 1.0
  });
  
  srbijaLayer.set('id', 'srbija');
  mapObj.addLayer(srbijaLayer);
};

const WMS_LAYERS = [
  // ===== RASTERS (klimatologija) =====
  { id: 'razlika_mraznih_91_20_vs_61_90', title: 'Razlika srednjeg broja mraznih dana', wmsName: 'Agris:Razlika srednjeg broja mraznih dana (1991-2020 - 1961-1990)' },
  { id: 'razlika_tropskih_91_20_vs_61_90', title: 'Razlika srednjeg broja tropskih dana', wmsName: 'Agris:Razlika srednjeg broja tropskih dana (1991-2020 - 1961-1990)' },
  { id: 'razlika_padavina_91_20_vs_61_90', title: 'Razlika srednjih godišnjih padavina', wmsName: 'Agris:Razlika srednjih godišnjih količina padavina (1991-2020 - 1961-1990)' },
  { id: 'razlika_temp_91_20_vs_61_90',     title: 'Razlika srednje godišnje temperature',     wmsName: 'Agris:Razlika srednjih godišnjih temperatura (1991-2020 - 1961-1990)' },

  { id: 'padavine_61_90', title: 'Srednje godišnje padavine 1961_1990', wmsName: 'Agris:Srednja godišnja količina padavina (1961-1990)' },
  { id: 'padavine_91_20', title: 'Srednje godišnje padavine 1991_2020', wmsName: 'Agris:Srednja godišnja količina padavina (1991-2020)' },

  { id: 'temp_61_90', title: 'Srednja godišnja temperatura 1961_1990', wmsName: 'Agris:Srednja godišnja temperatura (1961-1990)' },
  { id: 'temp_91_20', title: 'Srednja godišnja temperatura 1991_2020', wmsName: 'Agris:Srednja godišnja temperatura (1991-2020)' },

  { id: 'mrazni_61_90', title: 'Srednji broj mraznih dana 1961_1990', wmsName: 'Agris:Srednji broj mraznih dana (1961-1990)' },
  { id: 'mrazni_91_20', title: 'Srednji broj mraznih dana 1991_2020', wmsName: 'Agris:Srednji broj mraznih dana (1991-2020)' },

  { id: 'tropski_61_90', title: 'Srednji broj tropskih dana 1961_1990', wmsName: 'Agris:Srednji broj tropskih dana (1961-1990)' },
  { id: 'tropski_91_20', title: 'Srednji broj tropskih dana 1991_2020', wmsName: 'Agris:Srednji broj tropskih dana (1991-2020)' },

  // ===== RASTERS (VEGETACIONI PERIOD APR–SEP) =====
  { id: 'razlika_padavina_veg_91_20_vs_61_90',
    title: 'Razlika srednjih količina padavina',
    wmsName: 'Agris:Razlika srednja količina padavina u vegetacionom periodu april-septembar (1991-2020 - 1961-1990)' },

  { id: 'razlika_temp_veg_91_20_vs_61_90',
    title: 'Razlika srednje temperature',
    wmsName: 'Agris:Razlika srednja temperatura vazduha u vegetacionom periodu april-septembar (1991-2020 - 1961-1990)' },

  { id: 'padavine_veg_61_90',
    title: 'Srednja količina padavina 1961_1990',
    wmsName: 'Agris:Srednja količina padavina u vegetacionom periodu april-septembar (1961-1990)' },

  { id: 'padavine_veg_91_20',
    title: 'Srednja količina padavina 1991_2020',
    wmsName: 'Agris:Srednja količina padavina u vegetacionom periodu april-septembar (1991-2020)' },

  { id: 'temp_veg_61_90',
    title: 'Srednja temperatura 1961_1990',
    wmsName: 'Agris:Srednja temperatura vazduha u vegetacionom periodu april-septembar (1961-1990)' },

  { id: 'temp_veg_91_20',
    title: 'Srednja temperatura 1991_2020',
    wmsName: 'Agris:Srednja temperatura vazduha u vegetacionom periodu april-septembar (1991-2020)' },

  { id: 'dni_gt10_veg_61_90',
    title: 'Srednji broj dana padavine do 10 mm 1961_1990',
    wmsName: 'Agris:Srednji broj dana sa količinom padavina većom od 10.0 mm u vegetacionom periodu april-septembar (1961-1990)' },

  { id: 'dni_gt10_veg_91_20',
    title: 'Srednji broj dana padavine do 10 mm 1991_2020',
    wmsName: 'Agris:Srednji broj dana sa količinom padavina većom od 10.0 mm u vegetacionom periodu april-septembar (1991-2020)' },

 // ===== TVOJI VEKTORI =====
{ id: 'dren',        title: 'Drenažna sposobnost zemljišta',        wmsName: 'Agris:Dren' },
{ id: 'dren_spos_zem', title: 'Ugroženost zemljišta navodnjavanjem', wmsName: 'Agris:Ugrozenost' },
{ id: 't_29',        title: 'T_29',                                 wmsName: 'Agris:T_29' },
{ id: 'ugrozenost',  title: 'IWQI-DTD klasifikacija',                wmsName: 'Agris:Ugrozenost' },
{ id: 'pogodnost_zem_navod', title: 'Pogodnost zemljišta za navodnavanje', wmsName: 'Agris:pogodnost_zem_navod' },
{ id: 'iwqi_dtd',    title: 'IWQI-DTD klasifikacija',                wmsName: 'Agris:IWQI-DTD' },
{ id: 'ugr_zem_navod', title: 'Ugroženost zemljišta navodnjavanjem', wmsName: 'Agris:Ugrozenost' },
{ id: 'badem',       title: 'Badem',                                wmsName: 'Agris:badem' },
{ id: 'borovnica',   title: 'Borovnica',                            wmsName: 'Agris:borovnica' },
{ id: 'breskva',     title: 'Breskva',                              wmsName: 'Agris:breskva' },
{ id: 'crn_ribizla', title: 'Crna ribizla',                          wmsName: 'Agris:crn_ribizla' },
{ id: 'crv_ribizla', title: 'Crvena ribizla',                        wmsName: 'Agris:crv_ribizla' },
{ id: 'dunja',       title: 'Dunja',                                wmsName: 'Agris:dunja' },
{ id: 'jabuka',      title: 'Jabuka',                               wmsName: 'Agris:jabuka' },
{ id: 'jagoda',      title: 'Jagoda',                               wmsName: 'Agris:jagoda' },
{ id: 'kajsija',     title: 'Kajsija',                              wmsName: 'Agris:kajsija' },
{ id: 'ko_parcele',  title: 'Parcele',                              wmsName: 'Agris:ko_parcele' },
{ id: 'ko_vojvodina', title: 'KO Vojvodina',                        wmsName: 'Agris:ko_vojvodina' },
{ id: 'kruska',      title: 'Kruška',                               wmsName: 'Agris:kruska' },
{ id: 'kupina',      title: 'Kupina',                               wmsName: 'Agris:kupina' },
{ id: 'leska',       title: 'Leska',                                wmsName: 'Agris:leska' },
{ id: 'malina',      title: 'Malina',                               wmsName: 'Agris:malina' },
{ id: 'ogrozd',      title: 'Ogrozd',                               wmsName: 'Agris:ogrozd' },
{ id: 'orah',        title: 'Orah',                                 wmsName: 'Agris:orah' },
{ id: 'pedoloska',   title: 'Pedološka karta',                       wmsName: 'Agris:pedoloska' },

{ id: 'kukuruz',     title: 'Kukuruz',                              wmsName: 'Agris:kukuruz' },
{ id: 'psenica',     title: 'Pšenica',                              wmsName: 'Agris:psenica' },
{ id: 'soja',        title: 'Soja',                                 wmsName: 'Agris:soja' },
{ id: 'suncokret',   title: 'Suncokret',                            wmsName: 'Agris:suncokret' },

{ id: 'sljiva',      title: 'Šljiva',                               wmsName: 'Agris:sljiva' },
{ id: 'stanice',     title: 'Stanice',                              wmsName: 'Agris:stanice' },
{ id: 'tresnja',     title: 'Trešnja',                              wmsName: 'Agris:tresnja' },
{ id: 'visnja',      title: 'Višnja',                               wmsName: 'Agris:visnja' },
{ id: 'visnja_obl',  title: 'Višnja oblacinska',                    wmsName: 'Agris:visnja_obl' },
{ id: 'wrb',         title: 'WRB',                                  wmsName: 'Agris:wrb' },
{ id: 'wrb2014',     title: 'WRB2014',                              wmsName: 'Agris:wrb2014' },

// ===== LOVĆENAC PRECISION AGRICULTURE LAYERS =====
{ id: 'varijabilna_setva', title: 'Varijabilna setva', wmsName: 'Agris:varijabilna_setva' },

// ===== ČEREVIĆ PRECISION AGRICULTURE LAYERS =====
{ id: 'of_cerevic', title: 'Ortofoto Čerević', wmsName: 'Agris:OF_F_CerevicRGB' },
{ id: 'ndvi_cerevic', title: 'NDVI Čerević', wmsName: 'Agris:NDVI.rgb2' },
{ id: 'gndvi_cerevic', title: 'GNDVI Čerević', wmsName: 'Agris:GNDVI.rgb' },

// ===== RIVICA PRECISION AGRICULTURE LAYERS =====
{ id: 'parcela_rivica', title: 'Parcela', wmsName: 'Agris:parcela' },
{ id: 'ndre1', title: 'NDRE 05 maj 2023', wmsName: 'Agris:NDRE1' },
{ id: 'ndre2', title: 'NDRE 29 maj 2023', wmsName: 'Agris:NDRE2' },
{ id: 'ndvi1', title: 'NDVI 05 maj 2023', wmsName: 'Agris:NDVI1' },
{ id: 'ndvi2', title: 'NDVI 29 maj 2023', wmsName: 'Agris:NDVI2' },
{ id: 'of1',   title: 'Ortofoto 05 maj 2023',   wmsName: 'Agris:OF1' },
{ id: 'of2',   title: 'Ortofoto 29 maj 2023',   wmsName: 'Agris:OF2' },

// ===== TEMERIN PRECISION AGRICULTURE LAYERS =====
{ id: 'of_12jun23', title: 'Ortofoto 12 jun 2023', wmsName: 'Agris:OF_12jun23' },
{ id: 'of_14jul23', title: 'Ortofoto 14 jul 2023', wmsName: 'Agris:OF_14jul23' },
{ id: 'of_17avg23', title: 'Ortofoto 17 avgust 2023', wmsName: 'Agris:OF_17avg23' },
{ id: 'ndvi_temerin_12jun23', title: 'NDVI 12 jun 2023', wmsName: 'Agris:NDVI_Temerin_12jun23' },
{ id: 'ndvi_temerin_14jul23', title: 'NDVI 14 jul 2023', wmsName: 'Agris:NDVI_Temerin_14jul23' },
{ id: 'ndvi_temerin_17avg23', title: 'NDVI 17 avg 2023', wmsName: 'Agris:NDVI_Temerin_17avg23' },

// ===== KARLOVCI PRECISION AGRICULTURE LAYERS =====
{ id: 'of_02apr20', title: 'Ortofoto 02. april 2020.', wmsName: 'Agris:Vinograd' },
{ id: 'of_21avg20', title: 'Ortofoto 21. avgust 2020.', wmsName: 'Agris:OF_21avg20' },
{ id: 'csm_21avg20', title: 'Visina vegetacije 21. avgust 2020.', wmsName: 'Agris:CSM_21avg20' },
{ id: 'sk_dtm', title: 'Nadmorske visine', wmsName: 'Agris:sk_dtm' },
];

// ----------------------------- SOIL LAYERS ------------------------------
const SOIL_LAYERS = [
  { id: 'pedoloska',           title: 'Pedološka karta',                         wmsName: 'Agris:pedoloska',           field: 'TIP2' },
  { id: 'pogodnost_zem_navod', title: 'Pogodnost zemljišta za navodnavanje',     wmsName: 'Agris:pogodnost_zem_navod', field: 'IRIGABILNA' },
  { id: 'dren',                title: 'Drenažna sposobnost zemljišta',          wmsName: 'Agris:Dren',                field: 'DRENAZNA_K' },
  { id: 'wrb2014',             title: 'WRB2014',                                 wmsName: 'Agris:wrb2014',             field: 'WRB14_RSG' },
];

// ----------------------------- MELIORATION LAYERS ------------------------------
const MELIORATION_LAYERS = [
  { id: 'dren',                title: 'Drenažna sposobnost zemljišta',          wmsName: 'Agris:Dren',                field: 'DRENAZNA_K' },
  { id: 'pogodnost_zem_navod', title: 'Pogodnost zemljišta za navodnavanje',     wmsName: 'Agris:pogodnost_zem_navod', field: 'IRIGABILNA' },
  { id: 'ugrozenost',          title: 'IWQI-DTD klasifikacija',                  wmsName: 'Agris:Ugrozenost',          field: 'IWQI_DTD' },
  { id: 'ugr_zem_navod',       title: 'Ugroženost zemljišta navodnjavanjem',    wmsName: 'Agris:Ugrozenost',          field: '' },
  { id: 'iwqi_dtd',             title: 'IWQI-DTD klasifikacija',                 wmsName: 'Agris:IWQI-DTD',            field: 'IWQI_DTD' },
];

// ----------------------------- FARMING LAYERS ------------------------------
const FARMING_LAYERS = [
  { id: 'suncokret', title: 'Suncokret', wmsName: 'Agris:suncokret', field: 'Suncokret' },
  { id: 'psenica',   title: 'Pšenica',   wmsName: 'Agris:psenica',   field: 'Psenica' },
  { id: 'soja',      title: 'Soja',      wmsName: 'Agris:soja',      field: 'Soja' },
  { id: 'kukuruz',   title: 'Kukuruz',   wmsName: 'Agris:kukuruz',   field: 'Kukuruz' },
];

// ----------------------------- PRECISION AGRICULTURE LAYERS ------------------------------
const PRECISION_AGRICULTURE_LAYERS = [
  { id: 'ndre1', title: 'NDRE1', wmsName: 'Agris:NDRE1', field: 'NDRE1' },
  { id: 'ndre2', title: 'NDRE2', wmsName: 'Agris:NDRE2', field: 'NDRE2' },
  { id: 'ndvi1', title: 'NDVI1', wmsName: 'Agris:NDVI1', field: 'NDVI1' },
  { id: 'ndvi2', title: 'NDVI2', wmsName: 'Agris:NDVI2', field: 'NDVI2' },
  { id: 'of1',   title: 'OF1',   wmsName: 'Agris:OF1',   field: 'OF1' },
  { id: 'of2',   title: 'OF2',   wmsName: 'Agris:OF2',   field: 'OF2' },
  // Temerin layers
  { id: 'of_12jun23', title: 'OF 12. jun 2023', wmsName: 'Agris:OF_12jun23', field: 'OF_12jun23' },
  { id: 'of_14jul23', title: 'OF 14. jul 2023', wmsName: 'Agris:OF_14jul23', field: 'OF_14jul23' },
  { id: 'of_17avg23', title: 'OF 17. avg 2023', wmsName: 'Agris:OF_17avg23', field: 'OF_17avg23' },
  { id: 'ndvi_temerin_12jun23', title: 'NDVI Temerin 12. jun 2023', wmsName: 'Agris:NDVI_Temerin_12jun23', field: 'NDVI_Temerin_12jun23' },
  { id: 'ndvi_temerin_14jul23', title: 'NDVI Temerin 14. jul 2023', wmsName: 'Agris:NDVI_Temerin_14jul23', field: 'NDVI_Temerin_14jul23' },
  { id: 'ndvi_temerin_17avg23', title: 'NDVI Temerin 17. avg 2023', wmsName: 'Agris:NDVI_Temerin_17avg23', field: 'NDVI_Temerin_17avg23' },
];

// ----------------------------- LAYER GROUPS ------------------------------
const LAYER_GROUPS = [
  {
    id: 'katastar',
    title: 'Katastar',
    layers: ['ko_parcele']
  },
  {
    id: 'zemljiste',
    title: 'Zemljište',
    layers: ['pedoloska', 'pogodnost_zem_navod', 'dren', 'wrb2014']
  },
  {
    id: 'vocarstvo',
    title: 'Voćarstvo',
    layers: ['visnja_obl', 'visnja', 'tresnja', 'sljiva', 'orah', 'ogrozd', 'malina', 'leska', 'kruska', 'jagoda', 'jabuka', 'dunja', 'crv_ribizla', 'crn_ribizla', 'breskva', 'borovnica', 'badem']
  },
  {
    id: 'ratarstvo',
    title: 'Ratarstvo',
    layers: ['suncokret', 'psenica', 'soja', 'kukuruz']
  },
  {
    id: 'meteorologija',
    title: 'Meteorologija',
    layers: ['stanice', 'razlika_mraznih_91_20_vs_61_90', 'razlika_tropskih_91_20_vs_61_90', 'razlika_padavina_91_20_vs_61_90', 'razlika_temp_91_20_vs_61_90', 'padavine_61_90', 'padavine_91_20', 'temp_61_90', 'temp_91_20', 'mrazni_61_90', 'mrazni_91_20', 'tropski_61_90', 'tropski_91_20']
  },
  {
    id: 'vegetacioni_period',
    title: 'Vegetacioni period',
    layers: ['razlika_padavina_veg_91_20_vs_61_90', 'razlika_temp_veg_91_20_vs_61_90', 'padavine_veg_61_90', 'padavine_veg_91_20', 'temp_veg_61_90', 'temp_veg_91_20', 'dni_gt10_veg_61_90', 'dni_gt10_veg_91_20']
  },
  {
    id: 'melioracije',
    title: 'Melioracije',
    layers: ['dren', 'pogodnost_zem_navod', 'ugrozenost', 'ugr_zem_navod', 'iwqi_dtd']
  },
  {
    id: 'agrotehnika',
    title: 'Agro-tehnika i precizna poljoprivreda',
    subgroups: [
      {
        id: 'lovcenac',
        title: 'Lovćenac',
        layers: ['varijabilna_setva']
      },
      {
        id: 'rivica',
        title: 'Rivica',
        layers: ['parcela_rivica', 'of1', 'of2', 'ndvi1', 'ndvi2', 'ndre1', 'ndre2']
      },
      {
        id: 'cerevic',
        title: 'Čerević',
        layers: ['of_cerevic', 'ndvi_cerevic', 'gndvi_cerevic']
      }
    ]
  },
  {
    id: 'geodezija_gis',
    title: 'Geodezija, GIS i Daljinska detekcija',
    subgroups: [
      {
        id: 'temerin',
        title: 'Temerin',
        layers: ['of_12jun23', 'of_14jul23', 'of_17avg23', 'ndvi_temerin_12jun23', 'ndvi_temerin_14jul23', 'ndvi_temerin_17avg23']
      },
      {
        id: 'karlovci',
        title: 'Karlovci',
        layers: ['of_02apr20', 'of_21avg20', 'csm_21avg20', 'sk_dtm']
      }
    ]
  }
];

// ===== METEO RASTERS =====
const METEO_RASTER_LAYERS = LAYER_GROUPS
  .find(g => g.id === 'meteorologija').layers
  .filter(id => id !== 'stanice') // stanice su vektor, preskačemo
  .map(id => {
    const def = WMS_LAYERS.find(l => l.id === id);
    return { id: def.id, title: def.title, wmsName: def.wmsName };
  });

// ===== VEGETATION PERIOD RASTERS =====
const VEGETATION_RASTER_LAYERS = LAYER_GROUPS
  .find(g => g.id === 'vegetacioni_period').layers
  .map(id => {
    const def = WMS_LAYERS.find(l => l.id === id);
    return { id: def.id, title: def.title, wmsName: def.wmsName };
  });

// Pomoćna ekstrakcija vrednosti piksela iz GeoServer JSON-a
const extractRasterValue = (props) => {
  const candidates = [
    'GRAY_INDEX', 'GRAY_INDEX_1',
    'value', 'VALUE', 'Value',
    'Band1', 'band1', 'band_1',
    'RED', 'GREEN', 'BLUE'
  ];
  for (const k of candidates) {
    if (props?.[k] !== undefined && props?.[k] !== null) return props[k];
  }
  // fallback: prvi numerički atribut
  for (const [k, v] of Object.entries(props || {})) {
    const num = typeof v === 'number' ? v : Number(v);
    if (!Number.isNaN(num)) return num;
  }
  return null;
};

// Mapiranje vrednosti za ugrozenost (IWQI_DTD)
const mapUgrozenostValue = (value) => {
  if (!value) return value;
  
  const strValue = String(value).trim();
  
  // Mapiranje na osnovu vrednosti iz IWQI_DTD kolone
  switch (strValue) {
    case 'I':
    case '1':
      return 'Bez opasnosti';
    case 'II':
    case '2':
      return 'Moguca opasnost';
    case 'III':
    case '3':
      return 'Niska opasnost';
    case 'IV':
    case '4':
      return 'Visoka opasnost';
    default:
      return strValue; // Ako nije prepoznata vrednost, vrati originalnu
  }
};

// --- Mini ikonice za dugmad (inline SVG) ---
const IconSingleMap = (props) => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" {...props}>
    <rect x="3" y="5" width="18" height="14" rx="2" ry="2" stroke="currentColor" strokeWidth="2"/>
  </svg>
);

const IconDualMaps = ({ orientation = 'vertical', ...props }) => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" {...props}>
    <rect x="3" y="5" width="18" height="14" rx="2" ry="2" stroke="currentColor" strokeWidth="2"/>
    {orientation === 'vertical'
      ? <line x1="12" y1="5" x2="12" y2="19" stroke="currentColor" strokeWidth="2"/>
      : <line x1="3" y1="12" x2="21" y2="12" stroke="currentColor" strokeWidth="2"/>}
  </svg>
);

const IconSwipe = ({ orientation = 'vertical', ...props }) => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" {...props}>
    <rect x="3" y="5" width="18" height="14" rx="2" ry="2" stroke="currentColor" strokeWidth="2"/>
    {orientation === 'vertical' ? (
      <>
        <line x1="12" y1="5" x2="12" y2="19" stroke="currentColor" strokeWidth="2"/>
        <polyline points="10,12 8,10 8,14" fill="none" stroke="currentColor" strokeWidth="2" />
        <polyline points="14,12 16,10 16,14" fill="none" stroke="currentColor" strokeWidth="2" />
      </>
    ) : (
      <>
        <line x1="3" y1="12" x2="21" y2="12" stroke="currentColor" strokeWidth="2"/>
        <polyline points="12,10 10,8 14,8" fill="none" stroke="currentColor" strokeWidth="2" />
        <polyline points="12,14 10,16 14,16" fill="none" stroke="currentColor" strokeWidth="2" />
      </>
    )}
  </svg>
);

const IconLens = (props) => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" {...props}>
    <circle cx="10" cy="10" r="6" stroke="currentColor" strokeWidth="2"/>
    <line x1="14.5" y1="14.5" x2="20" y2="20" stroke="currentColor" strokeWidth="2" />
  </svg>
);

const IconLayersRound = (props) => (
  <img src={process.env.PUBLIC_URL + "/slike/layer.png"} alt="Layers" style={{ width: '27.5px', height: '27.5px' }} {...props} />
);

const IconRulerRound = (props) => (
  <img src={process.env.PUBLIC_URL + "/slike/ruler.png"} alt="Ruler" style={{ width: '27.5px', height: '27.5px' }} {...props} />
);

const IconLegendRound = (props) => (
  <img src={process.env.PUBLIC_URL + "/slike/legend.png"} alt="Legend" style={{ width: '27.5px', height: '27.5px' }} {...props} />
);

const IconDownloads = (props) => (
  <img src={process.env.PUBLIC_URL + "/slike/downloads.png"} alt="Downloads" style={{ width: '20px', height: '20px' }} {...props} />
);

const Geoportal = () => {
  // Reorder state for groups and layers (secondary menu ordering)
  const [groupOrder, setGroupOrder] = useState(() => LAYER_GROUPS.map(g => g.id));
  const [groupLayersOrder, setGroupLayersOrder] = useState(() => {
    const initial = {};
    LAYER_GROUPS.forEach(g => { 
      if (g.layers) {
        initial[g.id] = [...g.layers]; 
      } else if (g.subgroups) {
        // For groups with subgroups, create entries for each subgroup
        g.subgroups.forEach(sg => {
          initial[`${g.id}.${sg.id}`] = [...sg.layers];
        });
      }
    });
    return initial;
  });

  /* ---------- Layer ordering mode ---------- */
  const [useFixedLayerOrder, setUseFixedLayerOrder] = useState(true); // true = fixed order, false = dynamic menu order


  /* ---------- Map refs ---------- */
  const mapContainerA = useRef(null);
  const mapContainerB = useRef(null);
  const mapA = useRef(null);
  const mapB = useRef(null);

  /* ---------- View modes ---------- */
  const [viewMode, setViewMode] = useState('single');

  /* ---------- Base layers & visibility per map ---------- */
  const [baseLayerA, setBaseLayerA] = useState('osm');
  const [baseLayerB, setBaseLayerB] = useState('osm');

  const initVisibility = WMS_LAYERS.reduce((acc, l) => ({ ...acc, [l.id]: false }), {});
  const [visibilityA, setVisibilityA] = useState({ ...initVisibility });
  const [visibilityB, setVisibilityB] = useState({ ...initVisibility });
  


  /* ---------- Layer opacity state ---------- */
  const initOpacity = WMS_LAYERS.reduce((acc, l) => ({ ...acc, [l.id]: 1.0 }), {});
  const [opacityA, setOpacityA] = useState({ ...initOpacity });
  const [opacityB, setOpacityB] = useState({ ...initOpacity });

  /* ---------- Ciljna mapa po sloju (A ili B) ---------- */
  const initTargets = WMS_LAYERS.reduce((acc, l) => ({ ...acc, [l.id]: 'A' }), {});
  const [layerTarget, setLayerTarget] = useState(initTargets);

  /* ---------- Koju mapu meniji kontrolišu ---------- */
  const [activeMap, setActiveMap] = useState('A');

  /* ---------- UPUTSTVO OVERLAY ---------- */
  const [showUputstvo, setShowUputstvo] = useState(false);

  /* ---------- MENI STATE ---------- */
  const [isPrimaryMenuOpen,   setIsPrimaryMenuOpen]   = useState(false);
  const [isSecondaryMenuOpen, setIsSecondaryMenuOpen] = useState(false);
  const [selectedPrimaryItem, setSelectedPrimaryItem] = useState(null);
  
  const openSecondary = (key) => {
    // If the same item is already selected and menu is open, close it
    if (selectedPrimaryItem === key && isSecondaryMenuOpen) {
      setIsSecondaryMenuOpen(false);
      setSelectedPrimaryItem(null);
      deactivateMeasurementTool();
    } else {
      // Deactivate measurement when switching away from 'izmeri'
      if (selectedPrimaryItem === 'izmeri' && key !== 'izmeri') {
        deactivateMeasurementTool();
      }
      setSelectedPrimaryItem(key);
      setIsSecondaryMenuOpen(true);
    }
  };
  
  /* ---------- IZMERI STATE ---------- */
  const [selectedMeasurementTool, setSelectedMeasurementTool] = useState(null); // 'point' | 'line' | 'polygon' | null
  const [placedPoints, setPlacedPoints] = useState([]); // Array of {id, lng, lat, x, y} objects
  const [drawnLines, setDrawnLines] = useState([]); // Array of line objects
  const [currentLine, setCurrentLine] = useState(null); // Currently being drawn line
  const [drawnPolygons, setDrawnPolygons] = useState([]); // Array of polygon objects
  const [currentPolygon, setCurrentPolygon] = useState(null); // Currently being drawn polygon
  
  // Performance optimization: Cache measurement layers to avoid repeated searches
  const measurementLayersCache = useRef({
    pointsA: null, pointsB: null,
    linesA: null, linesB: null, 
    polygonsA: null, polygonsB: null
  });
  
  // Debounce state for measurement operations
  const measurementDebounceRef = useRef(null);

  // Optimized helper functions for measurement layers
  const getMeasurementLayer = useCallback((mapObj, layerType, create = true) => {
    if (!mapObj) return null;
    
    const mapKey = mapObj === mapA.current ? 'A' : 'B';
    const cacheKey = `${layerType}${mapKey}`;
    
    // Return cached layer if available and still exists in map
    if (measurementLayersCache.current[cacheKey]) {
      const cachedLayer = measurementLayersCache.current[cacheKey];
      const mapLayers = mapObj.getLayers().getArray();
      if (mapLayers.includes(cachedLayer)) {
        return cachedLayer;
      }
      // Layer was removed, clear cache
      measurementLayersCache.current[cacheKey] = null;
    }
    
    // Search for existing layer
    const layerName = `measurement-${layerType}`;
    const existingLayer = mapObj.getLayers().getArray().find(layer => layer.get('name') === layerName);
    
    if (existingLayer) {
      measurementLayersCache.current[cacheKey] = existingLayer;
      return existingLayer;
    }
    
    if (!create) return null;
    
    // Create new layer
    const source = new VectorSource({ features: [] });
    let style;
    
    if (layerType === 'points') {
      style = new Style({
        image: new CircleStyle({
          radius: 6,
          fill: new Fill({ color: '#ffffff' }),
          stroke: new Stroke({ color: '#000000', width: 2 })
        })
      });
    } else if (layerType === 'lines') {
      style = (feature) => {
        const geometry = feature.getGeometry();
        if (!geometry) return new Style();
        
        const isVertex = feature.get('isVertex');
        if (isVertex) {
          return new Style({
            image: new CircleStyle({
              radius: 4,
              fill: new Fill({ color: '#ffffff' }),
              stroke: new Stroke({ color: '#000000', width: 1 })
            })
          });
        }
        
        const isCurrentLine = feature.get('isCurrentLine');
        return [
          // Crna ivica (šira)
          new Style({
            stroke: new Stroke({
              color: '#000000',
              width: isCurrentLine ? 5 : 4,
              lineDash: isCurrentLine ? [5, 5] : undefined
            })
          }),
          // Bela linija (uža)
          new Style({
            stroke: new Stroke({
              color: '#ffffff',
              width: isCurrentLine ? 3 : 2,
              lineDash: isCurrentLine ? [5, 5] : undefined
            })
          })
        ];
      };
    } else if (layerType === 'polygons') {
      style = (feature) => {
        const geometry = feature.getGeometry();
        if (!geometry) return new Style();
        
        const isVertex = feature.get('isVertex');
        if (isVertex) {
          return new Style({
            image: new CircleStyle({
              radius: 4,
              fill: new Fill({ color: '#ffffff' }),
              stroke: new Stroke({ color: '#000000', width: 1 })
            })
          });
        }
        
        const isCurrentPolygon = feature.get('isCurrentPolygon');
        return [
          // Crna ivica (šira)
          new Style({
            stroke: new Stroke({
              color: '#000000',
              width: isCurrentPolygon ? 3 : 2,
              lineDash: isCurrentPolygon ? [5, 5] : undefined
            })
          }),
          // Bela popuna i bela ivica (uža)
          new Style({
            fill: new Fill({ color: 'rgba(255, 255, 255, 0.2)' }),
            stroke: new Stroke({
              color: '#ffffff',
              width: isCurrentPolygon ? 2 : 1,
              lineDash: isCurrentPolygon ? [5, 5] : undefined
            })
          })
        ];
      };
    }
    
    const newLayer = new VectorLayer({
      source: source,
      style: style,
      updateWhileAnimating: false,
      updateWhileInteracting: false,
      zIndex: 9999
    });

    newLayer.set('name', layerName);
    mapObj.addLayer(newLayer);
    
    // Cache the new layer
    measurementLayersCache.current[cacheKey] = newLayer;
    return newLayer;
  }, []);

  // Clear measurement layer cache when maps change
  const clearMeasurementCache = useCallback(() => {
    measurementLayersCache.current = {
      pointsA: null, pointsB: null,
      linesA: null, linesB: null, 
      polygonsA: null, polygonsB: null
    };
  }, []);

  /* ---------- GETFEATUREINFO STATE ---------- */
  const [clickedCoordinates, setClickedCoordinates] = useState(null); // { x, y } in UTM
  const [featureInfo, setFeatureInfo] = useState(null); // { opstina, ko, brparcele }
  const [fruitTreeInfo, setFruitTreeInfo] = useState(null); // { layerName: kategorija, ... }
  const [soilInfo, setSoilInfo] = useState(null); // { pedoloska: {title, value}, ... }
  const [isSoilExpanded, setIsSoilExpanded] = useState(false);
  const [farmingInfo, setFarmingInfo] = useState(null); // { suncokret: {title, value}, ... }
  const [isFarmingExpanded, setIsFarmingExpanded] = useState(false);
  const [meteoInfo, setMeteoInfo] = useState(null);       // { layerId: {title, value}, ... }
  const [isMeteoExpanded, setIsMeteoExpanded] = useState(false);
  const [vegetationInfo, setVegetationInfo] = useState(null);       // { layerId: {title, value}, ... }
  const [isVegetationExpanded, setIsVegetationExpanded] = useState(false);
  const [meliorationInfo, setMeliorationInfo] = useState(null);     // { layerId: {title, value}, ... }
  const [isMeliorationExpanded, setIsMeliorationExpanded] = useState(false);
  const [isFeatureInfoLoading, setIsFeatureInfoLoading] = useState(false);
  const [clickMarker, setClickMarker] = useState(null); // { id, coordinate } for click marker
  const [infoWindowPosition, setInfoWindowPosition] = useState(null); // { x, y } for info window position
  const [isFruitTreeExpanded, setIsFruitTreeExpanded] = useState(false); // Toggle for fruit tree section
  const [infoWindowOverlay, setInfoWindowOverlay] = useState(null); // OpenLayers overlay for info window

  /* ---------- MARKER STATE ---------- */
  // SVG 100×120 scaled by 0.45
  const [markerIconPxSize] = useState({ w: 45, h: 54 });

  // Ensure offset remains accurate when content changes or icon dimensions update
  useEffect(() => {
    if (!infoWindowOverlay) return;
    const dx = 12 + Math.round(markerIconPxSize.w * 0.25);
    const dy = -Math.round(markerIconPxSize.h / 2);
    infoWindowOverlay.setOffset([dx, dy]);
    const pos = infoWindowOverlay.getPosition?.();
    if (pos) infoWindowOverlay.setPosition(pos);
  }, [markerIconPxSize, infoWindowOverlay]);

  const addPointToMap = useCallback((lng, lat) => {
    try {
      const transformedCoord = transform([lng, lat], 'EPSG:4326', 'EPSG:32634');
      const [x, y] = transformedCoord;

      const newPoint = {
        id: `point_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        lng,
        lat,
        x: Math.round(x),
        y: Math.round(y)
      };

      setPlacedPoints(prev => [...prev, newPoint]);

      [mapA.current, mapB.current].forEach(map => {
        if (!map) return;

        const measurementLayer = getMeasurementLayer(map, 'points', true);
        if (!measurementLayer) return;

        const source = measurementLayer.getSource();
        const feature = new Feature({
          geometry: new Point(transformedCoord),
          id: newPoint.id
        });
        source.addFeature(feature);
      });
    } catch (error) {
      console.error('Error adding point to map:', error);
    }
  }, [getMeasurementLayer]);

  // Optimized function to remove a single point
  const removePoint = useCallback((pointId) => {
    setPlacedPoints(prev => prev.filter(p => p.id !== pointId));
    
    [mapA.current, mapB.current].forEach(map => {
      if (!map) return;
      
      const measurementLayer = getMeasurementLayer(map, 'points', false);
      if (!measurementLayer) return;
      
      const source = measurementLayer.getSource();
      const features = source.getFeatures();
      const featureToRemove = features.find(feature => feature.get('id') === pointId);
      if (featureToRemove) {
        source.removeFeature(featureToRemove);
      }
    });
  }, [getMeasurementLayer]);

  // Optimized function to clear all points
  const clearAllPoints = useCallback(() => {
    setPlacedPoints([]);
    
    [mapA.current, mapB.current].forEach(map => {
      if (!map) return;
      
      const measurementLayer = getMeasurementLayer(map, 'points', false);
      if (!measurementLayer) return;
      
      const source = measurementLayer.getSource();
      source.clear();
    });
  }, [getMeasurementLayer]);

  const addPointToLine = useCallback((lng, lat) => {
    try {
      const transformedCoord = transform([lng, lat], 'EPSG:4326', 'EPSG:32634');
      const [x, y] = transformedCoord;

      const newPoint = {
        lng,
        lat,
        x: Math.round(x),
        y: Math.round(y)
      };

      if (currentLine) {
        setCurrentLine(prev => ({
          ...prev,
          points: [...prev.points, newPoint]
        }));
      } else {
        const newLine = {
          id: `line_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
          points: [newPoint],
          isComplete: false
        };
        setCurrentLine(newLine);
      }
    } catch (error) {
      console.error('Error adding point to line:', error);
    }
  }, [currentLine]);

  // Function to calculate line length
  const calculateLineLength = useCallback((points) => {
    if (points.length < 2) return 0;
    
    let totalLength = 0;
    for (let i = 1; i < points.length; i++) {
      const dx = points[i].x - points[i-1].x;
      const dy = points[i].y - points[i-1].y;
      totalLength += Math.sqrt(dx * dx + dy * dy);
    }
    return Math.round(totalLength * 100) / 100; // Round to 2 decimal places
  }, []);

  // Function to finish drawing current line
  const finishCurrentLine = useCallback(() => {
    if (currentLine && currentLine.points.length >= 2) {
      const completedLine = {
        ...currentLine,
        isComplete: true,
        length: calculateLineLength(currentLine.points)
      };
      setDrawnLines(prev => [...prev, completedLine]);
      setCurrentLine(null);
    }
  }, [currentLine, calculateLineLength]);

  // Optimized function to update line visualization on map using cached layers
  // Full rebuild of completed lines on the map
  const rebuildLineVisualization = useCallback((map) => {
    if (!map) return;

    const measurementLayer = getMeasurementLayer(map, 'lines', true);
    if (!measurementLayer) return;

    const source = measurementLayer.getSource();
    source.clear();

    drawnLines.forEach(line => {
      if (line.points.length < 2) return;
      const coordinates = line.points.map(p => transform([p.lng, p.lat], 'EPSG:4326', 'EPSG:32634'));

      const lineFeature = new Feature({
        geometry: new LineString(coordinates),
        id: line.id,
        length: line.length
      });
      source.addFeature(lineFeature);

      coordinates.forEach((coord, index) => {
        const vertexFeature = new Feature({
          geometry: new Point(coord),
          id: `${line.id}_vertex_${index}`,
          isVertex: true
        });
        source.addFeature(vertexFeature);
      });
    });
  }, [drawnLines, getMeasurementLayer]);

  // Incremental update of the in-progress line preview only
  const updateCurrentLinePreview = useCallback((map) => {
    if (!map) return;

    const measurementLayer = getMeasurementLayer(map, 'lines', false);
    if (!measurementLayer) return;

    const source = measurementLayer.getSource();

    // Remove only current-line features
    source.getFeatures()
      .filter(f => f.get('isCurrent') || f.get('isCurrentLine'))
      .forEach(f => source.removeFeature(f));

    if (currentLine && currentLine.points.length >= 1) {
      const currentCoordinates = currentLine.points.map(p => transform([p.lng, p.lat], 'EPSG:4326', 'EPSG:32634'));

      if (currentLine.points.length >= 2) {
        const currentLineFeature = new Feature({
          geometry: new LineString(currentCoordinates),
          id: currentLine.id,
          isCurrentLine: true,
          isCurrent: true
        });
        source.addFeature(currentLineFeature);
      }

      currentCoordinates.forEach((coord, index) => {
        const vertexFeature = new Feature({
          geometry: new Point(coord),
          id: `${currentLine.id}_vertex_${index}`,
          isVertex: true,
          isCurrent: true
        });
        source.addFeature(vertexFeature);
      });
    }
  }, [currentLine, getMeasurementLayer]);

  // Function to remove a single line
  const removeLine = useCallback((lineId) => {
    setDrawnLines(prev => prev.filter(line => line.id !== lineId));
  }, []);

  // Optimized function to clear all lines
  const clearAllLines = useCallback(() => {
    setDrawnLines([]);
    setCurrentLine(null);
    
    [mapA.current, mapB.current].forEach(map => {
      if (!map) return;
      
      const measurementLayer = getMeasurementLayer(map, 'lines', false);
      if (!measurementLayer) return;
      
      const source = measurementLayer.getSource();
      source.clear();
    });
  }, [getMeasurementLayer]);

  // Optimized function to start or continue drawing a polygon with debouncing
  const addPointToPolygon = useCallback((lng, lat) => {
    try {
      const transformedCoord = transform([lng, lat], 'EPSG:4326', 'EPSG:32634');
      const [x, y] = transformedCoord;

      const newPoint = {
        lng,
        lat,
        x: Math.round(x),
        y: Math.round(y)
      };

      if (currentPolygon) {
        setCurrentPolygon(prev => ({
          ...prev,
          points: [...prev.points, newPoint]
        }));
      } else {
        const newPolygon = {
          id: `polygon_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
          points: [newPoint],
          isComplete: false
        };
        setCurrentPolygon(newPolygon);
      }
    } catch (error) {
      console.error('Error adding point to polygon:', error);
    }
  }, [currentPolygon]);

  // Function to calculate polygon area (using shoelace formula)
  const calculatePolygonArea = useCallback((points) => {
    if (points.length < 3) return 0;
    
    let area = 0;
    for (let i = 0; i < points.length; i++) {
      const j = (i + 1) % points.length;
      area += points[i].x * points[j].y;
      area -= points[j].x * points[i].y;
    }
    return Math.abs(area / 2); // Return area in square meters
  }, []);

  // Function to calculate polygon perimeter
  const calculatePolygonPerimeter = useCallback((points) => {
    if (points.length < 2) return 0;
    
    let perimeter = 0;
    for (let i = 0; i < points.length; i++) {
      const j = (i + 1) % points.length;
      const dx = points[j].x - points[i].x;
      const dy = points[j].y - points[i].y;
      perimeter += Math.sqrt(dx * dx + dy * dy);
    }
    return Math.round(perimeter * 100) / 100; // Round to 2 decimal places
  }, []);

  // Function to finish drawing current polygon
  const finishCurrentPolygon = useCallback(() => {
    if (currentPolygon && currentPolygon.points.length >= 3) {
      const completedPolygon = {
        ...currentPolygon,
        isComplete: true,
        area: calculatePolygonArea(currentPolygon.points),
        perimeter: calculatePolygonPerimeter(currentPolygon.points)
      };
      setDrawnPolygons(prev => [...prev, completedPolygon]);
      setCurrentPolygon(null);
    }
  }, [currentPolygon, calculatePolygonArea, calculatePolygonPerimeter]);

  // Helper to deactivate measurement tool and finish any in-progress drawing
  const deactivateMeasurementTool = useCallback(() => {
    finishCurrentLine();
    finishCurrentPolygon();
    setSelectedMeasurementTool(null);
    if (measurementDebounceRef.current) {
      clearTimeout(measurementDebounceRef.current);
      measurementDebounceRef.current = null;
    }
  }, [finishCurrentLine, finishCurrentPolygon]);

  // Optimized function to update polygon visualization on map using cached layers
  // Full rebuild of completed polygons on the map
  const rebuildPolygonVisualization = useCallback((map) => {
    if (!map) return;

    const measurementLayer = getMeasurementLayer(map, 'polygons', true);
    if (!measurementLayer) return;

    const source = measurementLayer.getSource();
    source.clear();

    drawnPolygons.forEach(polygon => {
      if (polygon.points.length < 3) return;
      const coordinates = polygon.points.map(p => transform([p.lng, p.lat], 'EPSG:4326', 'EPSG:32634'));
      coordinates.push(coordinates[0]); // Close the polygon

      const polygonFeature = new Feature({
        geometry: new Polygon([coordinates]),
        id: polygon.id,
        area: polygon.area,
        perimeter: polygon.perimeter
      });
      source.addFeature(polygonFeature);

      // Add vertices (exclude closing duplicate)
      coordinates.slice(0, -1).forEach((coord, index) => {
        const vertexFeature = new Feature({
          geometry: new Point(coord),
          id: `${polygon.id}_vertex_${index}`,
          isVertex: true
        });
        source.addFeature(vertexFeature);
      });
    });
  }, [drawnPolygons, getMeasurementLayer]);

  // Incremental update of the in-progress polygon preview only
  const updateCurrentPolygonPreview = useCallback((map) => {
    if (!map) return;

    const measurementLayer = getMeasurementLayer(map, 'polygons', false);
    if (!measurementLayer) return;

    const source = measurementLayer.getSource();

    // Remove only current-polygon features
    source.getFeatures()
      .filter(f => f.get('isCurrent') || f.get('isCurrentPolygon'))
      .forEach(f => source.removeFeature(f));

    // Add current polygon preview
    if (currentPolygon && currentPolygon.points.length >= 3) {
      const currentCoordinates = currentPolygon.points.map(p => transform([p.lng, p.lat], 'EPSG:4326', 'EPSG:32634'));
      currentCoordinates.push(currentCoordinates[0]);

      const currentPolygonFeature = new Feature({
        geometry: new Polygon([currentCoordinates]),
        id: currentPolygon.id,
        isCurrentPolygon: true,
        isCurrent: true
      });
      source.addFeature(currentPolygonFeature);
    }

    // Add vertices for current polygon
    if (currentPolygon && currentPolygon.points.length >= 1) {
      const currentVertexCoords = currentPolygon.points.map(p => transform([p.lng, p.lat], 'EPSG:4326', 'EPSG:32634'));
      currentVertexCoords.forEach((coord, index) => {
        const vertexFeature = new Feature({
          geometry: new Point(coord),
          id: `${currentPolygon.id}_vertex_${index}`,
          isVertex: true,
          isCurrent: true
        });
        source.addFeature(vertexFeature);
      });
    }
  }, [currentPolygon, getMeasurementLayer]);

  // Function to remove a single polygon
  const removePolygon = useCallback((polygonId) => {
    setDrawnPolygons(prev => prev.filter(polygon => polygon.id !== polygonId));
  }, []);

  // Optimized function to clear all polygons
  const clearAllPolygons = useCallback(() => {
    setDrawnPolygons([]);
    setCurrentPolygon(null);
    
    [mapA.current, mapB.current].forEach(map => {
      if (!map) return;
      
      const measurementLayer = getMeasurementLayer(map, 'polygons', false);
      if (!measurementLayer) return;
      
      const source = measurementLayer.getSource();
      source.clear();
    });
  }, [getMeasurementLayer]);

  // Full rebuild when completed lines change
  useEffect(() => {
    [mapA.current, mapB.current].forEach(map => {
      if (!map) return;
      rebuildLineVisualization(map);
      updateCurrentLinePreview(map);
    });
  }, [drawnLines, rebuildLineVisualization, updateCurrentLinePreview]);

  // Incremental update when current line changes
  useEffect(() => {
    [mapA.current, mapB.current].forEach(map => {
      if (!map) return;
      updateCurrentLinePreview(map);
    });
  }, [currentLine, updateCurrentLinePreview]);

  // Full rebuild when completed polygons change
  useEffect(() => {
    [mapA.current, mapB.current].forEach(map => {
      if (!map) return;
      rebuildPolygonVisualization(map);
      updateCurrentPolygonPreview(map);
    });
  }, [drawnPolygons, rebuildPolygonVisualization, updateCurrentPolygonPreview]);

  // Incremental update when current polygon changes
  useEffect(() => {
    [mapA.current, mapB.current].forEach(map => {
      if (!map) return;
      updateCurrentPolygonPreview(map);
    });
  }, [currentPolygon, updateCurrentPolygonPreview]);

  /* ---------- CLICK MARKER FUNCTIONS ---------- */
  const removeClickMarker = useCallback(() => {
    // Always clear any existing markers regardless of clickMarker state
    [mapA.current, mapB.current].forEach(mapObj => {
      if (!mapObj) return;
      
      const markerLayer = mapObj.getLayers().getArray().find(layer => layer.get('name') === 'click-marker');
      if (markerLayer) {
        const source = markerLayer.getSource();
        source.clear(); // Clear all features in the marker layer
      }
      
      // Remove ALL overlays that might be info windows
      const overlays = mapObj.getOverlays().getArray();
      overlays.forEach(overlay => {
        const element = overlay.getElement();
        if (element && element.className && element.className.includes('getfeatureinfo-window')) {
          mapObj.removeOverlay(overlay);
        }
      });
    });
    
    // Remove info window overlay
    if (infoWindowOverlay) {
      [mapA.current, mapB.current].forEach(mapObj => {
        if (mapObj) {
          mapObj.removeOverlay(infoWindowOverlay);
        }
      });
      setInfoWindowOverlay(null);
    }
    
    setClickMarker(null);
    setInfoWindowPosition(null);
    setSoilInfo(null);
    setIsSoilExpanded(false);
    setFarmingInfo(null);
    setIsFarmingExpanded(false);
    setMeteoInfo(null);
    setIsMeteoExpanded(false);
    setVegetationInfo(null);
    setIsVegetationExpanded(false);
    setMeliorationInfo(null);
    setIsMeliorationExpanded(false);
  }, [infoWindowOverlay]);

  const addClickMarker = useCallback((coordinate, map) => {
    try {
      // Always remove previous marker first
      removeClickMarker();
      
      // Additional cleanup: remove any existing overlays from both maps
      [mapA.current, mapB.current].forEach(mapObj => {
        if (!mapObj) return;
        const overlays = mapObj.getOverlays().getArray();
        overlays.forEach(overlay => {
          const element = overlay.getElement();
          if (element && element.className && element.className.includes('getfeatureinfo-window')) {
            mapObj.removeOverlay(overlay);
          }
        });
      });
      
      // Clear any existing getFeatureInfo data
      setClickedCoordinates(null);
      setFeatureInfo(null);
      setFruitTreeInfo(null);
      setSoilInfo(null);
      setFarmingInfo(null);
      setMeteoInfo(null);
      setVegetationInfo(null);
      setMeliorationInfo(null);
      setIsFeatureInfoLoading(false);
      setIsFruitTreeExpanded(false);
      setIsSoilExpanded(false);
      setIsFarmingExpanded(false);
      setIsMeteoExpanded(false);
      setIsVegetationExpanded(false);
      setIsMeliorationExpanded(false);

      // Create new marker
      const markerId = `click_marker_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      const newMarker = { id: markerId, coordinate };

      // Create info window overlay
      const infoWindowElement = document.createElement('div');
      infoWindowElement.className = 'getfeatureinfo-window';
      infoWindowElement.style.position = 'absolute';
      infoWindowElement.style.zIndex = '1002';
      
      // Ensure any existing elements with the same class are removed from DOM
      const existingElements = document.querySelectorAll('.getfeatureinfo-window');
      existingElements.forEach(el => {
        if (el.parentNode) {
          el.parentNode.removeChild(el);
        }
      });
      
      // Calculate dynamic offset based on marker icon dimensions
      const dx = 12 + Math.round(markerIconPxSize.w * 0.25);   // malo desno od centar-linije markera
      const dy = -Math.round(markerIconPxSize.h / 2);          // gornja ivica prozora = sredina markera po Y

      const overlay = new Overlay({
        element: infoWindowElement,
        positioning: 'top-left',
        offset: [dx, dy],
        stopEvent: true // Sprečava propagaciju događaja kroz overlay
      });

      // Add overlay to the map i postavi poziciju
      map.addOverlay(overlay);
      overlay.setPosition(coordinate);

      // Centriraj mapu na kliknutu tačku
      map.getView().animate({ center: coordinate, duration: 300 });

      setInfoWindowOverlay(overlay);
      setInfoWindowPosition({ x: 0, y: 0 });
      newMarker.overlayElement = infoWindowElement;

      // Add marker to both maps
      [mapA.current, mapB.current].forEach(mapObj => {
        if (!mapObj) return;

        // Get or create click marker layer
        let markerLayer = mapObj.getLayers().getArray().find(layer => layer.get('name') === 'click-marker');
        if (!markerLayer) {
          const source = new VectorSource({
            features: []
          });
          
                      markerLayer = new VectorLayer({
              source: source,
              style: new Style({
                image: new Icon({
                  src: MARKER_ICON_SRC,
                  scale: MARKER_ICON_SCALE,
                  anchor: [0.5, 1] // Anchor at bottom center of the icon
                })
              }),
              updateWhileAnimating: false,
              updateWhileInteracting: false,
              zIndex: 9999
            });
          markerLayer.set('name', 'click-marker');
          mapObj.addLayer(markerLayer);
        }

        // Add new marker feature
        const source = markerLayer.getSource();
        const feature = new Feature({
          geometry: new Point(coordinate),
          id: markerId
        });
        source.addFeature(feature);
      });

      setClickMarker(newMarker);
    } catch (error) {
      console.error('Error adding click marker:', error);
    }
  }, [clickMarker]);

  // Function to update info window content
  const updateInfoWindowContent = useCallback(() => {
    if (!clickMarker || !clickMarker.overlayElement) return;
    
    const element = clickMarker.overlayElement;
    
    element.innerHTML = `
      ${clickedCoordinates ? `
        <div class="getfeatureinfo-coordinates">
          N: ${clickedCoordinates.y.toLocaleString('sr-RS', { useGrouping: false })} m | E: ${clickedCoordinates.x.toLocaleString('sr-RS', { useGrouping: false })} m
        </div>
      ` : ''}
      
      ${isFeatureInfoLoading ? `
        <div class="getfeatureinfo-loading">
          Učitavanje informacija...
        </div>
      ` : ''}
      
      ${featureInfo && !isFeatureInfoLoading ? `
        <div class="getfeatureinfo-content">
          <div>
            <strong>Opština:</strong> ${featureInfo.opstina}
          </div>
          <div>
            <strong>KO:</strong> ${featureInfo.ko}
          </div>
          <div>
            <strong>Broj parcele:</strong> ${featureInfo.brparcele}
          </div>
        </div>
      ` : ''}
      
      ${soilInfo && !isFeatureInfoLoading ? `
        <div class="getfeatureinfo-content">
          <div style="margin-top: 10px; font-weight: bold; cursor: pointer; display: flex; align-items: center; gap: 5px;" onclick="window.toggleSoilExpanded()">
            Zemljište
            <span style="transform: ${isSoilExpanded ? 'rotate(180deg)' : 'rotate(0deg)'}; transition: transform 0.2s ease; font-size: 12px;">▼</span>
          </div>
          ${isSoilExpanded ? `
            <div style="margin-top: 6px; padding-left: 12px; border-left: 2px solid rgba(0,0,0,0.1);">
              ${(() => {
                const entries = Object.entries(soilInfo); // [ [layerId, {title, value}], ... ]
                return entries.map(([layerId, obj]) => {
                  return `
                    <div style="margin:4px 0;">
                      <span style="font-size:13px;"><b>${obj.title}:</b> ${obj.value}</span>
                    </div>`;
                }).join('');
              })()}
            </div>
          ` : ''}
        </div>
      ` : ''}
      
      ${fruitTreeInfo && !isFeatureInfoLoading && Object.keys(fruitTreeInfo).length > 0 ? `
        <div class="getfeatureinfo-content">
          <div style="margin-top: 10px; font-weight: bold; cursor: pointer; display: flex; align-items: center; gap: 5px;" onclick="window.toggleFruitTreeExpanded()">
            Voćarstvo
            <span style="transform: ${isFruitTreeExpanded ? 'rotate(180deg)' : 'rotate(0deg)'}; transition: transform 0.2s ease; font-size: 12px;">
              ▼
            </span>
          </div>
          ${isFruitTreeExpanded ? `
            <div style="margin-top: 5px; padding-left: 12px; border-left: 2px solid rgba(0,0,0,0.1);">
              ${(() => {
                const groupedByCategory = {};
                Object.entries(fruitTreeInfo).forEach(([layerName, kategorija]) => {
                  if (!groupedByCategory[kategorija]) {
                    groupedByCategory[kategorija] = [];
                  }
                  groupedByCategory[kategorija].push(layerName);
                });

                const categoryDisplayNames = {
                  'Povoljno': 'Povoljno',
                  'Delimicno povoljno': 'Delimično povoljno',
                  'Nepovoljno': 'Nepovoljno'
                };

                return Object.entries(groupedByCategory).map(([kategorija, layers]) => `
                  <div style="margin-bottom: 10px;">
                    <div style="font-weight: bold; font-size: 13px; margin-bottom: 5px; color: #000000;">
                      ${categoryDisplayNames[kategorija] || kategorija}
                    </div>
                    ${layers.map(layerName => `
                      <div style="margin-left: 10px; font-size: 13px; color: #000000; font-weight: normal;">
                        • ${layerName}
                      </div>
                    `).join('')}
                  </div>
                `).join('');
              })()}
            </div>
          ` : ''}
        </div>
      ` : ''}
      
      ${farmingInfo && !isFeatureInfoLoading ? `
        <div class="getfeatureinfo-content">
          <div style="margin-top: 10px; font-weight: bold; cursor: pointer; display: flex; align-items: center; gap: 5px;" onclick="window.toggleFarmingExpanded()">
            Ratarstvo
            <span style="transform: ${isFarmingExpanded ? 'rotate(180deg)' : 'rotate(0deg)'}; transition: transform 0.2s ease; font-size: 12px;">▼</span>
          </div>
          ${isFarmingExpanded ? `
            <div style="margin-top: 6px; padding-left: 12px; border-left: 2px solid rgba(0,0,0,0.1);">
              ${(() => {
                const entries = Object.entries(farmingInfo); // [ [layerId, {title, value}], ... ]
                return entries.map(([layerId, obj]) => {
                  return `
                    <div style="margin:4px 0;">
                      <span style="font-size:13px;"><b>${obj.title}:</b> ${obj.value}</span>
                    </div>`;
                }).join('');
              })()}
            </div>
          ` : ''}
        </div>
      ` : ''}
      
      ${meteoInfo && !isFeatureInfoLoading ? `
        <div class="getfeatureinfo-content">
          <div style="margin-top: 10px; font-weight: bold; cursor: pointer; display: flex; align-items: center; gap: 5px;" onclick="window.toggleMeteoExpanded()">
            Meteorologija
            <span style="transform: ${isMeteoExpanded ? 'rotate(180deg)' : 'rotate(0deg)'}; transition: transform 0.2s ease; font-size: 12px;">▼</span>
          </div>
          ${isMeteoExpanded ? `
            <div style="margin-top: 6px; padding-left: 12px; border-left: 2px solid rgba(0,0,0,0.1);">
              ${(() => {
                const entries = Object.entries(meteoInfo); // [ [layerId, {title, value}], ... ]
                return entries.map(([layerId, obj]) => {
                  return `
                    <div style="margin:4px 0;">
                      <span style="font-size:13px;"><b>${obj.title}:</b> ${obj.value}</span>
                    </div>`;
                }).join('');
              })()}
            </div>
          ` : ''}
        </div>
      ` : ''}
      
      ${vegetationInfo && !isFeatureInfoLoading ? `
        <div class="getfeatureinfo-content">
          <div style="margin-top: 10px; font-weight: bold; cursor: pointer; display: flex; align-items: center; gap: 5px;" onclick="window.toggleVegetationExpanded()">
            Vegetacioni period
            <span style="transform: ${isVegetationExpanded ? 'rotate(180deg)' : 'rotate(0deg)'}; transition: transform 0.2s ease; font-size: 12px;">▼</span>
          </div>
          ${isVegetationExpanded ? `
            <div style="margin-top: 6px; padding-left: 12px; border-left: 2px solid rgba(0,0,0,0.1);">
              ${(() => {
                const entries = Object.entries(vegetationInfo); // [ [layerId, {title, value}], ... ]
                return entries.map(([layerId, obj]) => {
                  return `
                    <div style="margin:4px 0;">
                      <span style="font-size:13px;"><b>${obj.title}:</b> ${obj.value}</span>
                    </div>`;
                }).join('');
              })()}
            </div>
          ` : ''}
        </div>
      ` : ''}
      
      ${meliorationInfo && !isFeatureInfoLoading ? `
        <div class="getfeatureinfo-content">
          <div style="margin-top: 10px; font-weight: bold; cursor: pointer; display: flex; align-items: center; gap: 5px;" onclick="window.toggleMeliorationExpanded()">
            Melioracije
            <span style="transform: ${isMeliorationExpanded ? 'rotate(180deg)' : 'rotate(0deg)'}; transition: transform 0.2s ease; font-size: 12px;">▼</span>
          </div>
          ${isMeliorationExpanded ? `
            <div style="margin-top: 6px; padding-left: 12px; border-left: 2px solid rgba(0,0,0,0.1);">
              ${(() => {
                const entries = Object.entries(meliorationInfo); // [ [layerId, {title, value}], ... ]
                return entries.map(([layerId, obj]) => {
                  return `
                    <div style="margin:4px 0;">
                      <span style="font-size:13px;"><b>${obj.title}:</b> ${obj.value}</span>
                    </div>`;
                }).join('');
              })()}
            </div>
          ` : ''}
        </div>
      ` : ''}
      
      ${!featureInfo && !fruitTreeInfo && !soilInfo && !farmingInfo && !meteoInfo && !vegetationInfo && !meliorationInfo && !isFeatureInfoLoading && clickedCoordinates ? `
        <div class="getfeatureinfo-no-data">
          Nema informacija na ovoj lokaciji
        </div>
      ` : ''}
      
      <button
        class="getfeatureinfo-close-button"
        onclick="window.closeInfoWindow()"
        title="Zatvori"
      >
        ✕
      </button>
    `;
  }, [clickedCoordinates, featureInfo, fruitTreeInfo, soilInfo, farmingInfo, meteoInfo, vegetationInfo, meliorationInfo, isFeatureInfoLoading, isFruitTreeExpanded, isSoilExpanded, isFarmingExpanded, isMeteoExpanded, isVegetationExpanded, isMeliorationExpanded, clickMarker]);

  // Add global functions for overlay event handlers
  useEffect(() => {
    window.toggleFruitTreeExpanded = () => {
      setIsFruitTreeExpanded(prev => !prev);
    };
    
    window.toggleSoilExpanded = () => {
      setIsSoilExpanded(prev => !prev);
    };
    
    window.toggleFarmingExpanded = () => {
      setIsFarmingExpanded(prev => !prev);
    };
    
    window.toggleMeteoExpanded = () => { setIsMeteoExpanded(prev => !prev); };
    
    window.toggleVegetationExpanded = () => { setIsVegetationExpanded(prev => !prev); };
    
    window.toggleMeliorationExpanded = () => { 
      setIsMeliorationExpanded(prev => !prev); 
    };
    
    window.closeInfoWindow = () => {
      setClickedCoordinates(null);
      setFeatureInfo(null);
      setFruitTreeInfo(null);
      setSoilInfo(null);
      setFarmingInfo(null);
      setMeteoInfo(null);
      setVegetationInfo(null);
      setMeliorationInfo(null);
      setIsFeatureInfoLoading(false);
      setIsFruitTreeExpanded(false);
      setIsSoilExpanded(false);
      setIsFarmingExpanded(false);
      setIsMeteoExpanded(false);
      setIsVegetationExpanded(false);
      setIsMeliorationExpanded(false);
      removeClickMarker();
    };
    
    return () => {
      delete window.toggleFruitTreeExpanded;
      delete window.toggleSoilExpanded;
      delete window.toggleFarmingExpanded;
      delete window.toggleMeteoExpanded;   // dodato
      delete window.toggleVegetationExpanded;   // dodato
      delete window.toggleMeliorationExpanded;   // dodato
      delete window.closeInfoWindow;
    };
  }, [removeClickMarker]);

  // Update overlay content when data changes
  useEffect(() => {
    updateInfoWindowContent();
  }, [updateInfoWindowContent]);

  // Ensure melioration toggle function is available when meliorationInfo changes
  useEffect(() => {
    if (meliorationInfo) {
      window.toggleMeliorationExpanded = () => { 
        setIsMeliorationExpanded(prev => !prev); 
      };
    }
  }, [meliorationInfo]);

  /* ---------- GETFEATUREINFO FUNCTION ---------- */
  const performGetFeatureInfo = useCallback(async (coordinate, map) => {
    try {
      // First, add click marker (this will remove any existing marker and overlay)
      addClickMarker(coordinate, map);
      
      // Set loading state for new data
      setIsFeatureInfoLoading(true);
      
      // Clear previous melioration info
      setMeliorationInfo(null);

      // Convert coordinate to WGS84 if needed
      const wgs84Coord = transform(coordinate, 'EPSG:32634', 'EPSG:4326');
      
      // Convert to UTM coordinates for display
      const [utmX, utmY] = proj4('EPSG:4326', 'EPSG:32634', wgs84Coord);
      setClickedCoordinates({ x: Math.round(utmX), y: Math.round(utmY) });

      // Get map view parameters
      const view = map.getView();
      const extent = view.calculateExtent(map.getSize());
      const resolution = view.getResolution();
      const projection = view.getProjection();

      // Define fruit tree layers to query
      const fruitTreeLayers = [
        { id: 'visnja_obl', title: 'Višnja oblacinska', wmsName: 'Agris:visnja_obl' },
        { id: 'visnja', title: 'Višnja', wmsName: 'Agris:visnja' },
        { id: 'tresnja', title: 'Trešnja', wmsName: 'Agris:tresnja' },
        { id: 'sljiva', title: 'Šljiva', wmsName: 'Agris:sljiva' },
        { id: 'orah', title: 'Orah', wmsName: 'Agris:orah' },
        { id: 'ogrozd', title: 'Ogrozd', wmsName: 'Agris:ogrozd' },
        { id: 'malina', title: 'Malina', wmsName: 'Agris:malina' },
        { id: 'leska', title: 'Leska', wmsName: 'Agris:leska' },
        { id: 'kruska', title: 'Kruška', wmsName: 'Agris:kruska' },
        { id: 'jagoda', title: 'Jagoda', wmsName: 'Agris:jagoda' },
        { id: 'jabuka', title: 'Jabuka', wmsName: 'Agris:jabuka' },
        { id: 'dunja', title: 'Dunja', wmsName: 'Agris:dunja' },
        { id: 'crv_ribizla', title: 'Crvena ribizla', wmsName: 'Agris:crv_ribizla' },
        { id: 'crn_ribizla', title: 'Crna ribizla', wmsName: 'Agris:crn_ribizla' },
        { id: 'breskva', title: 'Breskva', wmsName: 'Agris:breskva' },
        { id: 'borovnica', title: 'Borovnica', wmsName: 'Agris:borovnica' },
        { id: 'badem', title: 'Badem', wmsName: 'Agris:badem' }
      ];

      // Query fruit tree layers for kategorija attribute
      const fruitTreeResults = {};
      
      for (const layer of fruitTreeLayers) {
        try {
          const params = {
            SERVICE: 'WMS',
            VERSION: '1.1.1',
            REQUEST: 'GetFeatureInfo',
            LAYERS: layer.wmsName,
            QUERY_LAYERS: layer.wmsName,
            INFO_FORMAT: 'application/json',
            FEATURE_COUNT: 1,
            X: Math.round((coordinate[0] - extent[0]) / resolution),
            Y: Math.round((extent[3] - coordinate[1]) / resolution),
            SRS: projection.getCode(),
            WIDTH: map.getSize()[0],
            HEIGHT: map.getSize()[1],
            BBOX: extent.join(',')
          };

          const url = `${GEOSERVER_URL}/wms?${new URLSearchParams(params)}`;
          const response = await fetch(url);
          
          if (response.ok) {
            const data = await response.json();
            if (data.features && data.features.length > 0) {
              const feature = data.features[0];
              const properties = feature.properties;
              if (properties.kategorija) {
                fruitTreeResults[layer.title] = properties.kategorija;
              }
            }
          }
        } catch (error) {
          console.warn(`Error querying ${layer.title}:`, error);
        }
      }

      // Set fruit tree information
      if (Object.keys(fruitTreeResults).length > 0) {
        setFruitTreeInfo(fruitTreeResults);
      }

      // Query soil layers
      const soilResults = {};
      for (const layer of SOIL_LAYERS) {
        try {
          const params = {
            SERVICE: 'WMS',
            VERSION: '1.1.1',
            REQUEST: 'GetFeatureInfo',
            LAYERS: layer.wmsName,
            QUERY_LAYERS: layer.wmsName,
            INFO_FORMAT: 'application/json',
            FEATURE_COUNT: 1,
            X: Math.round((coordinate[0] - extent[0]) / resolution),
            Y: Math.round((extent[3] - coordinate[1]) / resolution),
            SRS: projection.getCode(),
            WIDTH: map.getSize()[0],
            HEIGHT: map.getSize()[1],
            BBOX: extent.join(',')
          };

          const url = `${GEOSERVER_URL}/wms?${new URLSearchParams(params)}`;
          const resp = await fetch(url);
          if (!resp.ok) continue;

          const data = await resp.json();
          if (data?.features?.length > 0) {
            const props = data.features[0].properties || {};
            const val = props[layer.field];
            if (val !== undefined && val !== null && String(val).trim() !== '') {
              soilResults[layer.id] = { title: layer.title, value: String(val) };
            }
          }
        } catch (e) {
          console.warn(`Soil GFI error for ${layer.title}:`, e);
        }
      }

      if (Object.keys(soilResults).length > 0) {
        setSoilInfo(soilResults);
      }

      // Query farming layers
      const farmingResults = {};
      for (const layer of FARMING_LAYERS) {
        try {
          const params = {
            SERVICE: 'WMS',
            VERSION: '1.1.1',
            REQUEST: 'GetFeatureInfo',
            LAYERS: layer.wmsName,
            QUERY_LAYERS: layer.wmsName,
            INFO_FORMAT: 'application/json',
            FEATURE_COUNT: 1,
            X: Math.round((coordinate[0] - extent[0]) / resolution),
            Y: Math.round((extent[3] - coordinate[1]) / resolution),
            SRS: projection.getCode(),
            WIDTH: map.getSize()[0],
            HEIGHT: map.getSize()[1],
            BBOX: extent.join(',')
          };

          const url = `${GEOSERVER_URL}/wms?${new URLSearchParams(params)}`;
          const resp = await fetch(url);
          if (!resp.ok) continue;

          const data = await resp.json();
          if (data?.features?.length > 0) {
            const props = data.features[0].properties || {};
            const val = props[layer.field];
            if (val !== undefined && val !== null && String(val).trim() !== '') {
              farmingResults[layer.id] = { title: layer.title, value: String(val) };
            }
          }
        } catch (e) {
          console.warn(`Farming GFI error for ${layer.title}:`, e);
        }
      }

      if (Object.keys(farmingResults).length > 0) {
        setFarmingInfo(farmingResults);
      }

      // Query meteorology rasters - vrednost piksela
      const meteoResults = {};
      for (const layer of METEO_RASTER_LAYERS) {
        try {
          const params = {
            SERVICE: 'WMS',
            VERSION: '1.1.1',
            REQUEST: 'GetFeatureInfo',
            LAYERS: layer.wmsName,
            QUERY_LAYERS: layer.wmsName,
            INFO_FORMAT: 'application/json',
            FEATURE_COUNT: 1,
            X: Math.round((coordinate[0] - extent[0]) / resolution),
            Y: Math.round((extent[3] - coordinate[1]) / resolution),
            SRS: projection.getCode(),
            WIDTH: map.getSize()[0],
            HEIGHT: map.getSize()[1],
            BBOX: extent.join(',')
          };

          const url = `${GEOSERVER_URL}/wms?${new URLSearchParams(params)}`;
          const resp = await fetch(url);
          if (!resp.ok) continue;

          const data = await resp.json();
          if (data?.features?.length > 0) {
            const props = data.features[0].properties || {};
            const val = extractRasterValue(props);
            if (val !== null && val !== undefined && String(val).trim() !== '') {
              const numVal = parseFloat(val);
              if (!isNaN(numVal)) {
                meteoResults[layer.id] = { title: layer.title, value: numVal.toFixed(1) };
              } else {
                meteoResults[layer.id] = { title: layer.title, value: String(val) };
              }
            }
          }
        } catch (e) {
          console.warn(`Meteo GFI error for ${layer.title}:`, e);
        }
      }
      if (Object.keys(meteoResults).length > 0) {
        setMeteoInfo(meteoResults);
      }

      // Query vegetation period rasters - vrednost piksela
      const vegetationResults = {};
      for (const layer of VEGETATION_RASTER_LAYERS) {
        try {
          const params = {
            SERVICE: 'WMS',
            VERSION: '1.1.1',
            REQUEST: 'GetFeatureInfo',
            LAYERS: layer.wmsName,
            QUERY_LAYERS: layer.wmsName,
            INFO_FORMAT: 'application/json',
            FEATURE_COUNT: 1,
            X: Math.round((coordinate[0] - extent[0]) / resolution),
            Y: Math.round((extent[3] - coordinate[1]) / resolution),
            SRS: projection.getCode(),
            WIDTH: map.getSize()[0],
            HEIGHT: map.getSize()[1],
            BBOX: extent.join(',')
          };

          const url = `${GEOSERVER_URL}/wms?${new URLSearchParams(params)}`;
          const resp = await fetch(url);
          if (!resp.ok) continue;

          const data = await resp.json();
          if (data?.features?.length > 0) {
            const props = data.features[0].properties || {};
            const val = extractRasterValue(props);
            if (val !== null && val !== undefined && String(val).trim() !== '') {
              const numVal = parseFloat(val);
              if (!isNaN(numVal)) {
                vegetationResults[layer.id] = { title: layer.title, value: numVal.toFixed(1) };
              } else {
                vegetationResults[layer.id] = { title: layer.title, value: String(val) };
              }
            }
          }
        } catch (e) {
          console.warn(`Vegetation GFI error for ${layer.title}:`, e);
        }
      }
      if (Object.keys(vegetationResults).length > 0) {
        setVegetationInfo(vegetationResults);
      }

      // Query melioration layers
      const meliorationResults = {};
      for (const layer of MELIORATION_LAYERS) {
        try {
          const params = {
            SERVICE: 'WMS',
            VERSION: '1.1.1',
            REQUEST: 'GetFeatureInfo',
            LAYERS: layer.wmsName,
            QUERY_LAYERS: layer.wmsName,
            INFO_FORMAT: 'application/json',
            FEATURE_COUNT: 1,
            X: Math.round((coordinate[0] - extent[0]) / resolution),
            Y: Math.round((extent[3] - coordinate[1]) / resolution),
            SRS: projection.getCode(),
            WIDTH: map.getSize()[0],
            HEIGHT: map.getSize()[1],
            BBOX: extent.join(',')
          };

          const url = `${GEOSERVER_URL}/wms?${new URLSearchParams(params)}`;
          const resp = await fetch(url);
          if (!resp.ok) continue;

          const data = await resp.json();
          if (data?.features?.length > 0) {
            const props = data.features[0].properties || {};
            const val = props[layer.field];
            if (val !== undefined && val !== null && String(val).trim() !== '') {
              // Posebno mapiranje za ugrozenost sloj
              let displayValue = String(val);
              if (layer.id === 'ugrozenost') {
                displayValue = mapUgrozenostValue(val);
              }
              meliorationResults[layer.id] = { title: layer.title, value: displayValue };
            }
          }
        } catch (e) {
          console.warn(`Melioration GFI error for ${layer.title}:`, e);
        }
      }
      if (Object.keys(meliorationResults).length > 0) {
        setMeliorationInfo(meliorationResults);
      } else {
        setMeliorationInfo(null);
      }

      // Query ko_parcele layer (existing functionality)
      const layers = map.getLayers().getArray();
      const parceleLayer = layers.find(layer => {
        const source = layer.getSource();
        return source && source.getParams && source.getParams().LAYERS === 'Agris:ko_parcele';
      });

      if (parceleLayer && parceleLayer.getVisible()) {
        const params = {
          SERVICE: 'WMS',
          VERSION: '1.1.1',
          REQUEST: 'GetFeatureInfo',
          LAYERS: 'Agris:ko_parcele',
          QUERY_LAYERS: 'Agris:ko_parcele',
          INFO_FORMAT: 'application/json',
          FEATURE_COUNT: 1,
          X: Math.round((coordinate[0] - extent[0]) / resolution),
          Y: Math.round((extent[3] - coordinate[1]) / resolution),
          SRS: projection.getCode(),
          WIDTH: map.getSize()[0],
          HEIGHT: map.getSize()[1],
          BBOX: extent.join(',')
        };

        const url = `${GEOSERVER_URL}/wms?${new URLSearchParams(params)}`;
        
        const response = await fetch(url);
        if (response.ok) {
          const data = await response.json();
          
          if (data.features && data.features.length > 0) {
            const feature = data.features[0];
            const properties = feature.properties;
            
            setFeatureInfo({
              opstina: properties.opstina__1 || 'N/A',
              ko: properties.kat_opst_1 || 'N/A',
              brparcele: properties.brparcele || 'N/A'
            });
          }
        }
      }
    } catch (error) {
      console.error('Error performing GetFeatureInfo:', error);
      setFeatureInfo(null);
      setFruitTreeInfo(null);
      setIsFruitTreeExpanded(false);
    } finally {
      setIsFeatureInfoLoading(false);
    }
  }, []);
  
  /* ---------- DRAG & DROP STATE (vizuelni indikator) ---------- */
  const [draggingType, setDraggingType] = useState(null); // 'group' | 'layer' | null
  const [draggedGroupId, setDraggedGroupId] = useState(null);
  const [draggedLayerId, setDraggedLayerId] = useState(null);
  const [dragOverGroup, setDragOverGroup] = useState({ id: null, position: null }); // position: 'above' | 'below'
  const [dragOverLayer, setDragOverLayer] = useState({ groupId: null, layerId: null, position: null });

  // Ref to track when arrow buttons were used for reordering
  const [reorderTrigger, setReorderTrigger] = useState(0);
  
  // Debounced layer order update - optimizovano za brži odziv
  const layerOrderTimeoutRef = useRef(null);

  const clearDragState = () => {
    setDraggingType(null);
    setDraggedGroupId(null);
    setDraggedLayerId(null);
    setDragOverGroup({ id: null, position: null });
    setDragOverLayer({ groupId: null, layerId: null, position: null });
  };

  /* ---------- GROUP STATE ---------- */
  const [openGroups, setOpenGroups] = useState(() => {
    // All groups closed by default
    const initial = {};
    return initial;
  });

  const [openSubgroups, setOpenSubgroups] = useState(() => {
    // All subgroups closed by default
    const initial = {};
    return initial;
  });


  /* ---------- SEARCH STATE ---------- */
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [showSearchResults, setShowSearchResults] = useState(false);

  /* ---------- DOWNLOAD SEARCH STATE ---------- */
  const [downloadSearchQuery, setDownloadSearchQuery] = useState('');
  const [downloadSearchResults, setDownloadSearchResults] = useState([]);
  const [isDownloadSearching, setIsDownloadSearching] = useState(false);
  const [showDownloadSearchResults, setShowDownloadSearchResults] = useState(false);
  const [downloadParcelsList, setDownloadParcelsList] = useState([]);

  /* ---------- ZIP CREATION STATE ---------- */
  const [isCreatingZip, setIsCreatingZip] = useState(false);
  const [zipFilename, setZipFilename] = useState(null);

  /* ---------- MAIL VERIFICATION DIALOG ---------- */
  const [isMailDialogOpen, setIsMailDialogOpen] = useState(false);
  const [mailDialogMode, setMailDialogMode] = useState('zip');

  /* ---------- BOTTOM-LEFT ---------- */
  const [isBottomLeftOpen, setIsBottomLeftOpen] = useState(false);

  /* ---------- NOVO: "Alati" toggle/panel ---------- */
  const [isToolsOpen, setIsToolsOpen] = useState(false);

  // --- LEGEND STATE ---
  const [legendOpen, setLegendOpen] = useState(false);
  const [legendEntries, setLegendEntries] = useState([]); // [{ id, title, url? , items? }]
  const [legendInMenu, setLegendInMenu] = useState(false); // true = legend in secondary menu, false = legend in left panel

  // --- LOCATION STATE ---
  const [isLocationActive, setIsLocationActive] = useState(false); // true = location marker is visible, false = hidden

  // --- NOTIFICATION SYSTEM STATE ---
  const [notifications, setNotifications] = useState([]);

  // --- NOTIFICATION FUNCTIONS ---
  const addNotification = (message, type = 'info', duration = 5000) => {
    const id = Date.now() + Math.random();
    const notification = { id, message, type, duration };
    
    setNotifications(prev => [...prev, notification]);
    
    if (duration > 0) {
      setTimeout(() => {
        removeNotification(id);
      }, duration);
    }
    
    return id;
  };

  const removeNotification = (id) => {
    setNotifications(prev => prev.filter(n => n.id !== id));
  };

  const showLoadingNotification = (message) => {
    return addNotification(message, 'loading', 0); // 0 = no auto-remove
  };

  const showSuccessNotification = (message) => {
    return addNotification(message, 'success', 4000);
  };

  const showErrorNotification = (message) => {
    return addNotification(message, 'error', 6000);
  };

  // (opciono) - ako želiš da šalješ ceo SLD body za Pedološku (zameni '...SLD XML...' tvojim SLD-om)
  const PEDOLOSKA_SLD = `<?xml version="1.0" encoding="UTF-8"?>
<!-- OVDE NALepi svoj SLD XML za Pedološku -->
...SLD XML...
`;

  // Ako si stil već objavio u GeoServer-u (npr. "pedoloska_style"), upiši ga ovde:
  const PEDOLOSKA_STYLE_NAME = ""; // npr. "workspace:pedoloska_style"; ostavi prazno ako koristiš podrazumevani stil

  // Specifične klase za prikaz u legendi Pedološke (prema koloni TIP2)
  const PEDOLOSKA_CLASSES = [
    'Aluvijalno zemljište',
    'Antropogenizovani pesak',
    'Černozem',
    'Deluvijalno zemljište',
    'Gajnjača',
    'Inicijalno zemljište',
    'Kiselo smeđe zemljište',
    'Livadska crnica',
    'Močvarno glejno zemljište',
    'Pararendzina',
    'Pseudoglej-lesive',
    'Ranker',
    'Rendzina',
    'Ritska crnica',
    'Ritska smonica',
    'Smeđe stepsko zemljište',
    'Smonica',
    'Solođ',
    'Solončak',
    'Solonjec',
    'Solonjec i solođ',
    'Solonjec solončakasti',
    'Tresetno zemljište'
  ];

  // Boje za Pedološku (prema SLD pravilima i koloni TIP2)
  const PEDOLOSKA_COLORS = {
    'Aluvijalno zemljište': '#96d8ec',
    'Antropogenizovani pesak': '#c6ac6c',
    'Černozem': '#583525',
    'Deluvijalno zemljište': '#efeddc',
    'Gajnjača': '#fab177',
    'Inicijalno zemljište': '#faedaf',
    'Kiselo smeđe zemljište': '#ff060a',
    'Livadska crnica': '#894638',
    'Močvarno glejno zemljište': '#12d429',
    'Pararendzina': '#f7e0b1',
    'Pseudoglej-lesive': '#fcff5a',
    'Ranker': '#406d53',
    'Rendzina': '#e869ff',
    'Ritska crnica': '#6f6fb3',
    'Ritska smonica': '#392c6c',
    'Smeđe stepsko zemljište': '#b57b00',
    'Smonica': '#261e49',
    'Solođ': '#e869ff',
    'Solončak': '#eabad6',
    'Solonjec': '#ec599f',
    'Solonjec i solođ': '#ed00c5',
    'Solonjec solončakasti': '#e49cb3',
    'Tresetno zemljište': '#2f0535'
  };

  // Legenda za "Pogodnost zemljišta za navodnjavanje" (irigabilna klasa)
  const IRIGABILNA_LEGEND_ITEMS = [
    { title: 'I',    color: '#e87830' },
    { title: 'II',   color: '#f0c0e8' },
    { title: 'IIIa', color: '#2d6b2d' },
    { title: 'IIIb', color: '#50a0e0' },
    { title: 'IIIc', color: '#c030c0' },
  ];

  // Legenda za "Drenažne sposobnosti zemljišta"
  // Drenažne sposobnosti (atribut DRENAZNA) – zelena skala
  const DRENAZNA_LEGEND_ITEMS = [
    { title: '1', color: '#F39C12' },
    { title: '2', color: '#F4D03F' },
    { title: '3', color: '#A3D977' },
    { title: '4', color: '#58D68D' },
    { title: '5', color: '#27AE60' },
    { title: 'Ostalo', color: '#CCCCCC' },
  ];

  // Dren (atribut DRENAZNA_K) – plava skala
  const DRENAZNA_K_LEGEND_ITEMS = [
    { title: '1', color: '#F39C12' },      // narandžasta
    { title: '2', color: '#F4D03F' },      // zlatno žuta
    { title: '3', color: '#5DADE2' },      // svetlo plava
    { title: '4', color: '#2874A6' },      // tamnija plava
    { title: '5', color: '#1B4F72' },      // najtamnija plava
    { title: 'Ostalo', color: '#CCCCCC' },
  ];

  // IWQI-DTD klasifikacija
  const IWQI_DTD_LEGEND_ITEMS = [
    { title: 'I klasa',    color: '#2E7D32' },
    { title: 'II klasa',   color: '#FFF59D' },
    { title: 'III klasa',  color: '#FB8C00' },
    { title: 'IV klasa',   color: '#C62828' },
  ];

  // Ugroženost (IWQI_DTD_kategorije)
  const UGROZENOST_LEGEND_ITEMS = [
    { title: 'Visoka opasnost',   color: '#C62828' },
    { title: 'Niska opasnost',    color: '#FB8C00' },
    { title: 'Moguca opasnost',   color: '#FFF59D' },
    { title: 'Bez opasnosti',     color: '#2E7D32' },
  ];

  // GNDVI Čerević (pre-rendered RGB ramp: green = high, red = low)
  const GNDVI_CEREVIC_LEGEND_ITEMS = [
    { title: 'Vrlo visoko', color: '#d73027' },
    { title: 'Visoko',      color: '#f46d43' },
    { title: 'Srednje',     color: '#fee08b' },
    { title: 'Nisko',       color: '#1a9850' },
  ];

  // NDVI Čerević (pre-rendered RGB ramp)
  const NDVI_CEREVIC_LEGEND_ITEMS = [
    { title: 'Bujna vegetacija',     color: '#00cc00' },
    { title: 'Dobra vegetacija',     color: '#99ff00' },
    { title: 'Umerena vegetacija',   color: '#ffff00' },
    { title: 'Umereno suvo',         color: '#ffa500' },
    { title: 'Jako suvo',            color: '#ff0000' },
  ];

  // Parcela Rivica (black outline, no fill)
  const PARCELA_LEGEND_ITEMS = [
    { title: 'Parcele', color: 'transparent', borderColor: '#000000' },
  ];

  // Varijabilna setva (Rate)
  const VARIJABILNA_SETVA_LEGEND_ITEMS = [
    { title: '66000', color: '#f7fcf5' },
    { title: '68000', color: '#a1d99b' },
    { title: '74000', color: '#31a354' },
    { title: '78000', color: '#00441b' },
  ];

  // Legenda za "WRB2014" (WRB14_RSG)
  const WRB2014_LEGEND_ITEMS = [
    { title: 'Arenosols',  color: '#F2C572' },
    { title: 'Cambisols',  color: '#A8E053' },
    { title: 'Chernozems', color: '#7B4A12' },
    { title: 'Fluvisols',  color: '#66D1C8' },
    { title: 'Gleysols',   color: '#55B7F3' },
    { title: 'Histosols',  color: '#3E2723' },
    { title: 'Leptosols',  color: '#B0B0B0' },
    { title: 'Phaeozems',  color: '#C5912F' },
    { title: 'Planosols',  color: '#8FD3FF' },
    { title: 'Regosols',   color: '#E8D7B5' },
    { title: 'Solonchaks', color: '#F7A8B8' },
    { title: 'Solonetz',   color: '#E87E2A' },
    { title: 'Vertisols',  color: '#6A4C9C' },
    { title: 'Ostalo',     color: '#8E44AD' },
  ];

  // Legenda za "Suncokret (klase)"
  const SUNCOKRET_LEGEND_ITEMS = [
    { title: 'Van rejonizacije', color: '#E0E0E0' },
    { title: '50-60',            color: '#FFF0B3' },
    { title: '60-70',            color: '#FFD27F' },
    { title: '70-80',            color: '#FFAC5F' },
    { title: '80-90',            color: '#FF7A3C' },
    { title: '90-100',           color: '#D84A2B' },
  ];

  // Helper funkcija za dobijanje boje iz legende
  const getLegendColor = (layerId, value) => {
    if (layerId === 'pedoloska') {
      return PEDOLOSKA_COLORS[value] || '#CCCCCC';
    }
    if (layerId === 'pogodnost_zem_navod') {
      const it = IRIGABILNA_LEGEND_ITEMS.find(i => String(i.title) === String(value));
      return it ? it.color : '#CCCCCC';
    }
    if (layerId === 'dren_spos_zem') {
      const it = UGROZENOST_LEGEND_ITEMS.find(i => String(i.title) === String(value));
      return it ? it.color : '#CCCCCC';
    }
    if (layerId === 'wrb2014') {
      const it = WRB2014_LEGEND_ITEMS.find(i => String(i.title).toLowerCase() === String(value).toLowerCase());
      return it ? it.color : '#8E44AD'; // 'Ostalo' boja po tvojoj listi
    }
    if (layerId === 'suncokret') {
      const it = SUNCOKRET_LEGEND_ITEMS.find(i => String(i.title) === String(value));
      return it ? it.color : '#CCCCCC';
    }
    if (layerId === 'psenica') {
      const it = PSENICA_LEGEND_ITEMS.find(i => String(i.title) === String(value));
      return it ? it.color : '#CCCCCC';
    }
    if (layerId === 'soja') {
      const it = SOJA_LEGEND_ITEMS.find(i => String(i.title) === String(value));
      return it ? it.color : '#CCCCCC';
    }
    if (layerId === 'kukuruz') {
      const it = KUKURUZ_LEGEND_ITEMS.find(i => String(i.title) === String(value));
      return it ? it.color : '#CCCCCC';
    }
    return '#CCCCCC';
  };

  // Legenda za "Pšenica (klase)"
  const PSENICA_LEGEND_ITEMS = [
    { title: 'Van rejonizacije', color: '#E0E0E0' },
    { title: '80-85',            color: '#FFF2B2' },
    { title: '85-90',            color: '#FFE08A' },
    { title: '90-95',            color: '#F6C65B' },
    { title: '95-100',           color: '#C9942D' },
  ];

  // Legenda za "Soja (klase)"
  const SOJA_LEGEND_ITEMS = [
    { title: 'Van rejonizacije', color: '#E0E0E0' },
    { title: '40-50',            color: '#FFFFE5' },
    { title: '50-60',            color: '#F7FCB9' },
    { title: '60-70',            color: '#D9F0A3' },
    { title: '70-80',            color: '#ADDD8E' },
    { title: '80-90',            color: '#78C679' },
    { title: '90-100',           color: '#31A354' },
  ];

  // Legenda za "Kukuruz (klase)"
  const KUKURUZ_LEGEND_ITEMS = [
    { title: 'Van rejonizacije', color: '#E0E0E0' },
    { title: '50-60',            color: '#FFF7BC' },
    { title: '60-70',            color: '#FEE391' },
    { title: '70-80',            color: '#FEC44F' },
    { title: '80-90',            color: '#FE9929' },
    { title: '90-100',           color: '#D95F0E' },
  ];

  // Legenda za sve slojeve iz grupe "Voćarstvo"
  const VOCARSTVO_LEGEND_ITEMS = [
    { title: 'Nepovoljno',           color: '#FF0000' },
    { title: 'Delimično povoljno',   color: '#FFFF00' },
    { title: 'Povoljno',             color: '#00FF00' },
  ];
  const VOCARSTVO_LAYER_IDS = (LAYER_GROUPS.find(g => g.id === 'vocarstvo')?.layers) || [];

  /* ---------- STATUS BAR (CRS, koordinate miša, razmera) ---------- */
  const [mouseUtm, setMouseUtm] = useState(null); // { x, y } u metrima (EPSG:32634)
  const [scaleText, setScaleText] = useState(''); // "1:xxxxx"

  const computeScaleForMap = (mapObj) => {
    if (!mapObj) return;
    try {
      const view = mapObj.getView();
      const resolution = view.getResolution();
      
      // Za UTM CRS (EPSG:32634), resolution je već u metrima po pikselu
      // Izračun razmere prema OGC 0.28mm/px specifikaciji: s = m/px / 0.00028
      const scale = Math.max(1, Math.round(resolution / OGC_METERS_PER_PX));
      const scaleStr = String(scale).replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
      setScaleText(`1:${scaleStr}`);
    } catch (_) {
      // noop
    }
  };


  // Helper koji pravi URL za GetLegendGraphic
  const buildLegendUrl = ({ wmsName, sldBody, styleName, scale = 40000 }) => {
    const base = `${GEOSERVER_URL}/wms`;
    const params = new URLSearchParams({
      service: 'WMS',
      version: '1.1.1',
      request: 'GetLegendGraphic',
      format: 'image/png',
      layer: wmsName,
      transparent: 'true',
      // opcionalno, lepši font i veličina
      legend_options: 'fontAntiAliasing:true;fontSize:12',
      // opcionalno, podesi SCALE da odgovara tvom prikazu
      SCALE: String(scale)
    });

    if (styleName) {
      params.set('style', styleName);
    } else if (sldBody) {
      params.set('SLD_BODY', sldBody);
    }
    return `${base}?${params.toString()}`;
  };

  const addLegendEntry = (entry) => {
    setLegendEntries(prev => prev.some(e => e.id === entry.id) ? prev : [...prev, entry]);
  };

  const removeLegendFor = (layerId) => {
    setLegendEntries(prev => {
      const next = prev.filter(e => e.id !== layerId);
      if (next.length === 0) setLegendOpen(false);
      return next;
    });
  };

  // Sort legend entries according to groupOrder and groupLayersOrder
  const getSortedLegendEntries = () => {
    // Build ordered list: for each group in groupOrder, use its layer order
    const orderedLayerIds = [];
    groupOrder.forEach(groupId => {
      const group = LAYER_GROUPS.find(g => g.id === groupId);
      if (!group) return;
      
      if (group.layers) {
        const layerIds = groupLayersOrder[groupId] || group.layers;
        layerIds.forEach(id => orderedLayerIds.push(id));
      } else if (group.subgroups) {
        group.subgroups.forEach(subgroup => {
          const subgroupKey = `${groupId}.${subgroup.id}`;
          const layerIds = groupLayersOrder[subgroupKey] || subgroup.layers;
          layerIds.forEach(id => orderedLayerIds.push(id));
        });
      }
    });

    // Sort legend entries according to the ordered layer IDs
    return legendEntries.sort((a, b) => {
      const aIndex = orderedLayerIds.indexOf(a.id);
      const bIndex = orderedLayerIds.indexOf(b.id);
      
      // If both are in the ordered list, sort by their position
      if (aIndex !== -1 && bIndex !== -1) {
        return aIndex - bIndex;
      }
      
      // If only one is in the ordered list, prioritize the one that is
      if (aIndex !== -1) return -1;
      if (bIndex !== -1) return 1;
      
      // If neither is in the ordered list, maintain original order
      return 0;
    });
  };

  // Pokaži legendu za traženi sloj (posebno tretiramo pedološku)
  const showLegendFor = (layerId) => {
    const layer = WMS_LAYERS.find(l => l.id === layerId);
    if (!layer) return;

    let url = '';

    if (layer.id === 'pedoloska') {
      // Custom lista + naše boje
      addLegendEntry({ id: layer.id, title: layer.title, items: PEDOLOSKA_CLASSES.map(t => ({ title: t, color: PEDOLOSKA_COLORS[t] || '#ddd' })) });
      setLegendOpen(true);
      return;
    } else if (VOCARSTVO_LAYER_IDS.includes(layer.id)) {
      // Jedinstvena legenda za sve kulture iz grupe Voćarstvo
      addLegendEntry({ id: layer.id, title: layer.title, items: VOCARSTVO_LEGEND_ITEMS });
      setLegendOpen(true);
      return;
    } else if (layer.id === 'pogodnost_zem_navod') {
      // Custom lista po prosleđenom SLD-u
      addLegendEntry({ id: layer.id, title: layer.title, items: IRIGABILNA_LEGEND_ITEMS });
      setLegendOpen(true);
      return;
    } else if (layer.id === 'dren_spos_zem') {
      // Ugroženost zemljišta navodnjavanjem
      addLegendEntry({ id: layer.id, title: layer.title, items: UGROZENOST_LEGEND_ITEMS });
      setLegendOpen(true);
      return;
    } else if (layer.id === 'dren') {
      // Dren – plava skala (DRENAZNA_K)
      addLegendEntry({ id: layer.id, title: layer.title, items: DRENAZNA_K_LEGEND_ITEMS });
      setLegendOpen(true);
      return;
    } else if (layer.id === 'wrb2014') {
      // Custom lista po prosleđenom SLD-u za WRB2014
      addLegendEntry({ id: layer.id, title: layer.title, items: WRB2014_LEGEND_ITEMS });
      setLegendOpen(true);
      return;
    } else if (layer.id === 'ugrozenost') {
      // Ugroženost – IWQI_DTD kategorije
      addLegendEntry({ id: layer.id, title: layer.title, items: UGROZENOST_LEGEND_ITEMS });
      setLegendOpen(true);
      return;
    } else if (layer.id === 'iwqi_dtd') {
      // IWQI-DTD klasifikacija
      addLegendEntry({ id: layer.id, title: layer.title, items: IWQI_DTD_LEGEND_ITEMS });
      setLegendOpen(true);
      return;
    } else if (layer.id === 'varijabilna_setva') {
      // Varijabilna setva – Rate
      addLegendEntry({ id: layer.id, title: layer.title, items: VARIJABILNA_SETVA_LEGEND_ITEMS });
      setLegendOpen(true);
      return;
    } else if (layer.id === 'parcela_rivica') {
      // Parcela – black outline
      addLegendEntry({ id: layer.id, title: layer.title, items: PARCELA_LEGEND_ITEMS });
      setLegendOpen(true);
      return;
    } else if (layer.id === 'gndvi_cerevic') {
      // GNDVI Čerević
      addLegendEntry({ id: layer.id, title: layer.title, items: GNDVI_CEREVIC_LEGEND_ITEMS });
      setLegendOpen(true);
      return;
    } else if (layer.id === 'ndvi_cerevic') {
      // NDVI Čerević
      addLegendEntry({ id: layer.id, title: layer.title, items: NDVI_CEREVIC_LEGEND_ITEMS });
      setLegendOpen(true);
      return;
    } else if (layer.id === 'suncokret') {
      // Custom lista po prosleđenom SLD-u za Suncokret
      addLegendEntry({ id: layer.id, title: layer.title, items: SUNCOKRET_LEGEND_ITEMS });
      setLegendOpen(true);
      return;
    } else if (layer.id === 'psenica') {
      // Custom lista po prosleđenom SLD-u za Pšenicu
      addLegendEntry({ id: layer.id, title: layer.title, items: PSENICA_LEGEND_ITEMS });
      setLegendOpen(true);
      return;
    } else if (layer.id === 'soja') {
      // Custom lista po prosleđenom SLD-u za Soju
      addLegendEntry({ id: layer.id, title: layer.title, items: SOJA_LEGEND_ITEMS });
      setLegendOpen(true);
      return;
    } else if (layer.id === 'kukuruz') {
      // Custom lista po prosleđenom SLD-u za Kukuruz
      addLegendEntry({ id: layer.id, title: layer.title, items: KUKURUZ_LEGEND_ITEMS });
      setLegendOpen(true);
      return;
    } else if (layer.id === 'ndvi1') {
      // Custom lista za NDVI1 na osnovu SLD koda
      const NDVI1_LEGEND_ITEMS = [
        { title: 'Vrlo nisko (0.15 - 0.31)', color: '#ffffcc' },
        { title: 'Nisko (0.31 - 0.47)', color: '#c2e699' },
        { title: 'Srednje (0.47 - 0.63)', color: '#78c679' },
        { title: 'Visoko (0.63 - 0.79)', color: '#31a354' },
        { title: 'Vrlo visoko (0.79 - 0.95)', color: '#006837' }
      ];
      addLegendEntry({ id: layer.id, title: layer.title, items: NDVI1_LEGEND_ITEMS });
      setLegendOpen(true);
      return;
    } else if (layer.id === 'ndre1') {
      // Custom lista za NDRE1 na osnovu SLD koda
      const NDRE1_LEGEND_ITEMS = [
        { title: 'Vrlo nisko (0.0 - 0.11)', color: '#4a2800' },
        { title: 'Nisko (0.11 - 0.23)', color: '#6b3a0a' },
        { title: 'Srednje nisko (0.23 - 0.34)', color: '#8b5e2f' },
        { title: 'Srednje (0.34 - 0.45)', color: '#a57840' },
        { title: 'Visoko (0.45 - 0.51)', color: '#c49a30' },
        { title: 'Vrlo visoko (0.51 - 0.56)', color: '#e8c520' }
      ];
      addLegendEntry({ id: layer.id, title: layer.title, items: NDRE1_LEGEND_ITEMS });
      setLegendOpen(true);
      return;
    } else if (layer.id === 'ndvi2') {
      // Custom lista za NDVI2 na osnovu SLD koda
      const NDVI2_LEGEND_ITEMS = [
        { title: 'Vrlo nisko (0.10 - 0.27)', color: '#ffffcc' },
        { title: 'Nisko (0.27 - 0.43)', color: '#c2e699' },
        { title: 'Srednje (0.43 - 0.60)', color: '#78c679' },
        { title: 'Visoko (0.60 - 0.77)', color: '#31a354' },
        { title: 'Vrlo visoko (0.77 - 0.93)', color: '#006837' }
      ];
      addLegendEntry({ id: layer.id, title: layer.title, items: NDVI2_LEGEND_ITEMS });
      setLegendOpen(true);
      return;
    } else if (layer.id === 'ndre2') {
      // Custom lista za NDRE2 na osnovu SLD koda
      const NDRE2_LEGEND_ITEMS = [
        { title: 'Vrlo nisko (0.0 - 0.14)', color: '#4a2800' },
        { title: 'Nisko (0.14 - 0.24)', color: '#6b3a0a' },
        { title: 'Srednje nisko (0.24 - 0.34)', color: '#8b5e2f' },
        { title: 'Srednje (0.34 - 0.44)', color: '#a57840' },
        { title: 'Visoko (0.44 - 0.53)', color: '#c49a30' },
        { title: 'Vrlo visoko (0.53+)', color: '#e8c520' }
      ];
      addLegendEntry({ id: layer.id, title: layer.title, items: NDRE2_LEGEND_ITEMS });
      setLegendOpen(true);
      return;
    } else if (layer.id === 'razlika_padavina_veg_91_20_vs_61_90') {
      // 5 jednakih intervala na osnovu stvarnih min/max vrednosti
      const minQ = -56.656227111816;
      const maxQ = 64.097305297852;
      const colors = ['#b2182b', '#f4a582', '#f7f7f7', '#92c5de', '#2166ac'];
      const items = buildEqualIntervalLegendItems(minQ, maxQ, colors);
      addLegendEntry({ id: layer.id, title: layer.title, items });
      setLegendOpen(true);
      return;
    } else if (layer.id === 'razlika_temp_veg_91_20_vs_61_90') {
      // 5 jednakih intervala na osnovu stvarnih min/max vrednosti
      const minQ = 3.0594805139117e-05;
      const maxQ = 1.59996342659;
      const colors = ['#2166ac', '#92c5de', '#f7f7f7', '#f4a582', '#b2182b'];
      const items = buildEqualIntervalLegendItems(minQ, maxQ, colors);
      addLegendEntry({ id: layer.id, title: layer.title, items });
      setLegendOpen(true);
      return;
    } else if (layer.id === 'padavine_veg_61_90') {
      // 5 jednakih intervala na osnovu stvarnih min/max vrednosti
      const minQ = 313.83209228516;
      const maxQ = 561.29016113281;
      const colors = ['#F7FBFF', '#DEEBF7', '#9ECAE1', '#3182BD', '#08519C'];
      const items = buildEqualIntervalLegendItems(minQ, maxQ, colors);
      addLegendEntry({ id: layer.id, title: layer.title, items });
      setLegendOpen(true);
      return;
    } else if (layer.id === 'padavine_veg_91_20') {
      // 5 jednakih intervala na osnovu stvarnih min/max vrednosti
      const minQ = 313.50073242188;
      const maxQ = 588.08880615234;
      const colors = ['#F7FBFF', '#DEEBF7', '#9ECAE1', '#3182BD', '#08519C'];
      const items = buildEqualIntervalLegendItems(minQ, maxQ, colors);
      addLegendEntry({ id: layer.id, title: layer.title, items });
      setLegendOpen(true);
      return;
    } else if (layer.id === 'temp_veg_61_90') {
      // 5 jednakih intervala na osnovu stvarnih min/max vrednosti
      const minQ = 7.9171772003174;
      const maxQ = 18.499300003052;
      const colors = ['#FFFFCC', '#FED976', '#FD8D3C', '#F03B20', '#BD0026'];
      const items = buildEqualIntervalLegendItems(minQ, maxQ, colors);
      addLegendEntry({ id: layer.id, title: layer.title, items });
      setLegendOpen(true);
      return;
    } else if (layer.id === 'temp_veg_91_20') {
      // 5 jednakih intervala na osnovu stvarnih min/max vrednosti
      const minQ = 9.5171403884888;
      const maxQ = 19.966218948364;
      const colors = ['#FFFFCC', '#FED976', '#FD8D3C', '#F03B20', '#BD0026'];
      const items = buildEqualIntervalLegendItems(minQ, maxQ, colors);
      addLegendEntry({ id: layer.id, title: layer.title, items });
      setLegendOpen(true);
      return;
    } else if (layer.id === 'dni_gt10_veg_61_90') {
      // 5 jednakih intervala na osnovu stvarnih min/max vrednosti
      const minQ = 9.400465965271;
      const maxQ = 18.699647903442;
      const colors = ['#F7FBFF', '#DEEBF7', '#9ECAE1', '#3182BD', '#08519C'];
      const items = buildEqualIntervalLegendItems(minQ, maxQ, colors);
      addLegendEntry({ id: layer.id, title: layer.title, items });
      setLegendOpen(true);
      return;
    } else if (layer.id === 'dni_gt10_veg_91_20') {
      // Legenda za "Srednji broj dana sa količinom padavina većom od 10.0 mm u vegetacionom periodu april-septembar (1991-2020)"
      // Na osnovu SLD koda sa tačnim break vrednostima
      const items = [
        { title: 'Od 9.7 do 11.3', color: '#F7FBFF' },
        { title: 'Od 11.3 do 13.1', color: '#DEEBF7' },
        { title: 'Od 13.1 do 15.0', color: '#9ECAE1' },
        { title: 'Od 15.0 do 16.8', color: '#3182BD' },
        { title: 'Od 16.8 do 20.3', color: '#08519C' }
      ];
      addLegendEntry({ id: layer.id, title: layer.title, items });
      setLegendOpen(true);
      return;
    } else if (layer.id === 'razlika_mraznih_91_20_vs_61_90') {
      // 5 jednakih intervala na osnovu stvarnih min/max vrednosti
      const minQ = -16.299478530884;
      const maxQ = 1.1990979909897;
      const colors = ['#FFFFFF', '#DBEEFF', '#9ECAE1', '#4AA3DF', '#08519C'];
      const items = buildEqualIntervalLegendItems(minQ, maxQ, colors);
      addLegendEntry({ id: layer.id, title: layer.title, items });
      setLegendOpen(true);
      return;
    } else if (layer.id === 'razlika_tropskih_91_20_vs_61_90') {
      // 5 jednakih intervala na osnovu stvarnih min/max vrednosti
      const minQ = 0.00028390804072842;
      const maxQ = 22.095624923706;
      const colors = ['#FFFFCC', '#FFE08A', '#FFB347', '#FF7043', '#D32F2F'];
      const items = buildEqualIntervalLegendItems(minQ, maxQ, colors);
      addLegendEntry({ id: layer.id, title: layer.title, items });
      setLegendOpen(true);
      return;
    } else if (layer.id === 'razlika_padavina_91_20_vs_61_90') {
      // 5 jednakih intervala na osnovu stvarnih min/max vrednosti
      const minQ = -12.479166030884;
      const maxQ = 119.294090271;
      const colors = ['#E0F3F8', '#ABD9E9', '#74ADD1', '#4575B4', '#313695'];
      const items = buildEqualIntervalLegendItems(minQ, maxQ, colors);
      addLegendEntry({ id: layer.id, title: layer.title, items });
      setLegendOpen(true);
      return;
    } else if (layer.id === 'razlika_temp_91_20_vs_61_90') {
      // 5 jednakih intervala na osnovu stvarnih min/max vrednosti
      const minQ = 2.6496170903556e-05;
      const maxQ = 1.4166322946548;
      const colors = ['#313695', '#4575B4', '#74ADD1', '#FDAE61', '#D73027'];
      const items = buildEqualIntervalLegendItems(minQ, maxQ, colors);
      addLegendEntry({ id: layer.id, title: layer.title, items });
      setLegendOpen(true);
      return;
    } else if (layer.id === 'padavine_61_90') {
      // 5 jednakih intervala na osnovu stvarnih min/max vrednosti
      const minQ = 535.20776367188;
      const maxQ = 964.24310302734;
      const colors = ['#E0F3F8', '#ABD9E9', '#74ADD1', '#4575B4', '#313695'];
      const items = buildEqualIntervalLegendItems(minQ, maxQ, colors);
      addLegendEntry({ id: layer.id, title: layer.title, items });
      setLegendOpen(true);
      return;
    } else if (layer.id === 'padavine_91_20') {
      // 5 jednakih intervala na osnovu stvarnih min/max vrednosti
      const minQ = 571.2080078125;
      const maxQ = 1040.0815429688;
      const colors = ['#E0F3F8', '#ABD9E9', '#74ADD1', '#4575B4', '#313695'];
      const items = buildEqualIntervalLegendItems(minQ, maxQ, colors);
      addLegendEntry({ id: layer.id, title: layer.title, items });
      setLegendOpen(true);
      return;
    } else if (layer.id === 'temp_61_90') {
      // 5 jednakih intervala na osnovu stvarnih min/max vrednosti
      const minQ = 2.7254374027252;
      const maxQ = 11.899627685547;
      const colors = ['#313695', '#4575B4', '#FFFFBF', '#FDAE61', '#D73027'];
      const items = buildEqualIntervalLegendItems(minQ, maxQ, colors);
      addLegendEntry({ id: layer.id, title: layer.title, items });
      setLegendOpen(true);
      return;
    } else if (layer.id === 'temp_91_20') {
      // 5 jednakih intervala na osnovu stvarnih min/max vrednosti
      const minQ = 4.1420697;
      const maxQ = 13.182904;
      const colors = ['#313695', '#4575B4', '#FFFFBF', '#FDAE61', '#D73027'];
      const items = buildEqualIntervalLegendItems(minQ, maxQ, colors);
      addLegendEntry({ id: layer.id, title: layer.title, items });
      setLegendOpen(true);
      return;
    } else if (layer.id === 'mrazni_61_90') {
      // 5 jednakih intervala na osnovu stvarnih min/max vrednosti
      const minQ = 62.207233428955;
      const maxQ = 178.69514465332;
      const colors = ['#313695', '#4575B4', '#74ADD1', '#ABD9E9', '#FFFFFF'];
      const items = buildEqualIntervalLegendItems(minQ, maxQ, colors);
      addLegendEntry({ id: layer.id, title: layer.title, items });
      setLegendOpen(true);
      return;
    } else if (layer.id === 'mrazni_91_20') {
      // 5 jednakih intervala na osnovu stvarnih min/max vrednosti
      const minQ = 52.107517242432;
      const maxQ = 162.39567565918;
      const colors = ['#313695', '#4575B4', '#74ADD1', '#ABD9E9', '#FFFFFF'];
      const items = buildEqualIntervalLegendItems(minQ, maxQ, colors);
      addLegendEntry({ id: layer.id, title: layer.title, items });
      setLegendOpen(true);
      return;
    } else if (layer.id === 'tropski_61_90') {
      // 5 jednakih intervala na osnovu stvarnih min/max vrednosti
      const minQ = 0.0012840508716181;
      const maxQ = 36.295749664307;
      const colors = ['#FFFFCC', '#FFE08A', '#FFB347', '#FF7043', '#D32F2F'];
      const items = buildEqualIntervalLegendItems(minQ, maxQ, colors);
      addLegendEntry({ id: layer.id, title: layer.title, items });
      setLegendOpen(true);
      return;
    } else if (layer.id === 'tropski_91_20') {
      // 5 jednakih intervala na osnovu stvarnih min/max vrednosti
      const minQ = 0.0020389747805893;
      const maxQ = 56.194526672363;
      const colors = ['#FFFFCC', '#FFE08A', '#FFB347', '#FF7043', '#D32F2F'];
      const items = buildEqualIntervalLegendItems(minQ, maxQ, colors);
      addLegendEntry({ id: layer.id, title: layer.title, items });
      setLegendOpen(true);
      return;
    } else if (layer.id === 'ko_parcele' || layer.id === 'ko_vojvodina') {
      // Ne prikazuj legendu za parcele i KO Vojvodina
      return;
    } else if (layer.id === 'of1' || layer.id === 'of2' || layer.id === 'of_12jun23' || layer.id === 'of_14jul23' || layer.id === 'of_17avg23' || layer.id === 'of_21avg20') {
      // Ne prikazuj legendu za ortofoto slojeve
      return;
    } else if (layer.id === 'ndvi_temerin_12jun23') {
      // Custom lista za NDVI Temerin 12jun23 na osnovu SLD koda
      const NDVI_TEMERIN_12JUN23_LEGEND_ITEMS = [
        { title: 'Vrlo nisko (0.07 - 0.24)', color: '#ffffcc' },
        { title: 'Nisko (0.24 - 0.42)', color: '#c2e699' },
        { title: 'Srednje (0.42 - 0.59)', color: '#78c679' },
        { title: 'Visoko (0.59 - 0.77)', color: '#31a354' },
        { title: 'Vrlo visoko (0.77 - 0.94)', color: '#006837' }
      ];
      addLegendEntry({ id: layer.id, title: layer.title, items: NDVI_TEMERIN_12JUN23_LEGEND_ITEMS });
      setLegendOpen(true);
      return;
    } else if (layer.id === 'ndvi_temerin_14jul23') {
      // Custom lista za NDVI Temerin 14jul23 na osnovu SLD koda
      const NDVI_TEMERIN_14JUL23_LEGEND_ITEMS = [
        { title: 'Vrlo nisko (0.38 - 0.49)', color: '#ffffcc' },
        { title: 'Nisko (0.49 - 0.60)', color: '#c2e699' },
        { title: 'Srednje (0.60 - 0.72)', color: '#78c679' },
        { title: 'Visoko (0.72 - 0.83)', color: '#31a354' },
        { title: 'Vrlo visoko (0.83 - 0.94)', color: '#006837' }
      ];
      addLegendEntry({ id: layer.id, title: layer.title, items: NDVI_TEMERIN_14JUL23_LEGEND_ITEMS });
      setLegendOpen(true);
      return;
    } else if (layer.id === 'ndvi_temerin_17avg23') {
      // Custom lista za NDVI Temerin 17avg23 na osnovu SLD koda
      const NDVI_TEMERIN_17AVG23_LEGEND_ITEMS = [
        { title: 'Vrlo nisko (0.36 - 0.47)', color: '#ffffcc' },
        { title: 'Nisko (0.47 - 0.59)', color: '#c2e699' },
        { title: 'Srednje (0.59 - 0.70)', color: '#78c679' },
        { title: 'Visoko (0.70 - 0.81)', color: '#31a354' },
        { title: 'Vrlo visoko (0.81 - 0.93)', color: '#006837' }
      ];
      addLegendEntry({ id: layer.id, title: layer.title, items: NDVI_TEMERIN_17AVG23_LEGEND_ITEMS });
      setLegendOpen(true);
      return;
    } else if (layer.id === 'sk_dtm') {
      // Custom lista za SK DTM na osnovu SLD koda
      const SK_DTM_LEGEND_ITEMS = [
        { title: 'Visoko (208.10 - 211.96)', color: '#8c510a' },
        { title: 'Više srednje (204.23 - 208.10)', color: '#d08b44' },
        { title: 'Srednje (200.36 - 204.23)', color: '#fee08b' },
        { title: 'Niže srednje (196.49 - 200.36)', color: '#66bd63' },
        { title: 'Nisko (192.62 - 196.49)', color: '#006837' }
      ];
      addLegendEntry({ id: layer.id, title: layer.title, items: SK_DTM_LEGEND_ITEMS });
      setLegendOpen(true);
      return;
    } else if (layer.id === 'csm_21avg20') {
      // Custom lista za CSM 21avg20 na osnovu SLD koda
      const CSM_21AVG20_LEGEND_ITEMS = [
        { title: 'Vrlo visoko (2.54 - 3.18)', color: '#8c2d04' },
        { title: 'Visoko (1.90 - 2.54)', color: '#238443' },
        { title: 'Srednje (1.26 - 1.90)', color: '#78c679' },
        { title: 'Nisko (0.62 - 1.26)', color: '#c2e699' },
        { title: 'Vrlo nisko (-0.02 - 0.62)', color: '#ffffcc' }
      ];
      addLegendEntry({ id: layer.id, title: layer.title, items: CSM_21AVG20_LEGEND_ITEMS });
      setLegendOpen(true);
      return;
    } else {
      // Generički za sve ostale slojeve - PNG iz GeoServer-a
      url = buildLegendUrl({ wmsName: layer.wmsName });
    }

    addLegendEntry({ id: layer.id, title: layer.title, url });
    setLegendOpen(true);
  };

  /* ---------- Layout / Swipe / Lens ---------- */
  const [layout, setLayout] = useState('row');
  const [sliderOrientation, setSliderOrientation] = useState('vertical');
  const [sliderPos, setSliderPos] = useState(0.5);
  const [debouncedSliderPos, setDebouncedSliderPos] = useState(0.5);
  const draggingRef = useRef(false);
  const sliderTimeoutRef = useRef(null);
  
  // Debounce slider position updates for performance
  useEffect(() => {
    if (sliderTimeoutRef.current) {
      clearTimeout(sliderTimeoutRef.current);
    }
    
    sliderTimeoutRef.current = setTimeout(() => {
      setDebouncedSliderPos(sliderPos);
    }, 2); // ~500fps za maksimalno glatak prikaz
    
    return () => {
      if (sliderTimeoutRef.current) {
        clearTimeout(sliderTimeoutRef.current);
      }
    };
  }, [sliderPos]);

  const [lensRadius, setLensRadius] = useState(160);
  const [lensPos, setLensPos] = useState({ x: 0.5, y: 0.5 });
  const lensWrapperRef = useRef(null);

  const isSyncingRef = useRef(false);

  /* ---------- Panel sizes ---------- */
  const primaryMenuWidth   = 300;
  const secondaryMenuWidth = 360;
  const spacingFromMenu    = 25;

  const activeMenuWidth =
    isSecondaryMenuOpen ? secondaryMenuWidth
      : isPrimaryMenuOpen ? primaryMenuWidth
      : 0;
  const offsetRight = spacingFromMenu + activeMenuWidth;

  /* ---------- BOUNDS CACHE (GetCapabilities) ---------- */
  // wmsName -> [[minLng,minLat],[maxLng,maxLat]]
  const boundsCacheRef = useRef({});
  const capsFetchedRef = useRef(false);
  const capsFetchingRef = useRef(false);

  const parseBoundsFromLayerNode = (layerEl) => {
    const ex = layerEl.querySelector('EX_GeographicBoundingBox');
    if (ex) {
      const west  = parseFloat(ex.querySelector('westBoundLongitude')?.textContent);
      const south = parseFloat(ex.querySelector('southBoundLatitude')?.textContent);
      const east  = parseFloat(ex.querySelector('eastBoundLongitude')?.textContent);
      const north = parseFloat(ex.querySelector('northBoundLatitude')?.textContent);
      if ([west, south, east, north].every(v => Number.isFinite(v))) {
        return [[west, south], [east, north]];
      }
    }
    const ll = layerEl.querySelector('LatLonBoundingBox');
    if (ll) {
      const minx = parseFloat(ll.getAttribute('minx'));
      const miny = parseFloat(ll.getAttribute('miny'));
      const maxx = parseFloat(ll.getAttribute('maxx'));
      const maxy = parseFloat(ll.getAttribute('maxy'));
      if ([minx, miny, maxx, maxy].every(v => Number.isFinite(v))) {
        return [[minx, miny], [maxx, maxy]];
      }
    }
    return null;
  };

  const fetchCapabilitiesOnce = async () => {
    if (capsFetchedRef.current || capsFetchingRef.current) return;
    capsFetchingRef.current = true;
    try {
      const url = `${GEOSERVER_URL}/wms?service=WMS&version=1.3.0&request=GetCapabilities`;
      const resp = await fetch(url);
      const text = await resp.text();
      const doc = new window.DOMParser().parseFromString(text, 'text/xml');

      const layerNodes = Array.from(doc.querySelectorAll('Layer > Layer'));
      const byName = {};
      layerNodes.forEach(node => {
        const name = node.querySelector('Name')?.textContent?.trim();
        if (name) byName[name] = node;
      });

      const namesToCache = [
        ...WMS_LAYERS.map(({ wmsName }) => wmsName),
        ...EXTRA_WMS_NAMES
      ];
      namesToCache.forEach((wmsName) => {
        const node = byName[wmsName];
        if (!node) return;
        const b = parseBoundsFromLayerNode(node);
        if (b) boundsCacheRef.current[wmsName] = b;
      });

      capsFetchedRef.current = true;
    } catch (e) {
      console.warn('GetCapabilities fetch fail:', e);
    } finally {
      capsFetchingRef.current = false;
    }
  };

  const fitToLayer = async (layerId, targetMapRef) => {
    const layer = WMS_LAYERS.find(l => l.id === layerId);
    if (!layer || !targetMapRef?.current) return;

    try {
      let extent;
      
      // Za sloj parcela, uvek prikaži celu Vojvodinu
      if (layerId === 'ko_parcele') {
        // Granice Vojvodine u EPSG:4326 (longitude, latitude)
        const vojvodinaBounds = [18.8, 44.6, 21.5, 46.2]; // [west, south, east, north]
        extent = transformExtent(vojvodinaBounds, 'EPSG:4326', 'EPSG:32634');
      } else {
        // Za ostale slojeve, koristi bounds iz GetCapabilities
        if (!capsFetchedRef.current && !capsFetchingRef.current) {
          await fetchCapabilitiesOnce();
        }

        const bounds = boundsCacheRef.current[layer.wmsName];
        if (!bounds) return;
        
        extent = transformExtent(bounds.flat(), 'EPSG:4326', 'EPSG:32634');
      }
      
      const bufferedExtent = bufferExtent(extent, 100); // Povećaj buffer za bolje uokvirivanje sloja
      const view = targetMapRef.current.getView();
      
      // Koristi fit method za automatsko skaliranje sloja
      view.fit(bufferedExtent, {
        duration: 1000, // Duža animacija za slojeve (1 sekunda)
        padding: [50, 50, 50, 50], // Veći padding za slojeve
        maxZoom: 16 // Ograniči maksimalni zoom za slojeve
      });
      
      // Ne zoomuj automatski drugu mapu - korisnik kontroliše svaku mapu nezavisno
      
    } catch (e) {
      console.warn('fitBounds error:', e);
    }
  };

  // Pokušaj da centriraš/zoomiraš na ceo sloj "Agris:srbija" jednom
  // (uklonjeno višestruko pozicioniranje – finalni pogled se postavlja pre inicijalnog prikaza)

  // (uklonjeno: dodatno inicijalno pozicioniranje – sada se centar/zoom postavljaju u style.load)

  /* ---------- STYLE: obe podloge prisutne ---------- */
  const createBaseLayers = () => {
    const googleLayer = new TileLayer({
      source: new XYZ({
        url: process.env.REACT_APP_GOOGLE_SATELLITE_URL,
        attributions: '<a href="https://www.google.com/help/legalnotices_maps/" target="_blank">© Google Maps</a>',
        crossOrigin: 'anonymous',
        tilePixelRatio: 1,
        transition: 0
      }),
      visible: false
    });
    googleLayer.set('id', 'base-google');

    const osmLayer = new TileLayer({
      source: new OSM({
        url: process.env.REACT_APP_OSM_TILE_URL,
        crossOrigin: 'anonymous',
        tilePixelRatio: 1,
        transition: 0
      }),
      visible: true
    });
    osmLayer.set('id', 'base-osm');

    return { googleLayer, osmLayer };
  };

  const applyBaseVisibility = (mapObj, type) => {
    if (!mapObj) return;
    const showGoogle = type === 'google';
    
    const layers = mapObj.getLayers();
    const googleLayer = layers.getArray().find(layer => layer.get('id') === 'base-google');
    const osmLayer = layers.getArray().find(layer => layer.get('id') === 'base-osm');
    
    if (googleLayer) googleLayer.setVisible(showGoogle);
    if (osmLayer) osmLayer.setVisible(!showGoogle);
  };

    // -- WMS rasteri: stabilan URL za OpenLayers (EPSG:32634, 1.1.1, tiled, URL-encode) --
const addOrUpdateWmsLayers = (mapObj, visState, opacityState = {}) => {
  if (!mapObj) return;

  WMS_LAYERS.forEach(({ id, wmsName }) => {
    const visible = visState[id] || false;
    const opacity = opacityState[id] ?? 1.0;
    


    // Razmaci/kvačice u nazivima -> uvek enkodovati
    // Ne enkodujemo ovde jer OpenLayers to radi automatski
    const encodedName = wmsName;

    // Najstabilniji setup za WMS u OpenLayers:
    // - version=1.1.1  (parametar je SRS, ne CRS)
    // - srs=EPSG:32634  (odgovara {bbox-epsg-32634})
    // - tiled=true     (GeoServer isporučuje prave pločice, bez "praznih" upita)
    // - transparent=true + PNG
    // Pedološka – konzervativan GetMap (bez tiled/styles/SLD) da izbegnemo SVG/exception odgovore
    const wmsParams = (id === 'pedoloska')
      ? {
          'LAYERS': encodedName,
          'VERSION': '1.1.1',
          'SRS': 'EPSG:32634',
          'FORMAT': 'image/png',
          'TRANSPARENT': true
        }
      : {
          'LAYERS': encodedName,
          'STYLES': '',
          'VERSION': '1.1.1',
          'SRS': 'EPSG:32634',
          'FORMAT': 'image/png',
          'TRANSPARENT': true,
          'TILED': true
        };

    // Check if layer already exists
    const existingLayer = mapObj.getLayers().getArray().find(layer => layer.get('id') === id);
    
    if (!existingLayer) {
      try {
        const wmsSource = new ImageWMS({
          url: GEOSERVER_URL + '/wms',
          params: wmsParams,
          serverType: 'geoserver',
          crossOrigin: 'anonymous',
          projection: 'EPSG:32634',
          transition: 0  // Bez fade efekta za brže učitavanje
        });
        
        // Add error handling for WMS source
        wmsSource.on('imageloaderror', (event) => {
          console.error(`WMS image load error for layer ${id}:`, event);
        });
        
        const wmsLayer = new ImageLayer({
          source: wmsSource,
          visible: visible,
          opacity: opacity
        });
        
        wmsLayer.set('id', id);
        mapObj.addLayer(wmsLayer);
      } catch (error) {
        console.error(`Error creating WMS layer ${id}:`, error);
      }
    } else {
      try {
        existingLayer.setVisible(visible);
        existingLayer.setOpacity(opacity);
      } catch (error) {
        console.error(`Error updating WMS layer ${id}:`, error);
      }
    }
  });
  
  // Apply correct drawing order after processing all WMS layers
  // This ensures layers appear in correct z-index immediately when turned on
  applyLayerDrawingOrder(mapObj);
};

  // Fixed layer order based on user specification - highest z-index at top of list
  // This order determines the visual stacking of layers on the map
  // First layer in list = highest z-index = appears on top
  // Last layer in list = lowest z-index = appears at bottom
  const FIXED_LAYER_ORDER = [
    'ko_vojvodina',         // KO Vojvodina
    'ko_parcele',           // Parcele
    'pedoloska',            // Pedološka
    'pogodnost_zem_navod',  // Pogodnost zemljišta za navodnjavanje
    'dren_spos_zem',        // Drenažne sposobnosti zemljišta
    'wrb2014',              // WRB2014
    'visnja_obl',           // Višnja obla
    'visnja',               // Višnja
    'tresnja',              // Trešnja
    'sljiva',               // Šljiva
    'orah',                 // Orah
    'ogrozd',               // Ogrozd
    'malina',               // Malina
    'leska',                // Leska
    'kruska',               // Kruška
    'jagoda',               // Jagoda
    'jabuka',               // Jabuka
    'dunja',                // Dunja
    'crv_ribizla',          // Crveni ribizla
    'crn_ribizla',          // Crni ribizla
    'breskva',              // Breskva
    'borovnica',            // Borovnica
    'badem',                // Badem
    'kukuruz',              // Kukuruz
    'suncokret',            // Suncokret
    'psenica',              // Pšenica
    'soja',                 // Soja
    'stanice',              // Stanice
    'razlika_mraznih_91_20_vs_61_90',      // Razlika srednjeg broja mraznih dana (1991-2020 - 1961-1990)
    'razlika_tropskih_91_20_vs_61_90',     // Razlika srednjeg broja tropskih dana (1991-2020 - 1961-1990)
    'razlika_padavina_91_20_vs_61_90',     // Razlika srednjih godišnjih količina padavina (1991-2020 - 1961-1990)
    'razlika_temp_91_20_vs_61_90',         // Razlika srednjih godišnjih temperatura (1991-2020 - 1961-1990)
    'padavine_61_90',       // Srednja godišnja količina padavina (1961-1990)
    'padavine_91_20',       // Srednja godišnja količina padavina (1991-2020)
    'temp_61_90',           // Srednja godišnja temperatura (1961-1990)
    'temp_91_20',           // Srednja godišnja temperatura (1991-2020)
    'mrazni_61_90',         // Srednji broj mraznih dana (1961-1990)
    'mrazni_91_20',         // Srednji broj mraznih dana (1991-2020)
    'tropski_61_90',        // Srednji broj tropskih dana (1961-1990)
    'tropski_91_20',        // Srednji broj tropskih dana (1991-2020)
    'razlika_padavina_veg_91_20_vs_61_90', // Razlika srednja količina padavina u vegetacionom periodu april–septembar (1991-2020 - 1961-1990)
    'razlika_temp_veg_91_20_vs_61_90',     // Razlika srednja temperatura vazduha u vegetacionom periodu april–septembar (1991-2020 - 1961-1990)
    'padavine_veg_61_90',   // Srednja količina padavina u vegetacionom periodu april–septembar (1961-1990)
    'padavine_veg_91_20',   // Srednja količina padavina u vegetacionom periodu april–septembar (1991-2020)
    'temp_veg_61_90',       // Srednja temperatura vazduha u vegetacionom periodu april–septembar (1961-1990)
    'temp_veg_91_20',       // Srednja temperatura vazduha u vegetacionom periodu april–septembar (1991-2020)
    'dni_gt10_veg_61_90',   // Srednji broj dana sa količinom padavina većom od 10 mm u vegetacionom periodu april–septembar (1961-1990)
    'dni_gt10_veg_91_20',   // Srednji broj dana sa količinom padavina većom od 10 mm u vegetacionom periodu april–septembar (1991-2020)
    'dren',                 // Drenaža
    'iwqi_dtd',             // IWQI-DTD klasifikacija
    'ugrozenost',           // Ugroženost
    'ndre1',                // NDRE1
    'ndre2',                // NDRE2
    'ndvi1',                // NDVI1
    'ndvi2',                // NDVI2
    'of1',                  // OF1
    'of2'                   // OF2
  ];

  // Apply visual stacking (z-index) in map to reflect layer order
  const applyLayerDrawingOrder = useCallback((mapObj) => {
    if (!mapObj) return;
    
    try {
      // Build ordered list of layer IDs — index 0 = top of menu = drawn on top (highest zIndex)
      let orderedLayerIds;
      if (useFixedLayerOrder) {
        orderedLayerIds = [...FIXED_LAYER_ORDER];
      } else {
        orderedLayerIds = [];
        groupOrder.forEach(groupId => {
          const group = LAYER_GROUPS.find(g => g.id === groupId);
          if (!group) return;
          if (group.layers) {
            const layerIds = groupLayersOrder[groupId] || group.layers;
            layerIds.forEach(id => orderedLayerIds.push(id));
          } else if (group.subgroups) {
            group.subgroups.forEach(subgroup => {
              const subgroupKey = `${groupId}.${subgroup.id}`;
              const layerIds = groupLayersOrder[subgroupKey] || subgroup.layers;
              layerIds.forEach(id => orderedLayerIds.push(id));
            });
          }
        });
      }

      // Deduplicate
      const uniqueIds = [...new Set(orderedLayerIds)];
      const total = uniqueIds.length;

      // Use setZIndex — the correct OL API for render order.
      // Base layers stay at zIndex 0.
      // WMS layers: index 0 (top of menu) → highest zIndex → drawn on top.
      mapObj.getLayers().getArray().forEach(layer => {
        // Measurement and click-marker layers must always stay on top of all other layers
        const layerName = layer.get('name');
        if (layerName && (layerName.startsWith('measurement-') || layerName === 'click-marker')) return;

        const id = layer.get('id');
        if (!id || id.startsWith('base-')) {
          layer.setZIndex(0);
          return;
        }
        const pos = uniqueIds.indexOf(id);
        if (pos !== -1) {
          // index 0 = top of list = drawn on top = highest zIndex
          layer.setZIndex(total - pos + 10);
        }
        // layers not in the list (markers, selections) keep their own zIndex
      });

    } catch (error) {
      console.warn('Failed to apply layer drawing order:', error);
    }
  }, [useFixedLayerOrder, groupOrder, groupLayersOrder, reorderTrigger]);

  // Debounced function to apply layer drawing order
  const applyLayerOrderDebounced = useCallback((mapObj, delay = 50) => {
    if (layerOrderTimeoutRef.current) {
      clearTimeout(layerOrderTimeoutRef.current);
    }
    
    layerOrderTimeoutRef.current = setTimeout(() => {
      if (mapObj) {
        applyLayerDrawingOrder(mapObj);
      }
    }, delay);
  }, [applyLayerDrawingOrder]);

  // Functions for moving groups and layers by one position
  const moveGroupUp = (groupId) => {
    // Automatically switch to dynamic mode when a group is moved
    setUseFixedLayerOrder(false);
    
    setGroupOrder(prev => {
      const currentIndex = prev.indexOf(groupId);
      if (currentIndex <= 0) return prev; // Already at top or not found
      
      const newOrder = [...prev];
      // Swap with previous item
      [newOrder[currentIndex - 1], newOrder[currentIndex]] = [newOrder[currentIndex], newOrder[currentIndex - 1]];
      
      // Trigger reorder effect
      setReorderTrigger(prev => prev + 1);
      
      return newOrder;
    });
  };

  const moveGroupDown = (groupId) => {
    // Automatically switch to dynamic mode when a group is moved
    setUseFixedLayerOrder(false);
    
    setGroupOrder(prev => {
      const currentIndex = prev.indexOf(groupId);
      if (currentIndex === -1 || currentIndex >= prev.length - 1) return prev; // Not found or already at bottom
      
      const newOrder = [...prev];
      // Swap with next item
      [newOrder[currentIndex], newOrder[currentIndex + 1]] = [newOrder[currentIndex + 1], newOrder[currentIndex]];
      
      // Trigger reorder effect
      setReorderTrigger(prev => prev + 1);
      
      return newOrder;
    });
  };

  const moveLayerUp = (groupId, layerId) => {
    // Automatically switch to dynamic mode when a layer is moved
    setUseFixedLayerOrder(false);
    
    setGroupLayersOrder(prev => {
      const currentLayers = [...(prev[groupId] || [])];
      const currentIndex = currentLayers.indexOf(layerId);
      if (currentIndex <= 0) return prev; // Already at top or not found
      
      // Swap with previous item
      [currentLayers[currentIndex - 1], currentLayers[currentIndex]] = [currentLayers[currentIndex], currentLayers[currentIndex - 1]];
      
      const newOrder = { ...prev, [groupId]: currentLayers };
      
      // Trigger reorder effect
      setReorderTrigger(prev => prev + 1);
      
      return newOrder;
    });
  };

  const moveLayerDown = (groupId, layerId) => {
    // Automatically switch to dynamic mode when a layer is moved
    setUseFixedLayerOrder(false);
    
    setGroupLayersOrder(prev => {
      const currentLayers = [...(prev[groupId] || [])];
      const currentIndex = currentLayers.indexOf(layerId);
      if (currentIndex === -1 || currentIndex >= currentLayers.length - 1) return prev; // Not found or already at bottom
      
      // Swap with next item
      [currentLayers[currentIndex], currentLayers[currentIndex + 1]] = [currentLayers[currentIndex + 1], currentLayers[currentIndex]];
      
      const newOrder = { ...prev, [groupId]: currentLayers };
      
      // Trigger reorder effect
      setReorderTrigger(prev => prev + 1);
      
      return newOrder;
    });
  };

  // Keep map drawing order (z-index) in sync with current menu order by default and after any user reorder
  useEffect(() => {
    // Only apply if using fixed order or if this is the initial setup
    if (!useFixedLayerOrder) return;
    
    // Use a shorter delay for better responsiveness
    const timeoutId = setTimeout(() => {
      if (mapA.current) {
        applyLayerDrawingOrder(mapA.current);
      }
      if (mapB.current) {
        applyLayerDrawingOrder(mapB.current);
      }
    }, 50);
    
    return () => clearTimeout(timeoutId);
  }, [groupOrder, groupLayersOrder, useFixedLayerOrder]);

  // Additional effect to handle arrow button reordering immediately
  useEffect(() => {
    if (reorderTrigger > 0) {
      // Apply layer drawing order immediately when arrow buttons are used
      if (mapA.current) {
        applyLayerDrawingOrder(mapA.current);
      }
      if (mapB.current) {
        applyLayerDrawingOrder(mapB.current);
      }
    }
  }, [groupOrder, groupLayersOrder, reorderTrigger, useFixedLayerOrder, applyLayerDrawingOrder]);

  // Keep legend order in sync with menu order
  useEffect(() => {
    // Force re-render of legend when group order changes
    // The getSortedLegendEntries function will handle the sorting
    if (legendOpen && legendEntries.length > 0) {
      // This will trigger a re-render of the legend with the new order
      setLegendEntries(prev => [...prev]);
    }
  }, [groupOrder, groupLayersOrder, reorderTrigger]);



  // Sa deljenim view-om, sinhronizacija je automatska
  // Ova funkcija se više ne koristi jer view objekat se deli između mapa
  const syncTo = useCallback((src, dst) => {
    // Automatska sinhronizacija kroz deljeni view
    // Nema potrebe za dodatnim kodom
  }, []);

  /* ---------- Inicijalizacija Mape A ---------- */
  useEffect(() => {
    // Inicijalni fit na granice Srbije bez animacije
    const { googleLayer, osmLayer } = createBaseLayers();
    
    // View za mapu A - dinamički fit na Srbiju
    const viewA = new View({
      projection: 'EPSG:32634',
      minZoom: 4,
      maxZoom: 21,
      constrainResolution: false,
      smoothExtentConstraint: true
    });
    
    mapA.current = new Map({
      target: mapContainerA.current,
      layers: [googleLayer, osmLayer],
      view: viewA,
      controls: [],
      interactions: defaultInteractions(),
      loadTilesWhileAnimating: false,
      loadTilesWhileInteracting: false
    });

    // Dinamički fit na celu Srbiju - prilagođava se veličini ekrana
    const containerSize = [
      mapContainerA.current.clientWidth || window.innerWidth,
      mapContainerA.current.clientHeight || window.innerHeight
    ];
    viewA.fit(SERBIA_EXTENT_32634, {
      size: containerSize,
      padding: [20, 20, 60, 20]
    });

    // Add WMS layers and always-on layer
    addOrUpdateWmsLayers(mapA.current, visibilityA, opacityA);
    addAlwaysOnLayer(mapA.current);

    // Status bar listeners (A)
    mapA.current.on('pointermove', (e) => {
      try {
        const coord = transform(e.coordinate, 'EPSG:32634', 'EPSG:4326');
        const [ux, uy] = proj4('EPSG:4326', 'EPSG:32634', coord);
        setMouseUtm({ x: Math.round(ux), y: Math.round(uy) });
      } catch (_) {}
    });

    const updateScaleA = () => computeScaleForMap(mapA.current);
    mapA.current.on('moveend', updateScaleA);
    mapA.current.on('change:resolution', updateScaleA);

    return () => {
      if (mapA.current) mapA.current.setTarget(undefined);
      if (mapB.current) mapB.current.setTarget(undefined);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addPointToMap]);

  /* ---------- Dodavanje/uklanjanje event listener-a za measurement tools ---------- */
  useEffect(() => {
    const handleMapClickA = (e) => {
      if (selectedMeasurementTool === 'point') {
        const coord = transform(e.coordinate, 'EPSG:32634', 'EPSG:4326');
        addPointToMap(coord[0], coord[1]);
      } else if (selectedMeasurementTool === 'line') {
        const coord = transform(e.coordinate, 'EPSG:32634', 'EPSG:4326');
        addPointToLine(coord[0], coord[1]);
      } else if (selectedMeasurementTool === 'polygon') {
        const coord = transform(e.coordinate, 'EPSG:32634', 'EPSG:4326');
        addPointToPolygon(coord[0], coord[1]);
      } else {
        // GetFeatureInfo when no measurement tool is selected
        performGetFeatureInfo(e.coordinate, mapA.current);
      }
    };

    const handleMapClickB = (e) => {
      if (selectedMeasurementTool === 'point') {
        const coord = transform(e.coordinate, 'EPSG:32634', 'EPSG:4326');
        addPointToMap(coord[0], coord[1]);
      } else if (selectedMeasurementTool === 'line') {
        const coord = transform(e.coordinate, 'EPSG:32634', 'EPSG:4326');
        addPointToLine(coord[0], coord[1]);
      } else if (selectedMeasurementTool === 'polygon') {
        const coord = transform(e.coordinate, 'EPSG:32634', 'EPSG:4326');
        addPointToPolygon(coord[0], coord[1]);
      } else {
        // GetFeatureInfo when no measurement tool is selected
        performGetFeatureInfo(e.coordinate, mapB.current);
      }
    };

    const handleMapContextMenuA = (e) => {
      if (selectedMeasurementTool === 'line') {
        e.preventDefault();
        finishCurrentLine();
      } else if (selectedMeasurementTool === 'polygon') {
        e.preventDefault();
        finishCurrentPolygon();
      }
    };

    const handleMapContextMenuB = (e) => {
      if (selectedMeasurementTool === 'line') {
        e.preventDefault();
        finishCurrentLine();
      } else if (selectedMeasurementTool === 'polygon') {
        e.preventDefault();
        finishCurrentPolygon();
      }
    };

    // Dodaj event listener-e ako su mape kreirane
    if (mapA.current) {
      mapA.current.on('singleclick', handleMapClickA);
      mapA.current.getViewport().addEventListener('contextmenu', handleMapContextMenuA);
    }
    if (mapB.current) {
      mapB.current.on('singleclick', handleMapClickB);
      mapB.current.getViewport().addEventListener('contextmenu', handleMapContextMenuB);
    }

    // Cleanup funkcija koja uklanja event listener-e
    return () => {
      if (mapA.current) {
        mapA.current.un('singleclick', handleMapClickA);
        mapA.current.getViewport().removeEventListener('contextmenu', handleMapContextMenuA);
      }
      if (mapB.current) {
        mapB.current.un('singleclick', handleMapClickB);
        mapB.current.getViewport().removeEventListener('contextmenu', handleMapContextMenuB);
      }
    };
  }, [selectedMeasurementTool, addPointToMap, addPointToLine, finishCurrentLine, addPointToPolygon, finishCurrentPolygon, performGetFeatureInfo, addClickMarker, removeClickMarker]);

  /* ---------- Kreiranje/uklanjanje Mape B po viewMode ---------- */
  useEffect(() => {
    const needsB = viewMode === 'dual' || viewMode === 'swipe' || viewMode === 'lens';

    if (needsB) {
      if (!mapB.current && mapContainerB.current) {
        const viewA = mapA.current?.getView();
        const center = viewA?.getCenter();

        const { googleLayer, osmLayer } = createBaseLayers();
        const [cxB, cyB] = getBoundsCenter(SRBIJA_BOUNDS_WGS84);
        
        const centerCoord = center 
          ? center // OpenLayers center is already in EPSG:32634
          : transform([cxB, cyB], 'EPSG:4326', 'EPSG:32634');
        
        // View za mapu B - dinamički fit
        const viewB = new View({
          center: centerCoord,
          projection: 'EPSG:32634',
          minZoom: 4,
          maxZoom: 19,
          constrainResolution: false,
          smoothExtentConstraint: true
        });
        
        mapB.current = new Map({
          target: mapContainerB.current,
          layers: [googleLayer, osmLayer],
          view: viewB,
          controls: [],
          interactions: defaultInteractions(),
          loadTilesWhileAnimating: false,
          loadTilesWhileInteracting: false
        });

        // Dinamički fit na celu Srbiju
        const containerSizeB = [
          mapContainerB.current.clientWidth || window.innerWidth / 2,
          mapContainerB.current.clientHeight || window.innerHeight
        ];
        viewB.fit(SERBIA_EXTENT_32634, {
          size: containerSizeB,
          padding: [20, 20, 60, 20]
        });

        // Add WMS layers and always-on layer
        applyBaseVisibility(mapB.current, baseLayerB);
        addOrUpdateWmsLayers(mapB.current, visibilityB, opacityB);
        addAlwaysOnLayer(mapB.current);

        // Status bar listeners (B)
        mapB.current.on('pointermove', (e) => {
          try {
            const coord = transform(e.coordinate, 'EPSG:32634', 'EPSG:4326');
            const [ux, uy] = proj4('EPSG:4326', 'EPSG:32634', coord);
            setMouseUtm({ x: Math.round(ux), y: Math.round(uy) });
          } catch (_) {}
        });

        const updateScaleB = () => computeScaleForMap(mapB.current);
        mapB.current.on('moveend', updateScaleB);
        mapB.current.on('change:resolution', updateScaleB);
        setTimeout(updateScaleB, 0);

        // Clear measurement cache when new map is created
        clearMeasurementCache();

        requestAnimationFrame(() => {
          if (mapA.current) {
            try {
              mapA.current.updateSize();
            } catch (e) {
              console.warn('Failed to update size mapA:', e);
            }
          }
          if (mapB.current) {
            try {
              mapB.current.updateSize();
            } catch (e) {
              console.warn('Failed to update size mapB:', e);
            }
          }
        });
      } else {
        requestAnimationFrame(() => {
          if (mapA.current) {
            try {
              mapA.current.updateSize();
            } catch (e) {
              console.warn('Failed to update size mapA:', e);
            }
          }
          if (mapB.current) {
            try {
              mapB.current.updateSize();
            } catch (e) {
              console.warn('Failed to update size mapB:', e);
            }
          }
        });
      }
    } else {
      if (activeMap !== 'A') setActiveMap('A');
      if (mapB.current) {
        mapB.current.setTarget(undefined);
        mapB.current = null;
        // Clear measurement cache when map is destroyed
        clearMeasurementCache();
      }
      requestAnimationFrame(() => {
        if (mapA.current) {
          try {
            mapA.current.updateSize();
          } catch (e) {
            console.warn('Failed to update size mapA:', e);
          }
        }
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewMode, activeMap]);

  /* ---------- Resize pri promeni rasporeda (dual) ---------- */
  useEffect(() => {
    if (viewMode !== 'dual') return;
    requestAnimationFrame(() => {
      if (mapA.current) {
        mapA.current.updateSize();
      }
      if (mapB.current) {
        mapB.current.updateSize();
      }
    });
  }, [layout, viewMode]);

  /* ---------- Promena podloge ---------- */
  useEffect(() => { applyBaseVisibility(mapA.current, baseLayerA); }, [baseLayerA]);
  useEffect(() => { applyBaseVisibility(mapB.current, baseLayerB); }, [baseLayerB]);

  /* ---------- WMS Layer visibility sync ---------- */
  const layerUpdateTimeoutRef = useRef(null);
  
  useEffect(() => {
    if (!mapA.current) return;
    
    // Debounce layer updates to avoid excessive re-rendering
    if (layerUpdateTimeoutRef.current) {
      clearTimeout(layerUpdateTimeoutRef.current);
    }
    
    layerUpdateTimeoutRef.current = setTimeout(() => {
      // Ensure WMS layers exist and update their visibility/opacity
      addOrUpdateWmsLayers(mapA.current, visibilityA, opacityA);
      
      // Apply layer ordering after layers are created/updated
      setTimeout(() => {
        if (mapA.current) {
          applyLayerDrawingOrder(mapA.current);
        }
      }, 25);
    }, 50); // Smanjen delay za brži odziv
  }, [visibilityA, opacityA, applyLayerDrawingOrder]);

  useEffect(() => {
    if (!mapB.current) return;
    
    // Debounce layer updates to avoid excessive re-rendering
    if (layerUpdateTimeoutRef.current) {
      clearTimeout(layerUpdateTimeoutRef.current);
    }
    
    layerUpdateTimeoutRef.current = setTimeout(() => {
      // Ensure WMS layers exist and update their visibility/opacity
      addOrUpdateWmsLayers(mapB.current, visibilityB, opacityB);
      
      // Apply layer ordering after layers are created/updated
      setTimeout(() => {
        if (mapB.current) {
          applyLayerDrawingOrder(mapB.current);
        }
      }, 25);
    }, 50); // Smanjen delay za brži odziv
  }, [visibilityB, opacityB, applyLayerDrawingOrder]);

  /* ---------- Dynamic layer order updates ---------- */
  useEffect(() => {
    // Only apply dynamic ordering when not using fixed order
    if (useFixedLayerOrder) return;
    
    // Apply layer order changes to both maps
    if (mapA.current) {
      applyLayerDrawingOrder(mapA.current);
    }
    if (mapB.current) {
      applyLayerDrawingOrder(mapB.current);
    }
  }, [groupOrder, groupLayersOrder, useFixedLayerOrder, applyLayerDrawingOrder, reorderTrigger]);

  /* ---------- Layer order mode change ---------- */
  useEffect(() => {
    // Apply layer order when switching between fixed and dynamic modes
    if (mapA.current) {
      applyLayerDrawingOrder(mapA.current);
    }
    if (mapB.current) {
      applyLayerDrawingOrder(mapB.current);
    }
  }, [useFixedLayerOrder, applyLayerDrawingOrder]);

  /* ---------- Sinhronizacija kretanja za swipe i lens ---------- */
  useEffect(() => {
    if (!mapA.current || !mapB.current) return;

    if (viewMode === 'swipe' || viewMode === 'lens') {
      // Za swipe i lens modove, koristi deljeni view za sinhronizaciju
      const viewA = mapA.current.getView();
      const viewB = mapB.current.getView();
      
      if (viewA !== viewB) {
        mapB.current.setView(viewA);
      }
    } else if (viewMode === 'dual') {
      // Za dual mod, osiguraj da svaka mapa ima svoj vlastiti view
      const viewA = mapA.current.getView();
      const viewB = mapB.current.getView();
      
      if (viewA === viewB) {
        // Kreiraj novi nezavisan view za mapu B
        const newViewB = new View({
          center: viewA.getCenter(),
          zoom: viewA.getZoom(),
          projection: 'EPSG:32634',
          minZoom: 4,
          maxZoom: 21,
          constrainResolution: false
        });
        mapB.current.setView(newViewB);
      }
    }

    return () => {
      // Cleanup nije potreban
    };
  }, [viewMode]);

  /* ---------- SWIPE: drag handle ---------- */
  useEffect(() => {
    let animationFrame = null;
    let lastMoveTime = 0;
    const moveThrottle = 4; // ~240fps za maksimalno glatak prikaz
    
    const onMove = (e) => {
      if (!draggingRef.current) return;
      
      const now = Date.now();
      if (now - lastMoveTime < moveThrottle) return;
      
      // Cancel previous animation frame
      if (animationFrame) {
        cancelAnimationFrame(animationFrame);
      }
      
      // Use requestAnimationFrame for smooth updates
      animationFrame = requestAnimationFrame(() => {
        const wrapper = document.getElementById('swipe-wrapper');
        if (!wrapper) return;
        const rect = wrapper.getBoundingClientRect();

        if (sliderOrientation === 'vertical') {
          const x = (e.touches ? e.touches[0].clientX : e.clientX) - rect.left;
          const p = Math.min(1, Math.max(0, x / rect.width));
          setSliderPos(p);
        } else {
          const y = (e.touches ? e.touches[0].clientY : e.clientY) - rect.top;
          const p = Math.min(1, Math.max(0, y / rect.height));
          setSliderPos(p);
        }
        lastMoveTime = now;
      });
    };
    
    const stop = () => { 
      draggingRef.current = false;
      if (animationFrame) {
        cancelAnimationFrame(animationFrame);
        animationFrame = null;
      }
    };

    window.addEventListener('mousemove', onMove, { passive: true });
    window.addEventListener('touchmove', onMove, { passive: false });
    window.addEventListener('mouseup', stop);
    window.addEventListener('touchend', stop);
    
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('touchmove', onMove);
      window.removeEventListener('mouseup', stop);
      window.removeEventListener('touchend', stop);
      if (animationFrame) {
        cancelAnimationFrame(animationFrame);
      }
    };
  }, [sliderOrientation]);

  const startDrag = (e) => {
    e.preventDefault();
    draggingRef.current = true;
  };

  /* ---------- LENS: pomeranje lupe ---------- */
  useEffect(() => {
    if (viewMode !== 'lens') return;
    const el = lensWrapperRef.current;
    if (!el) return;

    let lensAnimationFrame = null;
    let lastLensMoveTime = 0;
    const lensMoveThrottle = 4; // ~240fps za maksimalno glatak prikaz

    const move = (clientX, clientY) => {
      const now = Date.now();
      if (now - lastLensMoveTime < lensMoveThrottle) return;
      
      if (lensAnimationFrame) {
        cancelAnimationFrame(lensAnimationFrame);
      }
      
      lensAnimationFrame = requestAnimationFrame(() => {
        const rect = el.getBoundingClientRect();
        const x = Math.min(1, Math.max(0, (clientX - rect.left) / rect.width));
        const y = Math.min(1, Math.max(0, (clientY - rect.top) / rect.height));
        setLensPos({ x, y });
        lastLensMoveTime = now;
      });
    };

    const onMouseMove = (e) => move(e.clientX, e.clientY);
    const onTouchMove = (e) => {
      if (e.touches && e.touches[0]) {
        move(e.touches[0].clientX, e.touches[0].clientY);
      }
    };

    el.addEventListener('mousemove', onMouseMove, { passive: true });
    el.addEventListener('touchmove', onTouchMove, { passive: true });

    return () => {
      el.removeEventListener('mousemove', onMouseMove);
      el.removeEventListener('touchmove', onTouchMove);
      if (lensAnimationFrame) {
        cancelAnimationFrame(lensAnimationFrame);
      }
    };
  }, [viewMode]);

  /* ========== EKSKLUZIVNOST SLOJEVA U DVA-MAPSKIM MODOVIMA ========== */
  const isTwoMapMode = viewMode === 'dual' || viewMode === 'swipe' || viewMode === 'lens';

  // 1) Toggling sloja – u dva-mapskom modu pali na targetu i gasi na drugoj mapi.
  const toggleLayer = useCallback((layerId) => {
    if (!isTwoMapMode) {
      setVisibilityA(prev => {
        const next = { ...prev, [layerId]: !prev[layerId] };
        
        // Special handling for ko_parcele - toggle both ko_parcele and ko_vojvodina together
        if (layerId === 'ko_parcele') {
          next['ko_vojvodina'] = next[layerId]; // Set ko_vojvodina to same state as ko_parcele
        }
        
        // Let the useEffect handle the actual layer visibility
        // Don't manipulate the layer directly here
        
        // ako je gašenje – ukloni legendu za taj sloj
        if (prev[layerId]) {
          removeLegendFor(layerId);
          // Clear GetFeatureInfo if ko_parcele layer is being turned off
          if (layerId === 'ko_parcele') {
            setClickedCoordinates(null);
            setFeatureInfo(null);
            setFruitTreeInfo(null);
            setIsFeatureInfoLoading(false);
            setIsFruitTreeExpanded(false);
            removeClickMarker();
          }
        }
        
        // ako je paljenje – zoom na sloj (osim ako je parcela i postoji flag za sprečavanje)
        if (!prev[layerId] && next[layerId]) {
          // Proveri da li je ko_parcele sloj i da li postoji flag za sprečavanje zoom-a
          if (layerId === 'ko_parcele' && window.preventParcelLayerZoom) {
            // Ne zoomuj na parcele sloj kada se uključuje zbog pretrage
          } else {
            setTimeout(() => {
              // Zoom na aktivnu mapu kada se sloj upali
              const activeMapRef = activeMap === 'A' ? mapA : mapB;
              fitToLayer(layerId, activeMapRef);
            }, 50);
          }
        }
        
        return next;
      });
      return;
    }

    const target = layerTarget[layerId] === 'B' ? 'B' : 'A';
    const isOnTarget = target === 'A' ? visibilityA[layerId] : visibilityB[layerId];

    if (!isOnTarget) {
      // Turn on layer on target map and turn off on other map
      if (target === 'A') {
        setVisibilityA(prev => {
          const next = { ...prev, [layerId]: true };
          // Special handling for ko_parcele - toggle both ko_parcele and ko_vojvodina together
          if (layerId === 'ko_parcele') {
            next['ko_vojvodina'] = true;
          }
          return next;
        });
        setVisibilityB(prev => ({ ...prev, [layerId]: false }));
        
        // Ensure layer exists on both maps before toggling
        if (mapA.current && mapB.current) {
          addOrUpdateWmsLayers(mapA.current, { ...visibilityA, [layerId]: true }, opacityA);
          addOrUpdateWmsLayers(mapB.current, { ...visibilityB, [layerId]: false }, opacityB);
        }
        
        // Zoom to layer on map A (osim ako je parcela i postoji flag za sprečavanje)
        if (layerId === 'ko_parcele' && window.preventParcelLayerZoom) {
          // Ne zoomuj na parcele sloj kada se uključuje zbog pretrage
        } else {
          setTimeout(() => {
            // Zoom na svaki sloj kada se upali
            fitToLayer(layerId, mapA);
          }, 50);
        }
      } else {
        setVisibilityB(prev => {
          const next = { ...prev, [layerId]: true };
          // Special handling for ko_parcele - toggle both ko_parcele and ko_vojvodina together
          if (layerId === 'ko_parcele') {
            next['ko_vojvodina'] = true;
          }
          return next;
        });
        setVisibilityA(prev => ({ ...prev, [layerId]: false }));
        
        // Ensure layer exists on both maps before toggling
        if (mapA.current && mapB.current) {
          addOrUpdateWmsLayers(mapA.current, { ...visibilityA, [layerId]: false }, opacityA);
          addOrUpdateWmsLayers(mapB.current, { ...visibilityB, [layerId]: true }, opacityB);
        }
        
        // Zoom to layer on map B (osim ako je parcela i postoji flag za sprečavanje)
        if (layerId === 'ko_parcele' && window.preventParcelLayerZoom) {
          // Ne zoomuj na parcele sloj kada se uključuje zbog pretrage
        } else {
          setTimeout(() => {
            // Zoom na svaki sloj kada se upali
            fitToLayer(layerId, mapB);
          }, 50);
        }
      }
    } else {
      // Turn off layer on target map
      if (target === 'A') {
        setVisibilityA(prev => {
          const next = { ...prev, [layerId]: false };
          // Special handling for ko_parcele - toggle both ko_parcele and ko_vojvodina together
          if (layerId === 'ko_parcele') {
            next['ko_vojvodina'] = false;
          }
          removeLegendFor(layerId);
          // Clear GetFeatureInfo if ko_parcele layer is being turned off
          if (layerId === 'ko_parcele') {
            setClickedCoordinates(null);
            setFeatureInfo(null);
            setFruitTreeInfo(null);
            setIsFeatureInfoLoading(false);
            setIsFruitTreeExpanded(false);
            removeClickMarker();
          }
          return next;
        });
      } else {
        setVisibilityB(prev => {
          const next = { ...prev, [layerId]: false };
          // Special handling for ko_parcele - toggle both ko_parcele and ko_vojvodina together
          if (layerId === 'ko_parcele') {
            next['ko_vojvodina'] = false;
          }
          removeLegendFor(layerId);
          // Clear GetFeatureInfo if ko_parcele layer is being turned off
          if (layerId === 'ko_parcele') {
            setClickedCoordinates(null);
            setFeatureInfo(null);
            setFruitTreeInfo(null);
            setIsFeatureInfoLoading(false);
            setIsFruitTreeExpanded(false);
            removeClickMarker();
          }
          return next;
        });
      }
    }
  }, [isTwoMapMode, layerTarget, visibilityA, visibilityB, mapA, mapB, activeMap, removeLegendFor, setClickedCoordinates, setFeatureInfo, setFruitTreeInfo, setIsFeatureInfoLoading, setIsFruitTreeExpanded, removeClickMarker, fitToLayer]);

  // 2) Promena mete (A/B) dok je sloj upaljen – preseli prikaz.
  const changeLayerTarget = (layerId, newTarget) => {
    setLayerTarget(t => ({ ...t, [layerId]: newTarget }));

    if (!isTwoMapMode) return;

    const onA = visibilityA[layerId];
    const onB = visibilityB[layerId];

    if (!onA && !onB) return;

    // Move layer to new target map and turn off on other map
    if (newTarget === 'A') {
      setVisibilityA(prev => ({ ...prev, [layerId]: true }));
      setVisibilityB(prev => ({ ...prev, [layerId]: false }));
      
      // Ensure layer exists on both maps before moving
      if (mapA.current && mapB.current) {
        addOrUpdateWmsLayers(mapA.current, { ...visibilityA, [layerId]: true }, opacityA);
        addOrUpdateWmsLayers(mapB.current, { ...visibilityB, [layerId]: false }, opacityB);
      }
      
              // Zoom to layer on map A (osim ako je parcela i postoji flag za sprečavanje)
        if (layerId === 'ko_parcele' && window.preventParcelLayerZoom) {
          // Ne zoomuj na parcele sloj kada se uključuje zbog pretrage
        } else {
          setTimeout(() => {
            // Zoom na svaki sloj kada se upali
            fitToLayer(layerId, mapA);
          }, 50);
        }
    } else {
      setVisibilityB(prev => ({ ...prev, [layerId]: true }));
      setVisibilityA(prev => ({ ...prev, [layerId]: false }));
      
      // Ensure layer exists on both maps before moving
      if (mapA.current && mapB.current) {
        addOrUpdateWmsLayers(mapA.current, { ...visibilityA, [layerId]: false }, opacityA);
        addOrUpdateWmsLayers(mapB.current, { ...visibilityB, [layerId]: true }, opacityB);
      }
      
      // Zoom to layer on map B (osim ako je parcela i postoji flag za sprečavanje)
        if (layerId === 'ko_parcele' && window.preventParcelLayerZoom) {
          // Ne zoomuj na parcele sloj kada se uključuje zbog pretrage
        } else {
          setTimeout(() => {
            // Zoom na svaki sloj kada se upali
            fitToLayer(layerId, mapB);
          }, 50);
        }
    }
  };

  // 3) Kad uđemo u mod sa dve mape, garantuj ekskluzivnost i inicijalizuj slojeve na obe mape.
  useEffect(() => {
    if (!isTwoMapMode) return;
    
    // Ensure all layers exist on both maps when entering dual map mode
    if (mapA.current && mapB.current) {
      // Force creation of all layers on both maps
      addOrUpdateWmsLayers(mapA.current, visibilityA, opacityA);
      addOrUpdateWmsLayers(mapB.current, visibilityB, opacityB);
    }
    
    // Ensure exclusivity - only one map can have a layer visible
    WMS_LAYERS.forEach(({ id }) => {
      if (visibilityA[id] && visibilityB[id]) {
        const keep = layerTarget[id] === 'B' ? 'B' : 'A';
        if (keep === 'A') {
          setVisibilityB(prev => ({ ...prev, [id]: false }));
        } else {
          setVisibilityA(prev => ({ ...prev, [id]: false }));
        }
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewMode, visibilityA, visibilityB, layerTarget]);

  // Initialize all layers on both maps when entering dual map mode
  useEffect(() => {
    if (!isTwoMapMode || !mapA.current || !mapB.current) return;
    
    // Force creation of all WMS layers on both maps
    // This ensures all layers exist and can be quickly toggled between maps
    WMS_LAYERS.forEach(({ id }) => {
      // Create layer on map A if it doesn't exist
      const layerA = mapA.current.getLayers().getArray().find(layer => layer.get('id') === id);
      if (!layerA) {
        addOrUpdateWmsLayers(mapA.current, visibilityA, opacityA);
      }
      
      // Create layer on map B if it doesn't exist
      const layerB = mapB.current.getLayers().getArray().find(layer => layer.get('id') === id);
      if (!layerB) {
        addOrUpdateWmsLayers(mapB.current, visibilityB, opacityB);
      }
    });
    
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewMode, isTwoMapMode]);

  /* ---------- Izbor podloge za aktivnu mapu ---------- */
  const setBaseLayerForActive = (val) => {
    if (viewMode === 'single' || activeMap === 'A') setBaseLayerA(val);
    else setBaseLayerB(val);
  };

  /* ---------- Opacity change handler ---------- */
  const changeLayerOpacity = (layerId, newOpacity) => {
    if (!isTwoMapMode) {
      // Single map mode - update only map A
      setOpacityA(prev => ({ ...prev, [layerId]: newOpacity }));
      const layer = mapA.current?.getLayers().getArray().find(l => l.get('id') === layerId);
      if (layer) {
        layer.setOpacity(newOpacity);
      }
      return;
    }

    // Two map mode - update the target map
    const target = layerTarget[layerId] === 'B' ? 'B' : 'A';
    if (target === 'A') {
      setOpacityA(prev => ({ ...prev, [layerId]: newOpacity }));
      const layer = mapA.current?.getLayers().getArray().find(l => l.get('id') === layerId);
      if (layer) {
        layer.setOpacity(newOpacity);
      }
    } else {
      setOpacityB(prev => ({ ...prev, [layerId]: newOpacity }));
      const layer = mapB.current?.getLayers().getArray().find(l => l.get('id') === layerId);
      if (layer) {
        layer.setOpacity(newOpacity);
      }
    }
  };

  /* ---------- Get current opacity for layer ---------- */
  const getLayerOpacity = (layerId) => {
    if (!isTwoMapMode) return opacityA[layerId] ?? 1.0;
    return layerTarget[layerId] === 'B' ? (opacityB[layerId] ?? 1.0) : (opacityA[layerId] ?? 1.0);
  };

  /* ---------- Menu handlers (hamburger pametan) ---------- */
  const handleHamburgerClick = () => {
    if (isSecondaryMenuOpen || isPrimaryMenuOpen) {
      if (isSecondaryMenuOpen) setIsSecondaryMenuOpen(false);
      if (isPrimaryMenuOpen)   setIsPrimaryMenuOpen(false);
      setSelectedPrimaryItem(null);
      deactivateMeasurementTool();
      return;
    }
    setIsPrimaryMenuOpen(true);
  };

  const handlePrimaryMenuItemClick = (item) => {
    setSelectedPrimaryItem(item);
    setIsSecondaryMenuOpen(true);
  };

  /* ---------- Group toggle handler ---------- */
  const toggleGroup = (groupId) => {
    setOpenGroups(prev => ({
      ...prev,
      [groupId]: !prev[groupId]
    }));
  };

  const toggleSubgroup = (groupId, subgroupId) => {
    const key = `${groupId}.${subgroupId}`;
    setOpenSubgroups(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  /* ---------- Search functionality ---------- */
  
  /*
   * PARCEL SELECTION API
   * 
   * The following functions are exposed globally on the window object for external use:
   * 
   * 1. window.selectParcelFromBackend(parcelData)
   *    - Main function for selecting parcela from backend response
   *    - parcelData: { brparcele: string, kat_opst_1: string, ... }
   *    - Returns: Promise<boolean> - success/failure
   *    - Usage: window.selectParcelFromBackend({ brparcele: "123", kat_opst_1: "Novi Sad" })
   * 
   * 2. window.selectParcelByData(brparcele, katOpst1)
   *    - Simplified function for selecting parcela by brparcele and katOpst1
   *    - Returns: Promise<boolean> - success/failure
   *    - Usage: window.selectParcelByData("123", "Novi Sad")
   * 
   * 3. window.clearSelectedParcel()
   *    - Clears currently selected parcela from map
   *    - Usage: window.clearSelectedParcel()
   * 
   * Features:
   * - Automatically enables ko_parcele layer if not visible
   * - Highlights selected parcela with blue color (#0066ff)
   * - Zooms to parcela geometry bounds
   * - Updates search input with selected parcela info
   * - Handles PostGIS geometry conversion
   * - Provides fallback dummy geometry for testing
   */
  // Function to sort parcels by number (natural sorting)
  const sortParcelsByNumber = (parcels) => {
    return parcels.sort((a, b) => {
      const aNum = a.brparcele || '';
      const bNum = b.brparcele || '';
      
      // Split by non-numeric characters and compare parts
      const aParts = aNum.split(/(\d+)/);
      const bParts = bNum.split(/(\d+)/);
      
      for (let i = 0; i < Math.max(aParts.length, bParts.length); i++) {
        const aPart = aParts[i] || '';
        const bPart = bParts[i] || '';
        
        // If both parts are numeric, compare as numbers
        if (/^\d+$/.test(aPart) && /^\d+$/.test(bPart)) {
          const aNum = parseInt(aPart, 10);
          const bNum = parseInt(bPart, 10);
          if (aNum !== bNum) {
            return aNum - bNum;
          }
        } else {
          // Compare as strings (case-sensitive)
          const comparison = aPart.localeCompare(bPart);
          if (comparison !== 0) {
            return comparison;
          }
        }
      }
      
      // If numbers are the same, sort by katastarska opština
      return (a.kat_opst_1 || '').localeCompare(b.kat_opst_1 || '');
    });
  };

  // Real-time search with OpenLayers-like implementation
  const searchParcels = async (query) => {
    if (!query || query.trim().length < 2) {
      setSearchResults([]);
      setShowSearchResults(false);
      return;
    }

    setIsSearching(true);
    try {
      // Parse query similar to OpenLayers version
      const trimmed = query.trim();
      const match = trimmed.match(/^([\d/]+)(.*)$/);

      let brParceleCandidate = '';
      let koCandidate = '';

      if (match) {
        brParceleCandidate = match[1];
        koCandidate = match[2].trim();
      } else {
        koCandidate = trimmed;
      }

      // Build URL with parameters like in OpenLayers version
      let url = `${process.env.REACT_APP_BACKEND_URL}/api/koparcele?`;
      const params = new URLSearchParams();
      
      if (brParceleCandidate) {
        params.append('brparcele', brParceleCandidate);
      }
      if (koCandidate) {
        params.append('katOpst1', koCandidate);
      }
      
      url += params.toString();

      const response = await fetch(url);
      if (response.ok) {
        const result = await response.json();
        if (result.success && result.data) {
          // Sort results by parcel number
          const sortedResults = sortParcelsByNumber(result.data);
          setSearchResults(sortedResults);
          setShowSearchResults(true);
          
          // Auto-select first result if there's only one (like in OL version)
          if (result.data.length === 1) {
            setTimeout(() => {
              autoSelectParcel(result.data[0]);
              setShowSearchResults(false);
            }, 200); // Još brži odziv
          }
        } else {
          setSearchResults([]);
          setShowSearchResults(false);
        }
      } else {
        console.error('Search failed:', response.statusText);
        setSearchResults([]);
        setShowSearchResults(false);
      }
    } catch (error) {
      console.error('Search error:', error);
      setSearchResults([]);
      setShowSearchResults(false);
    } finally {
      setIsSearching(false);
    }
  };

  const handleSearchChange = (e) => {
    const query = e.target.value;
    setSearchQuery(query);
    
    // Debounce search - wait 200ms after user stops typing (brži odziv)
    clearTimeout(window.searchTimeout);
    window.searchTimeout = setTimeout(() => {
      searchParcels(query);
    }, 200);
  };

  // Real-time search effect (similar to OpenLayers useEffect with searchQuery dependency)
  useEffect(() => {
    if (!searchQuery.trim()) {
      setSearchResults([]);
      setShowSearchResults(false);
      return;
    }

    // This will be triggered by the debounced search
    // The actual search is handled by handleSearchChange debouncing
  }, [searchQuery]);

  // Download search function - similar to searchParcels but for download selection
  const searchParcelsForDownload = async (query) => {
    if (!query || query.trim().length < 2) {
      setDownloadSearchResults([]);
      setShowDownloadSearchResults(false);
      return;
    }

    setIsDownloadSearching(true);
    try {
      // Parse query similar to main search
      const trimmed = query.trim();
      const match = trimmed.match(/^([\d/]+)(.*)$/);

      let brParceleCandidate = '';
      let koCandidate = '';

      if (match) {
        brParceleCandidate = match[1];
        koCandidate = match[2].trim();
      } else {
        koCandidate = trimmed;
      }

      // Build URL with parameters
      let url = `${process.env.REACT_APP_BACKEND_URL}/api/koparcele?`;
      const params = new URLSearchParams();
      
      if (brParceleCandidate) {
        params.append('brparcele', brParceleCandidate);
      }
      if (koCandidate) {
        params.append('katOpst1', koCandidate);
      }
      
      url += params.toString();

      const response = await fetch(url);
      if (response.ok) {
        const result = await response.json();
        if (result.success && result.data) {
          // Sort results by parcel number
          const sortedResults = sortParcelsByNumber(result.data);
          setDownloadSearchResults(sortedResults);
          setShowDownloadSearchResults(true);
          
          // Auto-select first result if there's only one
          if (result.data.length === 1) {
            setTimeout(() => {
              addParcelToDownloadList(result.data[0]);
            }, 200);
          }
        } else {
          setDownloadSearchResults([]);
          setShowDownloadSearchResults(false);
        }
      } else {
        console.error('Download search failed:', response.statusText);
        setDownloadSearchResults([]);
        setShowDownloadSearchResults(false);
      }
    } catch (error) {
      console.error('Download search error:', error);
      setDownloadSearchResults([]);
      setShowDownloadSearchResults(false);
    } finally {
      setIsDownloadSearching(false);
    }
  };

  const handleDownloadSearchChange = (e) => {
    const query = e.target.value;
    setDownloadSearchQuery(query);
    
    // Debounce search - wait 200ms after user stops typing
    clearTimeout(window.downloadSearchTimeout);
    window.downloadSearchTimeout = setTimeout(() => {
      searchParcelsForDownload(query);
    }, 200);
  };

  // Function to add parcel to download list
  const addParcelToDownloadList = (parcel) => {
    // Check if parcel already exists in the list
    const exists = downloadParcelsList.some(p => 
      p.brparcele === parcel.brparcele && p.kat_opst_1 === parcel.kat_opst_1
    );
    
    if (!exists) {
      setDownloadParcelsList(prev => [...prev, parcel]);
      
      // Log geometry to console
      console.log('=== SELEKTOVANA PARCELA ZA PREUZIMANJE ===');
      console.log('Broj parcele:', parcel.brparcele);
      console.log('Katastarska opština:', parcel.kat_opst_1);
      console.log('Opština:', parcel.opstina_im || 'N/A');
      console.log('ID:', parcel.id || 'N/A');
      
      if (parcel.geometry) {
        console.log('Geometrija (raw):', parcel.geometry);
        
        // Try to parse and display geometry in a more readable format
        try {
          let geometryData = parcel.geometry;
          if (typeof geometryData === 'string') {
            geometryData = JSON.parse(geometryData);
          }
          console.log('Geometrija (parsed):', geometryData);
          
          if (geometryData.type) {
            console.log('Tip geometrije:', geometryData.type);
            if (geometryData.coordinates) {
              console.log('Koordinate:', geometryData.coordinates);
            }
          }
        } catch (error) {
          console.log('Greška pri parsiranju geometrije:', error);
        }
      } else {
        console.log('Geometrija: N/A');
      }
      
      console.log('Kompletni objekat parcele:', parcel);
      console.log('==========================================');
    }
    
    // Clear search and close dropdown
    setDownloadSearchQuery('');
    setShowDownloadSearchResults(false);
  };

  // Function to remove parcel from download list
  const removeParcelFromDownloadList = (parcelToRemove) => {
    setDownloadParcelsList(prev => 
      prev.filter(p => !(p.brparcele === parcelToRemove.brparcele && p.kat_opst_1 === parcelToRemove.kat_opst_1))
    );
  };

  // Function to clear all parcels from download list
  const clearDownloadParcelsList = () => {
    setDownloadParcelsList([]);
  };

  // Function to create ZIP file from selected parcels
  const createZipFile = async () => {
    if (downloadParcelsList.length === 0) {
      showErrorNotification('Nema parcela za kreiranje ZIP fajla');
      return;
    }

    setIsCreatingZip(true);
    setZipFilename(null);
    
    const loadingId = showLoadingNotification(`Kreiranje ZIP fajla za ${downloadParcelsList.length} parcela...`);

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/koparcele/create-zip`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json; charset=utf-8',
        },
        body: JSON.stringify({
          parcels: downloadParcelsList
        })
      });

      const result = await response.json();
      removeNotification(loadingId);

      if (result.success) {
        setZipFilename(result.zip_filename);
        showSuccessNotification(`ZIP fajl uspešno kreiran: ${result.zip_filename}`);
        console.log('ZIP kreiran:', result);
      } else {
        showErrorNotification(`Greška pri kreiranju ZIP fajla: ${result.error}`);
        console.error('Greška pri kreiranju ZIP:', result);
      }
    } catch (error) {
      removeNotification(loadingId);
      console.error('Greška pri pozivanju create-zip endpoint-a:', error);
      showErrorNotification('Greška pri kreiranju ZIP fajla');
    } finally {
      setIsCreatingZip(false);
    }
  };

  // NOVO: handler za MailVerificationDialog (mode='report')
  const handleReportConfirm = useCallback(({ send }) => {
    const loadingId = showLoadingNotification('Generišem PDF, stići će na e‑mail za par minuta...');
    send()
      .then((res) => {
        removeNotification(loadingId);
        if (res?.success) {
          showSuccessNotification('PDF je poslat na e‑mail.');
        } else {
          showErrorNotification(res?.message || 'Greška pri slanju PDF‑a.');
        }
      })
      .catch(() => {
        removeNotification(loadingId);
        showErrorNotification('Greška pri slanju PDF‑a.');
      });
  }, [showLoadingNotification, showSuccessNotification, showErrorNotification, removeNotification]);

  // Handler za MailVerificationDialog (mode='zip')
  const handleZipConfirm = useCallback(({ send }) => {
    const loadingId = showLoadingNotification('Kreiram ZIP i šaljem na e‑mail...');
    send()
      .then((res) => {
        removeNotification(loadingId);
        if (res?.success) {
          showSuccessNotification('ZIP fajl je poslat na e‑mail.');
        } else {
          showErrorNotification(res?.error || 'Greška pri slanju ZIP‑a.');
        }
      })
      .catch(() => {
        removeNotification(loadingId);
        showErrorNotification('Greška pri slanju ZIP‑a.');
      });
  }, [showLoadingNotification, showSuccessNotification, showErrorNotification, removeNotification]);

  const openMailVerificationDialog = (mode = 'zip') => {
    // mode može biti 'zip' ili 'report' — korisno za razlikovanje akcije
    try {
      setMailDialogMode(mode);
      setIsMailDialogOpen(true);
    } catch (e) {
      console.error('Failed to open mail verification dialog:', e);
    }
  };

  // Function to automatically download ZIP file when zipFilename is set
  const downloadZipFile = async (filename) => {
    if (!filename) {
      console.error('Nema imena fajla za preuzimanje');
      return;
    }

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/koparcele/download-zip/${encodeURIComponent(filename)}`);
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Greška pri preuzimanju fajla');
      }

      // Get the blob from response
      const blob = await response.blob();
      
      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      
      // Trigger download
      document.body.appendChild(link);
      link.click();
      
      // Cleanup
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      showSuccessNotification(`ZIP fajl ${filename} uspešno preuzet!`);
      console.log(`ZIP fajl ${filename} uspešno preuzet`);
    } catch (error) {
      console.error('Greška pri preuzimanju ZIP fajla:', error);
      showErrorNotification(`Greška pri preuzimanju fajla: ${error.message}`);
    }
  };

  // Function to download report
  const downloadReport = async () => {
    if (downloadParcelsList.length === 0) {
      showErrorNotification('Nema parcela za kreiranje izveštaja');
      return;
    }

    const loadingId = showLoadingNotification(`Generiranje PDF izveštaja za ${downloadParcelsList.length} parcela...`);

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/koparcele/download-report`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json; charset=utf-8',
        },
        body: JSON.stringify({
          parcels: downloadParcelsList
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        removeNotification(loadingId);
        throw new Error(errorData.error || 'Greška pri preuzimanju izveštaja');
      }

      // Get the blob from response
      const blob = await response.blob();
      removeNotification(loadingId);
      
      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'izvestaj.pdf';
      
      // Trigger download
      document.body.appendChild(link);
      link.click();
      
      // Cleanup
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      showSuccessNotification('PDF izveštaj uspešno preuzet!');
      console.log('Izveštaj uspešno preuzet');
    } catch (error) {
      removeNotification(loadingId);
      console.error('Greška pri preuzimanju izveštaja:', error);
      showErrorNotification(`Greška pri preuzimanju izveštaja: ${error.message}`);
    }
  };

  // Real-time download search effect
  useEffect(() => {
    if (!downloadSearchQuery.trim()) {
      setDownloadSearchResults([]);
      setShowDownloadSearchResults(false);
      return;
    }
  }, [downloadSearchQuery]);

  // Automatic download effect - triggers when zipFilename is set
  useEffect(() => {
    if (zipFilename) {
      downloadZipFile(zipFilename);
    }
  }, [zipFilename]);


  /* ====== BEGIN UTM→WGS84 HELPERS ====== */

  // 1) Transformacija jedne koordinate
  function utmToWgsCoord([x, y]) {
    return proj4('EPSG:32634', 'EPSG:4326', [x, y]); // -> [lon, lat]
  }

  // 2) Reprojekcija bilo kog GeoJSON geometry-ja
  function reprojectGeometryUTMtoWGS(geomIn) {
    if (!geomIn) return null;
    const g = typeof geomIn === 'string' ? JSON.parse(geomIn) : JSON.parse(JSON.stringify(geomIn));
    const walk = (c, depth) => (depth === 1 ? utmToWgsCoord(c) : c.map(cc => walk(cc, depth - 1)));
    switch (g.type) {
      case 'Point':            g.coordinates = walk(g.coordinates, 1); break;
      case 'MultiPoint':
      case 'LineString':       g.coordinates = walk(g.coordinates, 2); break;
      case 'MultiLineString':
      case 'Polygon':          g.coordinates = walk(g.coordinates, 3); break;
      case 'MultiPolygon':     g.coordinates = walk(g.coordinates, 4); break;
      case 'GeometryCollection': g.geometries = g.geometries.map(reprojectGeometryUTMtoWGS); break;
      default: return null;
    }
    return g;
  }

  // 3) Normalizuj šta god backend pošalje -> FeatureCollection u WGS84 + bbox
  function normalizeToFC_WGS(dataUTM) {
    let obj = typeof dataUTM === 'string' ? JSON.parse(dataUTM) : dataUTM;
    const fc = { type: 'FeatureCollection', features: [] };

    if (obj?.type === 'FeatureCollection') {
      obj.features.forEach(f => {
        const gg = reprojectGeometryUTMtoWGS(f.geometry);
        if (gg) fc.features.push({ type: 'Feature', geometry: gg, properties: f.properties || {} });
      });
    } else if (obj?.type === 'Feature') {
      const gg = reprojectGeometryUTMtoWGS(obj.geometry);
      if (gg) fc.features.push({ type: 'Feature', geometry: gg, properties: obj.properties || {} });
    } else if (obj?.type) {
      // čist Geometry
      const gg = reprojectGeometryUTMtoWGS(obj);
      if (gg) fc.features.push({ type: 'Feature', geometry: gg, properties: {} });
    } else {
      return { featureCollection: null, bounds: null };
    }

    // bbox iz FC (u WGS)
    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    const scan = (cs, d) => {
      if (d === 1) {
        const [x, y] = cs;
        if (Number.isFinite(x) && Number.isFinite(y)) {
          if (x < minX) minX = x; if (y < minY) minY = y;
          if (x > maxX) maxX = x; if (y > maxY) maxY = y;
        }
        return;
      }
      cs.forEach(c => scan(c, d - 1));
    };

    fc.features.forEach(f => {
      const t = f.geometry.type;
      if (t === 'Point') scan(f.geometry.coordinates, 1);
      else if (t === 'MultiPoint' || t === 'LineString') scan(f.geometry.coordinates, 2);
      else if (t === 'MultiLineString' || t === 'Polygon') scan(f.geometry.coordinates, 3);
      else if (t === 'MultiPolygon') scan(f.geometry.coordinates, 4);
    });

    const bounds =
      (isFinite(minX) && isFinite(minY) && isFinite(maxX) && isFinite(maxY))
        ? [[minX, minY], [maxX, maxY]]
        : null;

    return { featureCollection: fc, bounds };
  }

    /* ====== END UTM→WGS84 HELPERS ====== */

  // Helper function to fetch and display parcel geometry
  const fetchAndDisplayParcelGeometry = async (parcel) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/koparcele/geometry?brparcele=${encodeURIComponent(parcel.brparcele)}&katOpst1=${encodeURIComponent(parcel.kat_opst_1)}`);
      
      if (response.ok) {
        const result = await response.json();
        
        if (result.success && result.data) {
          const geometry = result.data;
          
          // Add geometry to map as a temporary layer
          addParcelGeometry(geometry, parcel);
          
          // Ne pozivamo zoomToParcelGeometry ovde - to će se uraditi u autoSelectParcel ili handleParcelClick
          // sa delay-om da se osigura da je geometrija dodana na mapu
          
          return true; // Success
        } else {
          console.warn('No geometry data received:', result);
          showErrorNotification(`Geometrija nije pronađena za parcelu ${parcel.brparcele}, ${parcel.kat_opst_1}`);
          return false;
        }
      } else {
        const errorText = await response.text();
        console.error('Server error:', response.status, errorText);
        showErrorNotification(`Greška servera: ${response.status}`);
        return false;
      }
    } catch (error) {
      console.error('Error fetching parcel geometry:', error);
      showErrorNotification('Greška pri dobijanju geometrije parcele');
      return false;
    }
  };

  // Checked state helper moved up to avoid TDZ issues
  const checkedFor = (layerId) => {
    if (!isTwoMapMode) {
      return !!visibilityA[layerId];
    }
    // In two-map mode, layer can only be visible on one map at a time
    const target = layerTarget[layerId] === 'B' ? 'B' : 'A';
    return target === 'B' ? !!visibilityB[layerId] : !!visibilityA[layerId];
  };

  // Funkcija za zoom na selektovanu parcelu iz sloja - moved up to avoid TDZ issues
  const zoomToSelectedParcel = useCallback(() => {
    if (!mapA.current) return;
    
    const layerId = 'selected-parcel-layer';
    const layer = mapA.current.getLayers().getArray().find(l => l.get('id') === layerId);
    if (!layer) return;
    
    const extent = layer.getSource().getExtent();
    if (!extent || extent[0] === Infinity) return;
    
    // Dinamički padding na osnovu veličine parcele
    const width = extent[2] - extent[0];  // širina u metrima
    const height = extent[3] - extent[1]; // visina u metrima
    const area = width * height;          // površina u m²
    
    // Dinamički padding - isti kao u zoomToParcelGeometry
    let paddingPercent;
    if (area < 1000) {           // Mala parcela
      paddingPercent = 0.4;      // 40% padding
    } else if (area < 10000) {   // Srednja parcela
      paddingPercent = 0.25;     // 25% padding
    } else if (area < 50000) {   // Velika parcela
      paddingPercent = 0.15;     // 15% padding
    } else {                     // Vrlo velika parcela
      paddingPercent = 0.1;      // 10% padding
    }
    
    // Izračunaj padding u pikselima
    const mapSize = mapA.current.getSize();
    const paddingPx = Math.round(Math.min(mapSize[0], mapSize[1]) * paddingPercent);
    const padding = [paddingPx, paddingPx, paddingPx, paddingPx];
    
    const view = mapA.current.getView();
    
    // Koristi fit method sa dinamičkim padding-om
    view.fit(extent, {
      duration: 1000,
      padding: padding,
      maxZoom: 20
    });
    
    // Ne zoomuj automatski mapB - korisnik kontroliše svaku mapu nezavisno
  }, []);

  // Function to automatically select and highlight parcel from backend data
  const autoSelectParcel = useCallback(async (parcel) => {
    // Enable parcels layer if not already enabled (without zooming to all parcels)
    if (!checkedFor('ko_parcele')) {
      // Set a flag to prevent auto-zoom when toggling layer for parcel selection
      window.preventParcelLayerZoom = true;
      toggleLayer('ko_parcele');
      // Clear the flag after a short delay
      setTimeout(() => {
        delete window.preventParcelLayerZoom;
      }, 100);
    }
    
    // Fetch and display geometry
    const success = await fetchAndDisplayParcelGeometry(parcel);
    
    if (success) {
      // Update search query to show selected parcel
      setSearchQuery(`${parcel.brparcele} - ${parcel.kat_opst_1}`);
      
      // Close search results
      setShowSearchResults(false);
      
      // Zoom to selected parcel sa optimizovanim padding-om
      setTimeout(() => {
        zoomToSelectedParcel();
      }, 50);
    }
    
    return success;
  }, [checkedFor, toggleLayer, fetchAndDisplayParcelGeometry, zoomToSelectedParcel]);

  // Function to handle parcel selection from external sources (e.g., backend responses)
  const handleBackendParcelSelection = useCallback(async (parcelData) => {
    // Validate parcel data
    if (!parcelData || !parcelData.brparcele || !parcelData.kat_opst_1) {
      console.error('Invalid parcel data for selection:', parcelData);
      return false;
    }
    
    // Auto-select the parcel
    const success = await autoSelectParcel(parcelData);
    
    if (success) {
      // Show notification
      
      // Optional: Show a toast notification
      // You could add a toast notification library here
    }
    
    return success;
  }, [autoSelectParcel]);

  // Handle parcel click similar to OpenLayers version
  const handleParcelClick = async (parcela) => {
    // Enable ko_parcele layer if not already enabled (like in OL version)
    if (!checkedFor('ko_parcele')) {
      // Set a flag to prevent auto-zoom when toggling layer for parcel selection
      window.preventParcelLayerZoom = true;
      toggleLayer('ko_parcele');
      // Clear the flag after a short delay
      setTimeout(() => {
        delete window.preventParcelLayerZoom;
      }, 100);
    }

    // Clear previous selection
    const layerId = 'selected-parcel-layer';
    
    if (mapA.current) {
      // Remove existing layer
      const existingLayer = mapA.current.getLayers().getArray().find(layer => layer.get('id') === layerId);
      if (existingLayer) {
        mapA.current.removeLayer(existingLayer);
      }
    }

    // Set search query (like in OL version)
    setSearchQuery(`${parcela.brparcele} - ${parcela.kat_opst_1}`);
    setSearchResults([]);
    setShowSearchResults(false);

    // Fetch geometry from backend (similar to OL version)
    const geometryUrl = `${process.env.REACT_APP_BACKEND_URL}/api/koparcele/geometry?brparcele=${encodeURIComponent(parcela.brparcele)}&katOpst1=${encodeURIComponent(parcela.kat_opst_1)}`;
    
    try {
      const response = await fetch(geometryUrl, { method: 'GET' });
      
      if (response.ok) {
        const result = await response.json();
        if (result.success && result.data) {
          const geometry = result.data;
          
          // Add geometry to map (Mapbox GL equivalent of OL's addFeature)
          addParcelGeometry(geometry, parcela);
          
          // Dodaj mali delay pre zoom-a da se geometrija stigne dodati na mapu
          setTimeout(() => {
            // Zoom to geometry bounds sa optimizovanim padding-om za parcele
            zoomToParcelGeometry(geometry);
          }, 50);
        } else {
          console.warn('No geometry data received:', result);
        }
      } else {
        console.warn('Neuspešna pretraga geometrije, status =', response.status);
      }
    } catch (error) {
      console.error('Greška prilikom dobijanja geometrije:', error);
    }
  };

  const selectParcel = useCallback(async (parcel) => {
    // Use the same logic as handleParcelClick
    await handleParcelClick(parcel);
  }, [handleParcelClick]);

  // Add parcel geometry to map (backend šalje UTM; ovde konvertujemo u WGS84)
  const addParcelGeometry = (geometryUTM, parcelaMeta) => {
    const layerId = 'selected-parcel-layer';

    try {
      const { featureCollection } = normalizeToFC_WGS(geometryUTM);
      if (!featureCollection || featureCollection.features.length === 0) {
        console.warn('Nije moguće reprojektovati/normalizovati geometriju');
        return;
      }

      if (mapA.current) {
        // Remove existing layer if it exists
        const existingLayer = mapA.current.getLayers().getArray().find(layer => layer.get('id') === layerId);
        if (existingLayer) {
          mapA.current.removeLayer(existingLayer);
        }

        // Create vector source with GeoJSON data
        const vectorSource = new VectorSource({
          features: new GeoJSON().readFeatures(featureCollection, {
            featureProjection: 'EPSG:32634'
          })
        });

        // Create vector layer with fill style
        const vectorLayer = new VectorLayer({
          source: vectorSource,
          style: new Style({
            fill: new Fill({
              color: 'rgba(0, 102, 255, 0.4)'
            }),
            stroke: new Stroke({
              color: '#0066ff',
              width: 4
            })
          }),
          updateWhileAnimating: false,
          updateWhileInteracting: false
        });

        vectorLayer.set('id', layerId);
        mapA.current.addLayer(vectorLayer);
      }
    } catch (error) {
      console.error('Greška pri dodavanju geometrije parcele na mapu:', error);
    }
  };

  // Optimizovana funkcija za zoom na parcelu sa dinamičkim padding-om
  const zoomToParcelGeometry = (geometryUTM) => {
    if (!mapA.current || !geometryUTM) return;
    
    try {
      const { bounds } = normalizeToFC_WGS(geometryUTM);
      if (!bounds) {
        console.warn('Ne mogu da izračunam granice geometrije (bbox)');
        return;
      }
      
      // Proveri da li su bounds validni
      const [sw, ne] = bounds;
      if (!sw || !ne || !Array.isArray(sw) || !Array.isArray(ne) || 
          sw.length !== 2 || ne.length !== 2 ||
          !Number.isFinite(sw[0]) || !Number.isFinite(sw[1]) ||
          !Number.isFinite(ne[0]) || !Number.isFinite(ne[1])) {
        console.warn('Nevalidni bounds:', bounds);
        return;
      }
      
      // Proveri da li su koordinate u razumnim granicama
      if (sw[0] < -180 || sw[0] > 180 || ne[0] < -180 || ne[0] > 180 ||
          sw[1] < -90 || sw[1] > 90 || ne[1] < -90 || ne[1] > 90) {
        console.warn('Koordinate van razumnih granica:', bounds);
        return;
      }
      
      // Convert bounds to extent
      const extent = transformExtent(bounds.flat(), 'EPSG:4326', 'EPSG:32634');
      
      // Dinamički padding na osnovu veličine parcele
      const width = extent[2] - extent[0];  // širina u metrima
      const height = extent[3] - extent[1]; // visina u metrima
      const area = width * height;          // površina u m²
      
      // Dinamički padding - veće parcele = manji padding, manje parcele = veći padding
      let paddingPercent;
      if (area < 1000) {           // Manje od 1000m² (mala parcela)
        paddingPercent = 0.4;      // 40% padding - dosta prostora oko parcele
      } else if (area < 10000) {   // 1000-10000m² (srednja parcela)
        paddingPercent = 0.25;     // 25% padding
      } else if (area < 50000) {   // 10000-50000m² (velika parcela)
        paddingPercent = 0.15;     // 15% padding
      } else {                     // Veće od 50000m² (vrlo velika parcela)
        paddingPercent = 0.1;      // 10% padding - parcela zauzima većinu ekrana
      }
      
      // Izračunaj padding u pikselima na osnovu veličine mape
      const mapSize = mapA.current.getSize();
      const paddingPx = Math.round(Math.min(mapSize[0], mapSize[1]) * paddingPercent);
      const padding = [paddingPx, paddingPx, paddingPx, paddingPx];
      
      const view = mapA.current.getView();
      
      // Koristi OpenLayers fit method sa dinamičkim padding-om
      view.fit(extent, {
        duration: 1000, // Malo duža animacija za lepši efekat
        padding: padding,
        maxZoom: 20 // Povećan maksimalni zoom za manje parcele
      });
      
      // Ne zoomuj automatski mapB - korisnik kontroliše svaku mapu nezavisno
      
    } catch (error) {
      console.error('Greška pri zoom-ovanju na parcelu:', error);
    }
  };


  // Close search results when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.search-container')) {
        setShowSearchResults(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Expose parcel selection function globally for external use
  useEffect(() => {
    // Main function for selecting parcel from backend data
    window.selectParcelFromBackend = handleBackendParcelSelection;
    
    // Make autoSelectParcel available globally
    window.selectParcelOnMap = autoSelectParcel;
    
    // Also expose a function to select parcel by brparcele and katOpst1
    window.selectParcelByData = async (brparcele, katOpst1) => {
      const parcelData = {
        brparcele: brparcele,
        kat_opst_1: katOpst1
      };
      return await handleBackendParcelSelection(parcelData);
    };

    // Expose function to clear selected parcel
    window.clearSelectedParcel = () => {
      const layerId = 'selected-parcel-layer';
      
      if (mapA.current) {
        // Remove the vector layer
        const existingLayer = mapA.current.getLayers().getArray().find(layer => layer.get('id') === layerId);
        if (existingLayer) {
          mapA.current.removeLayer(existingLayer);
        }
      }
      
      // Clear search query
      setSearchQuery('');
    };

    return () => {
      // Cleanup global functions
      delete window.selectParcelFromBackend;
      delete window.selectParcelOnMap;
      delete window.selectParcelByData;
      delete window.clearSelectedParcel;
    };
  }, [handleBackendParcelSelection, autoSelectParcel]);



  /* ---------- UI helpers ---------- */
  // (deprecated duplicate removed; kept memoized version above)

  const percent = Math.round(debouncedSliderPos * 100);
  const clipForB_Swipe = sliderOrientation === 'vertical'
    ? `inset(0 0 0 ${percent}%)`
    : `inset(${percent}% 0 0 0)`;

  const clipForB_Lens = `circle(${lensRadius}px at ${Math.round(lensPos.x * 100)}% ${Math.round(lensPos.y * 100)}%)`;

  /* ------------------------------ RENDER ------------------------------ */
  return (
    <div className="geoportal-container">
      {/* SEARCH BOX - Top Left */}
      <div className="search-container">
        <div className="search-input-wrapper">
          <input
            type="text"
            placeholder="Pretraži parcele..."
            value={searchQuery}
            onChange={handleSearchChange}
            className="search-input"
          />
          {searchQuery && (
            <button
              className="search-clear"
              aria-label="Obriši pretragu"
              title="Obriši pretragu"
              onClick={() => {
                setSearchQuery('');
                setSearchResults([]);
                setShowSearchResults(false);
                // ukloni selektovanu parcelu
                if (window.clearSelectedParcel) {
                  window.clearSelectedParcel();
                }
              }}
            >
              ×
            </button>
          )}
          {isSearching && (
            <div className="search-spinner">⟳</div>
          )}
        </div>
        
        {/* Search Results Dropdown */}
        {showSearchResults && searchResults.length > 0 && (
          <div className="search-results">
            {searchResults.slice(0, 10).map((parcel, index) => (
              <div
                key={parcel.id || index}
                className="search-result-item"
                onClick={() => handleParcelClick(parcel)}
              >
                <div className="parcel-number">{parcel.brparcele}</div>
                <div className="parcel-ko">{parcel.kat_opst_1}</div>
                {parcel.opstina_im && (
                  <div className="parcel-opstina">{parcel.opstina_im}</div>
                )}
              </div>
            ))}
          </div>
        )}
        
        {showSearchResults && searchResults.length === 0 && searchQuery.length >= 2 && !isSearching && (
          <div className="search-results">
            <div className="search-no-results">Nema rezultata</div>
          </div>
        )}
      </div>





      {/* RIGHT FAB - PRIMARY MENU REPLACEMENT */}
      <div className={`right-fab ${isSecondaryMenuOpen ? 'shifted' : ''}`}>
        <button
          className="fab-btn"
          title="Slojevi"
          onClick={() => openSecondary('slojevi')}
          aria-label="Slojevi"
        >
          <IconLayersRound/>
        </button>

        <button
          className="fab-btn"
          title="Merenje"
          onClick={() => openSecondary('izmeri')}
          aria-label="Merenje"
        >
          <IconRulerRound/>
        </button>

        <button
          className="fab-btn"
          title="Legenda"
          onClick={() => openSecondary('legenda')}
          aria-label="Legenda"
        >
          <IconLegendRound/>
        </button>


        <button
          className="fab-btn"
          title="Preuzimanje"
          onClick={() => openSecondary('preuzimanje')}
          aria-label="Preuzimanje"
        >
          <img
            src={process.env.PUBLIC_URL + "/slike/downloads.png"}
            alt="Preuzimanje"
            style={{
              width: '27.5px',
              height: '27.5px',
              filter: 'brightness(0)' // Makes the image black
            }}
          />
        </button>

        <button
          className="fab-btn"
          title="Uputstvo"
          onClick={() => setShowUputstvo(true)}
          aria-label="Uputstvo"
        >
          <svg fill="#000000" viewBox="0 0 427.773 427.773" style={{ width: '24px', height: '24px' }}>
            <path d="M421.504,357.406l-34.771-34.771c-3.839-3.836-8.771-5.905-13.789-6.222l-11.812-11.81 c17.547-27.896,14.189-65.271-10.076-89.535c-28.166-28.167-73.993-28.166-102.162,0c-28.162,28.166-28.162,73.995,0.004,102.161 c24.265,24.266,61.64,27.622,89.531,10.076l11.813,11.81c0.313,5.021,2.385,9.953,6.223,13.79l34.771,34.771 c8.358,8.359,21.909,8.357,30.27,0C429.861,379.318,429.863,365.766,421.504,357.406z M335.924,302.096 c-19.82,19.819-52.07,19.819-71.892,0c-19.819-19.82-19.819-52.069,0-71.891c19.818-19.82,52.066-19.82,71.892,0.001 C355.742,250.026,355.742,282.276,335.924,302.096z"/>
            <path d="M236.812,204.111c16.552-16.55,38.584-25.665,62.041-25.665s45.49,9.114,62.041,25.665 c14.13,14.128,23.068,32.854,25.173,52.733c1.618,15.289-0.81,30.8-7.021,44.875l0.211,0.211 c6.553,1.604,12.537,4.972,17.312,9.744l3.432,3.433V75.095c0-3.779-1.871-7.315-5-9.44 c-30.646-20.82-65.452-31.827-100.669-31.827c-32.813,0-65.27,9.561-94.332,27.711c-29.072-18.15-61.537-27.711-94.35-27.711 c-35.207,0-70.014,11.006-100.652,31.827C1.871,67.781,0,71.317,0,75.095v267.551c0,4.229,2.336,8.11,6.074,10.091 c3.738,1.979,8.258,1.728,11.758-0.647c26.836-18.235,57.207-27.879,87.822-27.879c30.617,0,60.994,9.644,87.84,27.881 c1.922,1.311,4.164,1.974,6.408,1.974c0.012,0,0.025-0.002,0.039-0.002c2.301,0.03,4.6-0.634,6.566-1.974 c11.48-7.801,23.611-14.016,36.129-18.59c-2.004-1.677-3.953-3.438-5.821-5.308C202.603,293.983,202.603,238.32,236.812,204.111z M233.032,110.767c21.408-14.636,50.663-16.821,66.198-16.821c15.534,0,44.791,2.186,66.198,16.821 c5.271,3.603,6.621,10.794,3.021,16.063c-3.603,5.269-10.797,6.621-16.064,3.019c-11.887-8.127-31.262-12.789-53.153-12.789 c-21.893,0-41.269,4.661-53.154,12.789c-1.994,1.363-4.266,2.018-6.512,2.018c-3.689,0-7.313-1.761-9.554-5.036 C226.411,121.562,227.764,114.37,233.032,110.767z M233.032,160.767c21.408-14.636,50.663-16.821,66.198-16.821 c15.534,0,44.791,2.186,66.198,16.821c5.271,3.603,6.621,10.794,3.021,16.063c-3.603,5.269-10.797,6.621-16.064,3.019 c-11.887-8.127-31.262-12.789-53.153-12.789c-21.893,0-41.269,4.661-53.154,12.789c-1.994,1.363-4.266,2.018-6.512,2.018 c-3.689,0-7.313-1.761-9.554-5.036C226.411,171.562,227.764,164.37,233.032,160.767z M169.986,276.831 c-3.602,5.27-10.795,6.621-16.064,3.02c-11.887-8.128-31.262-12.789-53.154-12.789s-41.268,4.661-53.154,12.789 c-1.994,1.362-4.266,2.019-6.512,2.019c-3.689,0-7.313-1.761-9.553-5.036c-3.602-5.27-2.25-12.461,3.02-16.062 c21.408-14.638,50.664-16.821,66.199-16.821s44.791,2.187,66.199,16.821C172.236,264.37,173.587,271.562,169.986,276.831z M169.986,226.831c-3.602,5.27-10.795,6.621-16.064,3.02c-11.887-8.128-31.262-12.789-53.154-12.789s-41.268,4.661-53.154,12.789 c-1.994,1.362-4.266,2.019-6.512,2.019c-3.689,0-7.313-1.761-9.553-5.036c-3.602-5.27-2.25-12.461,3.02-16.063 c21.408-14.636,50.664-16.821,66.199-16.821s44.791,2.186,66.199,16.821C172.236,214.37,173.587,221.562,169.986,226.831z M169.986,176.831c-3.602,5.269-10.795,6.621-16.064,3.019c-11.887-8.127-31.262-12.789-53.154-12.789 S59.5,171.722,47.614,179.85c-1.994,1.363-4.266,2.018-6.512,2.018c-3.689,0-7.313-1.761-9.553-5.036 c-3.602-5.269-2.25-12.461,3.02-16.063c21.408-14.636,50.664-16.821,66.199-16.821s44.791,2.186,66.199,16.821 C172.236,164.37,173.587,171.562,169.986,176.831z M169.986,126.831c-3.602,5.269-10.795,6.621-16.064,3.019 c-11.887-8.127-31.262-12.789-53.154-12.789S59.5,121.722,47.614,129.85c-1.994,1.363-4.266,2.018-6.512,2.018 c-3.689,0-7.313-1.761-9.553-5.036c-3.602-5.269-2.25-12.461,3.02-16.063c21.408-14.636,50.664-16.821,66.199-16.821 s44.791,2.186,66.199,16.821C172.236,114.37,173.587,121.562,169.986,126.831z"/>
          </svg>
        </button>
      </div>

      {/* SEKUNDARNI MENU */}
    <div
      className={`secondary-menu ${isSecondaryMenuOpen ? 'open' : ''}`}
      style={{ right : isSecondaryMenuOpen ? 0 : -360, zIndex: 1100 }}
    >
      <button className="close-menu-button" onClick={() => {
        setIsSecondaryMenuOpen(false);
        deactivateMeasurementTool();
      }}>
        Skupi&nbsp;››
      </button>

      {selectedPrimaryItem === 'slojevi' && (
        <div className="secondary-content" style={{ display: 'flex', flexDirection: 'column', height: 'calc(90vh - 60px)' }}>

          
          <div style={{ overflowY: 'auto', flex: 1, paddingRight: 6 }}>
            {/* Prikaz grupa slojeva */}
            {[...groupOrder].map(groupId => {
              const group = LAYER_GROUPS.find(g => g.id === groupId);
              if (!group) return null;
              const layersInOrder = group.layers ? (groupLayersOrder[group.id] || group.layers) : [];
              return (
              <div key={group.id} style={{ marginBottom: '20px' }}>
                {/* Zaglavlje grupe - klikabilno */}
                <div
                  onClick={() => toggleGroup(group.id)}
                  className={`group-header ${draggingType === 'group' && draggedGroupId === group.id ? 'dragging' : ''} ${draggingType === 'group' && dragOverGroup.id === group.id && dragOverGroup.position === 'above' ? 'drag-over-top' : ''} ${draggingType === 'group' && dragOverGroup.id === group.id && dragOverGroup.position === 'below' ? 'drag-over-bottom' : ''}`}
                  style={{ justifyContent: 'space-between', alignItems: 'center', gap: 8, position: 'relative' }}
                  onDragOver={(e) => {
                    e.preventDefault();
                    if (draggingType !== 'group') return;
                    const rect = e.currentTarget.getBoundingClientRect();
                    const band = Math.min(16, rect.height / 3);
                    const y = e.clientY - rect.top;
                    const position = y <= band ? 'above' : (rect.height - y <= band ? 'below' : 'middle');
                    if (position !== 'middle') setDragOverGroup({ id: group.id, position });
                  }}
                  onDrop={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    const draggedGroupId = e.dataTransfer.getData('text/group');
                    if (!draggedGroupId || draggedGroupId === group.id) return;
                    
                    // Automatically switch to dynamic mode when a group is moved
                    setUseFixedLayerOrder(false);
                    
                    setGroupOrder(prev => {
                      const next = [...prev];
                      const fromIdx = next.indexOf(draggedGroupId);
                      if (fromIdx === -1) return prev;
                      next.splice(fromIdx, 1);
                      let toIdx = next.indexOf(group.id);
                      if (toIdx === -1) return prev;
                      const placeIdx = (dragOverGroup.id === group.id && dragOverGroup.position === 'below') ? toIdx + 1 : toIdx;
                      next.splice(placeIdx, 0, draggedGroupId);
                      // After group order change, re-apply stacking
                      applyLayerDrawingOrder(mapA.current);
                      applyLayerDrawingOrder(mapB.current);
                      return next;
                    });
                    clearDragState();
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span
                      className="group-arrow"
                      aria-hidden="true"
                      style={{ fontSize: 14, display: 'inline-block', transform: openGroups[group.id] ? 'none' : 'rotate(-90deg)' }}
                    >
                      ▼
                    </span>
                    <h4 style={{ 
                      margin: 0, 
                      fontSize: '14px', 
                      fontWeight: '700', 
                      color: '#000000',
                      letterSpacing: '0.5px',
                      position: 'relative',
                    }}>
                      {group.title.toUpperCase()}
                    </h4>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                    {/* Up Arrow */}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        moveGroupUp(group.id);
                      }}
                      title="Pomeri grupu gore"
                      style={{
                        background: 'rgba(255,255,255,0.1)',
                        border: '1px solid rgba(255,255,255,0.2)',
                        borderRadius: '3px',
                        color: '#1a1a1a',
                        cursor: 'pointer',
                        padding: '2px 4px',
                        fontSize: '10px',
                        lineHeight: 1,
                        transition: 'all 0.2s ease',
                        opacity: groupOrder.indexOf(group.id) === 0 ? 0.3 : 0.6
                      }}
                      disabled={groupOrder.indexOf(group.id) === 0}
                      onMouseEnter={(e) => {
                        if (!e.target.disabled) {
                          e.target.style.opacity = '1';
                          e.target.style.background = 'rgba(255,255,255,0.2)';
                          e.target.style.transform = 'scale(1.1)';
                        }
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.opacity = e.target.disabled ? '0.3' : '0.6';
                        e.target.style.background = 'rgba(255,255,255,0.1)';
                        e.target.style.transform = 'scale(1)';
                      }}
                    >
                      ▲
                    </button>
                    
                    {/* Down Arrow */}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        moveGroupDown(group.id);
                      }}
                      title="Pomeri grupu dole"
                      style={{
                        background: 'rgba(255,255,255,0.1)',
                        border: '1px solid rgba(255,255,255,0.2)',
                        borderRadius: '3px',
                        color: '#1a1a1a',
                        cursor: 'pointer',
                        padding: '2px 4px',
                        fontSize: '10px',
                        lineHeight: 1,
                        transition: 'all 0.2s ease',
                        opacity: groupOrder.indexOf(group.id) === groupOrder.length - 1 ? 0.3 : 0.6
                      }}
                      disabled={groupOrder.indexOf(group.id) === groupOrder.length - 1}
                      onMouseEnter={(e) => {
                        if (!e.target.disabled) {
                          e.target.style.opacity = '1';
                          e.target.style.background = 'rgba(255,255,255,0.2)';
                          e.target.style.transform = 'scale(1.1)';
                        }
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.opacity = e.target.disabled ? '0.3' : '0.6';
                        e.target.style.background = 'rgba(255,255,255,0.1)';
                        e.target.style.transform = 'scale(1)';
                      }}
                    >
                      ▼
                    </button>
                    
                    {/* Drag Handle */}
                    <span
                      className="drag-handle"
                      title="Prevuci grupu za promenu redosleda"
                      style={{ 
                        fontSize: 16, 
                        opacity: 0.6, 
                        cursor: 'grab', 
                        userSelect: 'none',
                        padding: '2px 6px',
                        borderRadius: '3px',
                        transition: 'all 0.2s ease',
                        minWidth: '24px',
                        textAlign: 'center',
                        background: 'rgba(255,255,255,0.1)',
                        border: '1px solid rgba(255,255,255,0.2)'
                      }}
                      draggable
                      onMouseEnter={(e) => {
                        e.target.style.opacity = '1';
                        e.target.style.background = 'rgba(255,255,255,0.2)';
                        e.target.style.transform = 'scale(1.05)';
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.opacity = '0.6';
                        e.target.style.background = 'rgba(255,255,255,0.1)';
                        e.target.style.transform = 'scale(1)';
                      }}
                      onDragStart={(e) => {
                        e.stopPropagation();
                        e.dataTransfer.setData('text/group', group.id);
                        e.dataTransfer.effectAllowed = 'move';
                        setDraggingType('group');
                        setDraggedGroupId(group.id);
                        e.target.style.cursor = 'grabbing';
                      }}
                      onDragEnd={(e) => { 
                        e.stopPropagation(); 
                        clearDragState(); 
                        e.target.style.cursor = 'grab';
                      }}
                    >
                      ⋮⋮
                    </span>
                  </div>
                  {draggingType === 'group' && draggedGroupId !== group.id && dragOverGroup.id === group.id && dragOverGroup.position === 'above' && (
                    <div className="drop-indicator drop-top" style={{
                      position: 'absolute',
                      left: 0,
                      right: 0,
                      height: 0,
                      borderTop: '2px dashed #4A90E2',
                      top: '-6px'
                    }} />
                  )}
                  {draggingType === 'group' && draggedGroupId !== group.id && dragOverGroup.id === group.id && dragOverGroup.position === 'below' && (
                    <div className="drop-indicator drop-bottom" style={{
                      position: 'absolute',
                      left: 0,
                      right: 0,
                      height: 0,
                      borderTop: '2px dashed #4A90E2',
                      bottom: '-6px'
                    }} />
                  )}
                </div>

                {/* Slojevi u grupi - prikazuju se samo ako je grupa otvorena */}
                {openGroups[group.id] && (
                  <ul
                    className={`layer-toggle-list ${draggingType === 'layer' && dragOverLayer.groupId === group.id && dragOverLayer.layerId == null && dragOverLayer.position === 'top' ? 'drag-list-over-top' : ''} ${draggingType === 'layer' && dragOverLayer.groupId === group.id && dragOverLayer.layerId == null && dragOverLayer.position === 'bottom' ? 'drag-list-over-bottom' : ''}`}
                    style={{ marginLeft: '12px' }}
                    onDragOver={(e) => {
                      e.preventDefault();
                      if (draggingType !== 'layer') return;
                      const rect = e.currentTarget.getBoundingClientRect();
                      const y = e.clientY - rect.top;
                      const topBand = Math.min(24, rect.height / 2);
                      const bottomBand = topBand;
                      if (y <= topBand) {
                        setDragOverLayer({ groupId: group.id, layerId: null, position: 'top' });
                      } else if (rect.height - y <= bottomBand) {
                        setDragOverLayer({ groupId: group.id, layerId: null, position: 'bottom' });
                      }
                    }}
                    onDrop={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      const draggedLayerId = e.dataTransfer.getData('text/layer');
                      if (!draggedLayerId) return;
                      if (!(dragOverLayer.groupId === group.id && dragOverLayer.layerId == null)) return;
                      
                      // Automatically switch to dynamic mode when a layer is moved
                      setUseFixedLayerOrder(false);
                      
                      setGroupLayersOrder(prev => {
                        const current = [...(prev[group.id] || group.layers)];
                        const fromIdx = current.indexOf(draggedLayerId);
                        if (fromIdx === -1) return prev;
                        current.splice(fromIdx, 1);
                        if (dragOverLayer.groupId === group.id && dragOverLayer.layerId == null && dragOverLayer.position === 'bottom') {
                          current.push(draggedLayerId);
                        } else {
                          current.unshift(draggedLayerId);
                        }
                        const next = { ...prev, [group.id]: current };
                        applyLayerDrawingOrder(mapA.current);
                        applyLayerDrawingOrder(mapB.current);
                        return next;
                      });
                      clearDragState();
                    }}
                  >
                  {/* Render layers for regular groups or subgroups */}
                  {group.layers ? layersInOrder.map(layerId => {
                    const layer = WMS_LAYERS.find(l => l.id === layerId);
                    if (!layer) return null;
                    
                    return (
                      <li
                        key={layer.id}
                        className={`${checkedFor(layer.id) ? 'selected' : ''} ${draggingType === 'layer' && draggedLayerId === layer.id ? 'dragging' : ''} ${draggingType === 'layer' && dragOverLayer.groupId === group.id && dragOverLayer.layerId === layer.id && dragOverLayer.position === 'above' ? 'drag-over-top' : ''} ${draggingType === 'layer' && dragOverLayer.groupId === group.id && dragOverLayer.layerId === layer.id && dragOverLayer.position === 'below' ? 'drag-over-bottom' : ''}`}
                        draggable={false}
                        onDragOver={(e) => {
                          e.preventDefault();
                          const rect = e.currentTarget.getBoundingClientRect();
                          const band = Math.min(16, rect.height / 3);
                          const y = e.clientY - rect.top;
                          let position = 'middle';
                          if (y <= band) position = 'above';
                          else if (rect.height - y <= band) position = 'below';
                          setDragOverLayer({ groupId: group.id, layerId: layer.id, position });
                        }}
                        onDrop={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          const draggedLayerId = e.dataTransfer.getData('text/layer');
                          if (!draggedLayerId || draggedLayerId === layer.id) return;
                          
                          // Automatically switch to dynamic mode when a layer is moved
                          setUseFixedLayerOrder(false);
                          
                          setGroupLayersOrder(prev => {
                            const current = [...(prev[group.id] || group.layers)];
                            const fromIdx = current.indexOf(draggedLayerId);
                            const toIdx = current.indexOf(layer.id);
                            if (fromIdx === -1 || toIdx === -1) return prev;
                            current.splice(fromIdx, 1);
                            const placeIdx = (dragOverLayer.groupId === group.id && dragOverLayer.layerId === layer.id && dragOverLayer.position === 'below') ? toIdx + 1 : toIdx;
                            current.splice(placeIdx, 0, draggedLayerId);
                            const next = { ...prev, [group.id]: current };
                            applyLayerDrawingOrder(mapA.current);
                            applyLayerDrawingOrder(mapB.current);
                            return next;
                          });
                          clearDragState();
                        }}
                        onDragEnd={clearDragState}
                      >
                        <div style={{ width: '100%', position: 'relative' }}>
                          {draggingType === 'layer' && draggedLayerId !== layer.id && dragOverLayer.groupId === group.id && dragOverLayer.layerId === layer.id && dragOverLayer.position === 'above' && (
                            <div className="drop-indicator drop-top" style={{
                              position: 'absolute',
                              left: 0,
                              right: 0,
                              height: 0,
                              borderTop: '2px dashed #4A90E2',
                              top: -4
                            }} />
                          )}
                          {draggingType === 'layer' && draggedLayerId !== layer.id && dragOverLayer.groupId === group.id && dragOverLayer.layerId === layer.id && dragOverLayer.position === 'below' && (
                            <div className="drop-indicator drop-bottom" style={{
                              position: 'absolute',
                              left: 0,
                              right: 0,
                              height: 0,
                              borderBottom: '2px dashed #4A90E2',
                              bottom: -4
                            }} />
                          )}
                          <label style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, width: '100%' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flex: 1 }}>
                              <input
                                type="checkbox"
                                checked={checkedFor(layer.id)}
                                onChange={() => {
                                  toggleLayer(layer.id);
                                  // ako pališ sloj – odmah pokaži legendu
                                  if (!checkedFor(layer.id)) {
                                    showLegendFor(layer.id);
                                  } else {
                                    removeLegendFor(layer.id);
                                  }
                                }}
                                title="Uključi/isključi sloj"
                              />

                              {/* Klik na naziv otvara legendu */}
                              <span
                                style={{ flex: 1, cursor: 'pointer' }}
                                onClick={() => showLegendFor(layer.id)}
                                title="Prikaži legendu"
                              >
                                {layer.title}
                              </span>
                            </div>
                            
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                              {isTwoMapMode && (
                                <div className="layer-target-switch" style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                                  <label style={{ fontSize: 11 }}>
                                    <input
                                      type="radio"
                                      name={`target_${layer.id}`}
                                      value="A"
                                      checked={layerTarget[layer.id] === 'A'}
                                      onChange={() => changeLayerTarget(layer.id, 'A')}
                                    />
                                    &nbsp;A
                                  </label>
                                  <label style={{ fontSize: 11 }}>
                                    <input
                                      type="radio"
                                      name={`target_${layer.id}`}
                                      value="B"
                                      checked={layerTarget[layer.id] === 'B'}
                                      onChange={() => changeLayerTarget(layer.id, 'B')}
                                    />
                                    &nbsp;B
                                  </label>
                                </div>
                              )}
                              {/* Up Arrow */}
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  moveLayerUp(group.id, layer.id);
                                }}
                                title="Pomeri sloj gore"
                                style={{
                                  background: 'rgba(255,255,255,0.08)',
                                  border: '1px solid rgba(255,255,255,0.15)',
                                  borderRadius: '2px',
                                  color: '#000000',
                                  cursor: 'pointer',
                                  padding: '1px 3px',
                                  fontSize: '8px',
                                  lineHeight: 1,
                                  transition: 'all 0.2s ease',
                                  opacity: (groupLayersOrder[group.id] || group.layers).indexOf(layer.id) === 0 ? 0.3 : 0.6
                                }}
                                disabled={(groupLayersOrder[group.id] || group.layers).indexOf(layer.id) === 0}
                                onMouseEnter={(e) => {
                                  if (!e.target.disabled) {
                                    e.target.style.opacity = '1';
                                    e.target.style.background = 'rgba(255,255,255,0.15)';
                                    e.target.style.transform = 'scale(1.2)';
                                  }
                                }}
                                onMouseLeave={(e) => {
                                  e.target.style.opacity = e.target.disabled ? '0.3' : '0.6';
                                  e.target.style.background = 'rgba(255,255,255,0.08)';
                                  e.target.style.transform = 'scale(1)';
                                }}
                              >
                                ▲
                              </button>
                              
                              {/* Down Arrow */}
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  moveLayerDown(group.id, layer.id);
                                }}
                                title="Pomeri sloj dole"
                                style={{
                                  background: 'rgba(255,255,255,0.08)',
                                  border: '1px solid rgba(255,255,255,0.15)',
                                  borderRadius: '2px',
                                  color: '#000000',
                                  cursor: 'pointer',
                                  padding: '1px 3px',
                                  fontSize: '8px',
                                  lineHeight: 1,
                                  transition: 'all 0.2s ease',
                                  opacity: (groupLayersOrder[group.id] || group.layers).indexOf(layer.id) === (groupLayersOrder[group.id] || group.layers).length - 1 ? 0.3 : 0.6
                                }}
                                disabled={(groupLayersOrder[group.id] || group.layers).indexOf(layer.id) === (groupLayersOrder[group.id] || group.layers).length - 1}
                                onMouseEnter={(e) => {
                                  if (!e.target.disabled) {
                                    e.target.style.opacity = '1';
                                    e.target.style.background = 'rgba(255,255,255,0.15)';
                                    e.target.style.transform = 'scale(1.2)';
                                  }
                                }}
                                onMouseLeave={(e) => {
                                  e.target.style.opacity = e.target.disabled ? '0.3' : '0.6';
                                  e.target.style.background = 'rgba(255,255,255,0.08)';
                                  e.target.style.transform = 'scale(1)';
                                }}
                              >
                                ▼
                              </button>
                              
                              {/* Drag Handle */}
                              <span
                                className="drag-handle"
                                title="Prevuci sloj za promenu redosleda"
                                style={{ 
                                  fontSize: 14, 
                                  opacity: 0.6, 
                                  cursor: 'grab', 
                                  userSelect: 'none',
                                  padding: '1px 4px',
                                  borderRadius: '2px',
                                  transition: 'all 0.2s ease',
                                  minWidth: '20px',
                                  textAlign: 'center',
                                  background: 'rgba(255,255,255,0.08)',
                                  border: '1px solid rgba(255,255,255,0.15)'
                                }}
                                draggable
                                onMouseEnter={(e) => {
                                  e.target.style.opacity = '1';
                                  e.target.style.background = 'rgba(255,255,255,0.15)';
                                  e.target.style.transform = 'scale(1.1)';
                                }}
                                onMouseLeave={(e) => {
                                  e.target.style.opacity = '0.6';
                                  e.target.style.background = 'rgba(255,255,255,0.08)';
                                  e.target.style.transform = 'scale(1)';
                                }}
                                onDragStart={(e) => {
                                  e.stopPropagation();
                                  e.dataTransfer.setData('text/layer', layer.id);
                                  e.dataTransfer.effectAllowed = 'move';
                                  setDraggingType('layer');
                                  setDraggedLayerId(layer.id);
                                  e.target.style.cursor = 'grabbing';
                                }}
                                onDragEnd={(e) => { 
                                  e.stopPropagation(); 
                                  clearDragState(); 
                                  e.target.style.cursor = 'grab';
                                }}
                              >
                                ⋮⋮
                              </span>
                            </div>
                          </label>
                          
                          {/* Opacity Slider - only show when layer is checked, below the text */}
                          {checkedFor(layer.id) && (
                            <div className="opacity-slider-container">
                              <span style={{ fontSize: 11, color: '#1a1a1a', opacity: 0.8, minWidth: '60px' }}>
                                Providnost:
                              </span>
                              <input
                                type="range"
                                min="0"
                                max="1"
                                step="0.01"
                                value={getLayerOpacity(layer.id)}
                                onMouseDown={(e) => e.stopPropagation()}
                                onClick={(e) => e.stopPropagation()}
                                onChange={(e) => { e.stopPropagation(); changeLayerOpacity(layer.id, parseFloat(e.target.value)); }}
                                className="opacity-slider"
                              />
                              <span className="opacity-value">
                                {Math.round(getLayerOpacity(layer.id) * 100)}%
                              </span>
                            </div>
                          )}
                        </div>
                      </li>
                    );
                  }) : group.subgroups ? (
                    // Render subgroups
                    <div style={{ marginLeft: '0px' }}>
                      {group.subgroups.map(subgroup => {
                        const subgroupKey = `${group.id}.${subgroup.id}`;
                        const subgroupLayers = groupLayersOrder[subgroupKey] || subgroup.layers;
                        
                        return (
                          <div key={subgroup.id} style={{ marginBottom: '15px' }}>
                            {/* Subgroup header */}
                            <div
                              onClick={() => toggleSubgroup(group.id, subgroup.id)}
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 8,
                                cursor: 'pointer',
                                padding: '4px 8px',
                                background: 'rgba(255,255,255,0.05)',
                                borderRadius: '4px',
                                border: '1px solid rgba(255,255,255,0.1)'
                              }}
                            >
                              <span
                                className="group-arrow"
                                style={{
                                  fontSize: 12,
                                  display: 'inline-block',
                                  transform: openSubgroups[subgroupKey] ? 'none' : 'rotate(-90deg)',
                                  transition: 'transform 0.2s ease'
                                }}
                              >
                                ▼
                              </span>
                              <span style={{
                                margin: 0,
                                fontWeight: '600',
                                color: '#000000',
                                letterSpacing: '0.3px'
                              }}>
                                {subgroup.title}
                              </span>
                            </div>

                            {/* Subgroup layers */}
                            {openSubgroups[subgroupKey] && (
                              <ul style={{ marginTop: '8px', marginLeft: '16px' }} className="layer-toggle-list">
                                {subgroupLayers.map(layerId => {
                                  const layer = WMS_LAYERS.find(l => l.id === layerId);
                                  if (!layer) return null;
                                  
                                  return (
                                    <li key={layer.id} className={`${checkedFor(layer.id) ? 'selected' : ''}`}>
                                      <div style={{ width: '100%', minWidth: 0, overflow: 'hidden' }}>
                                      <label style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, width: '100%' }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flex: 1 }}>
                                          <input
                                            type="checkbox"
                                            checked={checkedFor(layer.id)}
                                            onChange={() => {
                                              toggleLayer(layer.id);
                                              if (!checkedFor(layer.id)) {
                                                showLegendFor(layer.id);
                                              } else {
                                                removeLegendFor(layer.id);
                                              }
                                            }}
                                            title="Uključi/isključi sloj"
                                          />
                                          <span
                                            style={{ flex: 1, cursor: 'pointer' }}
                                            onClick={() => showLegendFor(layer.id)}
                                            title="Prikaži legendu"
                                          >
                                            {layer.title}
                                          </span>
                                        </div>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                          {isTwoMapMode && (
                                            <div className="layer-target-switch" style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                                              <label style={{ fontSize: 11 }}>
                                                <input
                                                  type="radio"
                                                  name={`target_${layer.id}`}
                                                  value="A"
                                                  checked={layerTarget[layer.id] === 'A'}
                                                  onChange={() => changeLayerTarget(layer.id, 'A')}
                                                />
                                                &nbsp;A
                                              </label>
                                              <label style={{ fontSize: 11 }}>
                                                <input
                                                  type="radio"
                                                  name={`target_${layer.id}`}
                                                  value="B"
                                                  checked={layerTarget[layer.id] === 'B'}
                                                  onChange={() => changeLayerTarget(layer.id, 'B')}
                                                />
                                                &nbsp;B
                                              </label>
                                            </div>
                                          )}
                                          <button
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              moveLayerUp(subgroupKey, layer.id);
                                            }}
                                            title="Pomeri sloj gore"
                                            style={{
                                              background: 'rgba(255,255,255,0.08)',
                                              border: '1px solid rgba(255,255,255,0.15)',
                                              borderRadius: '2px',
                                              color: '#000000',
                                              cursor: 'pointer',
                                              padding: '1px 3px',
                                              fontSize: '8px',
                                              lineHeight: 1,
                                              transition: 'all 0.2s ease',
                                              opacity: subgroupLayers.indexOf(layer.id) === 0 ? 0.3 : 0.6
                                            }}
                                            disabled={subgroupLayers.indexOf(layer.id) === 0}
                                          >
                                            ▲
                                          </button>
                                          <button
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              moveLayerDown(subgroupKey, layer.id);
                                            }}
                                            title="Pomeri sloj dole"
                                            style={{
                                              background: 'rgba(255,255,255,0.08)',
                                              border: '1px solid rgba(255,255,255,0.15)',
                                              borderRadius: '2px',
                                              color: '#000000',
                                              cursor: 'pointer',
                                              padding: '1px 3px',
                                              fontSize: '8px',
                                              lineHeight: 1,
                                              transition: 'all 0.2s ease',
                                              opacity: subgroupLayers.indexOf(layer.id) === subgroupLayers.length - 1 ? 0.3 : 0.6
                                            }}
                                            disabled={subgroupLayers.indexOf(layer.id) === subgroupLayers.length - 1}
                                          >
                                            ▼
                                          </button>
                                        </div>
                                      </label>

                                      {/* Opacity Slider - only show when layer is checked */}
                                      {checkedFor(layer.id) && (
                                        <div className="opacity-slider-container">
                                          <span style={{ fontSize: 11, color: '#1a1a1a', opacity: 0.8, minWidth: '60px' }}>
                                            Providnost:
                                          </span>
                                          <input
                                            type="range"
                                            min="0"
                                            max="1"
                                            step="0.01"
                                            value={getLayerOpacity(layer.id)}
                                            onMouseDown={(e) => e.stopPropagation()}
                                            onClick={(e) => e.stopPropagation()}
                                            onChange={(e) => { e.stopPropagation(); changeLayerOpacity(layer.id, parseFloat(e.target.value)); }}
                                            className="opacity-slider"
                                          />
                                          <span className="opacity-value">
                                            {Math.round(getLayerOpacity(layer.id) * 100)}%
                                          </span>
                                        </div>
                                      )}
                                      </div>
                                    </li>
                                  );
                                })}
                              </ul>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  ) : null}
                  </ul>
                )}
              </div>
              );
            })}
          </div>
        </div>
      )}

      {selectedPrimaryItem === 'izmeri' && (
        <div className="secondary-content measurement-tools-content">
          <h3 className="measurement-tools-title">Izaberite opciju za merenje</h3>
          
          <div className="measurement-tools-container">
            <button
              className={`measurement-tool-btn ${selectedMeasurementTool === 'point' ? 'active' : ''}`}
              onClick={() => {
                const newTool = selectedMeasurementTool === 'point' ? null : 'point';
                setSelectedMeasurementTool(newTool);
                if (newTool !== null) {
                  setClickedCoordinates(null);
                  setFeatureInfo(null);
                  setFruitTreeInfo(null);
                  setIsFeatureInfoLoading(false);
                  setIsFruitTreeExpanded(false);
                  removeClickMarker();
                }
              }}
            >
              <i className="fa fa-map-marker"></i>
              <span>Tačka</span>
            </button>

            <button
              className={`measurement-tool-btn ${selectedMeasurementTool === 'line' ? 'active' : ''}`}
              onClick={() => {
                const newTool = selectedMeasurementTool === 'line' ? null : 'line';
                setSelectedMeasurementTool(newTool);
                if (newTool !== null) {
                  setClickedCoordinates(null);
                  setFeatureInfo(null);
                  setFruitTreeInfo(null);
                  setIsFeatureInfoLoading(false);
                  setIsFruitTreeExpanded(false);
                  removeClickMarker();
                }
              }}
            >
              <i className="fa fa-minus"></i>
              <span>Linija</span>
            </button>

            <button
              className={`measurement-tool-btn ${selectedMeasurementTool === 'polygon' ? 'active' : ''}`}
              onClick={() => {
                const newTool = selectedMeasurementTool === 'polygon' ? null : 'polygon';
                setSelectedMeasurementTool(newTool);
                if (newTool !== null) {
                  setClickedCoordinates(null);
                  setFeatureInfo(null);
                  setFruitTreeInfo(null);
                  setIsFeatureInfoLoading(false);
                  setIsFruitTreeExpanded(false);
                  removeClickMarker();
                }
              }}
            >
              <i className="fa fa-draw-polygon"></i>
              <span>Poligon</span>
            </button>
          </div>

          {selectedMeasurementTool && (
            <button
              className="measurement-stop-btn"
              onClick={() => deactivateMeasurementTool()}
            >
              <i className="fa fa-stop"></i>
              <span>Prekini merenje</span>
            </button>
          )}

          {selectedMeasurementTool === 'point' && (
            <div className="measurement-info">
              <p>Kliknite na mapu da bi postavili tačku(tačke)</p>
              
              {placedPoints.length > 0 && (
                <div className="points-list">
                  <h4 style={{ margin: '16px 0 12px 0', fontSize: '14px', fontWeight: '600', color: '#1a1a1a' }}>
                    Postavljene tačke ({placedPoints.length}):
                  </h4>
                  <div className="points-container">
                    {placedPoints.map((point, index) => (
                      <div key={point.id} className="point-item">
                        <div className="point-number">{index + 1}</div>
                        <div className="point-coordinates">
                          N: {point.y.toLocaleString('sr-RS', { useGrouping: false })} m | E: {point.x.toLocaleString('sr-RS', { useGrouping: false })} m
                        </div>
                        <button 
                          className="point-remove"
                          onClick={() => removePoint(point.id)}
                          title="Ukloni tačku"
                        >
                          ×
                        </button>
                      </div>
                    ))}
                  </div>
                  <button 
                    className="clear-all-points"
                    onClick={clearAllPoints}
                    style={{ 
                      marginTop: '12px', 
                      padding: '6px 12px', 
                      fontSize: '12px',
                      background: 'var(--glass-bg)',
                      border: '1px solid var(--glass-border)',
                      borderRadius: '8px',
                      color: 'var(--text-soft)',
                      cursor: 'pointer'
                    }}
                  >
                    Obriši sve tačke
                  </button>
                </div>
              )}
            </div>
          )}

          {selectedMeasurementTool === 'line' && (
            <div className="measurement-info">
              <p>
                {currentLine && currentLine.points.length > 0 
                  ? `Crtate liniju... (${currentLine.points.length} tačaka). Desni klik za završetak.`
                  : 'Kliknite na mapu da započnete crtanje linije. Desni klik za završetak.'
                }
              </p>
              
              {drawnLines.length > 0 && (
                <div className="lines-list">
                  <h4 style={{ margin: '16px 0 8px 0', fontSize: '14px', color: 'var(--text)' }}>
                    Nacrtane linije:
                  </h4>
                  <div className="lines-container">
                    {drawnLines.map((line, index) => (
                      <div key={line.id} className="line-item">
                        <div className="line-number">{index + 1}</div>
                        <div className="line-info">
                          <div style={{ fontSize: '12px', color: 'var(--text)' }}>
                            Dužina: {line.length.toLocaleString()} m
                          </div>
                          <div style={{ fontSize: '11px', color: 'var(--text-soft)' }}>
                            {line.points.length} tačaka
                          </div>
                        </div>
                        <button 
                          className="line-remove"
                          onClick={() => removeLine(line.id)}
                          title="Ukloni liniju"
                        >
                          ×
                        </button>
                      </div>
                    ))}
                  </div>
                  <button 
                    className="clear-all-lines btn-small"
                    onClick={clearAllLines}
                    style={{ 
                      marginTop: '12px', 
                      padding: '6px 12px',
                      fontSize: '12px',
                      width: '100%'
                    }}
                  >
                    Obriši sve linije
                  </button>
                </div>
              )}
            </div>
          )}

          {selectedMeasurementTool === 'polygon' && (
            <div className="measurement-info">
              <p>
                {currentPolygon && currentPolygon.points.length > 0 
                  ? `Crtate poligon... (${currentPolygon.points.length} tačaka). Dupli klik za završetak.`
                  : 'Kliknite na mapu da započnete crtanje poligona. Dupli klik za završetak.'
                }
              </p>
              
              {drawnPolygons.length > 0 && (
                <div className="polygons-list">
                  <h4 style={{ margin: '16px 0 8px 0', fontSize: '14px', color: 'var(--text)' }}>
                    Nacrtani poligoni:
                  </h4>
                  <div className="polygons-container">
                    {drawnPolygons.map((polygon, index) => (
                      <div key={polygon.id} className="polygon-item">
                        <div className="polygon-number">{index + 1}</div>
                        <div className="polygon-info">
                          <div style={{ fontSize: '12px', color: 'var(--text)' }}>
                            Površina: {polygon.area.toLocaleString()} m²
                          </div>
                          <div style={{ fontSize: '11px', color: 'var(--text-soft)' }}>
                            Obim: {polygon.perimeter.toLocaleString()} m
                          </div>
                          <div style={{ fontSize: '11px', color: 'var(--text-soft)' }}>
                            {polygon.points.length} tačaka
                          </div>
                        </div>
                        <button 
                          className="polygon-remove"
                          onClick={() => removePolygon(polygon.id)}
                          title="Ukloni poligon"
                        >
                          ×
                        </button>
                      </div>
                    ))}
                  </div>
                  <button 
                    className="clear-all-polygons btn-small"
                    onClick={clearAllPolygons}
                    style={{ 
                      marginTop: '12px', 
                      padding: '6px 12px',
                      fontSize: '12px',
                      width: '100%'
                    }}
                  >
                    Obriši sve poligone
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {selectedPrimaryItem === 'legenda' && (
        <div className="secondary-content legend-content">
          <div className="legend-header">
            <h3 className="legend-title">Legenda</h3>
            {legendInMenu && (
              <button
                className="btn-small legend-return-btn"
                onClick={() => {
                  setLegendInMenu(false);
                  setLegendOpen(true);
                }}
              >
                Vrati na levu stranu
              </button>
            )}
          </div>

          <div className="legend-scroll-container">
            {legendEntries.length > 0 ? (
              getSortedLegendEntries().map((entry, idx) => (
                <div key={entry.id} className="legend-entry-card">
                  <div className="legend-entry-header">
                    <strong className="legend-entry-title">
                      {entry.title}
                    </strong>
                    <button
                      onClick={() => removeLegendFor(entry.id)}
                      title="Ukloni legendu"
                      className="legend-remove-btn"
                    >
                      ✕
                    </button>
                  </div>

                  {entry.items && Array.isArray(entry.items) ? (
                    <div className="legend-items-container">
                      <ul className="legend-items-list">
                        {entry.items.map((it, idx) => (
                          <li key={idx} className="legend-item">
                            <span
                              className="legend-color-box"
                              style={{ background: it.color || '#ddd', ...(it.borderColor ? { border: `2px solid ${it.borderColor}` } : {}) }}
                            />
                            <span className="legend-item-text">{it.title}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ) : (
                    <div className="legend-image-container">
                      <img
                        src={entry.url}
                        alt={`Legenda: ${entry.title}`}
                        className="legend-image"
                        onError={(e) => { e.currentTarget.style.display = 'none'; }}
                      />
                    </div>
                  )}
                  {idx < legendEntries.length - 1 && (
                    <div className="legend-separator" />
                  )}
                </div>
              ))
            ) : (
              <div className="legend-empty-state">
                <div className="legend-empty-icon">🗺️</div>
                <p className="legend-empty-title">Nema aktivnih legenda</p>
                <p className="legend-empty-subtitle">
                  Uključite slojeve da biste videli njihove legende
                </p>
              </div>
            )}
          </div>
        </div>
      )}

      {selectedPrimaryItem === 'preuzimanje' && (
        <div className="secondary-content" style={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 60px)', padding: '20px', marginTop: '25px' }}>
            <h3 className="download-section-title">Selekcija parcele za preuzimanje</h3>
            
            {/* Search container for download */}
            <div className="download-search-container" style={{ 
              marginBottom: showDownloadSearchResults ? '320px' : '15px'
            }}>
              <div className="search-input-wrapper">
                <input
                  type="text"
                  placeholder="Pretraži parcele za preuzimanje..."
                  value={downloadSearchQuery}
                  onChange={handleDownloadSearchChange}
                  className="search-input"
                />
                {downloadSearchQuery && (
                  <button
                    className="search-clear"
                    aria-label="Obriši pretragu"
                    title="Obriši pretragu"
                  onClick={() => {
                    setDownloadSearchQuery('');
                    setDownloadSearchResults([]);
                    setShowDownloadSearchResults(false);
                    setDownloadParcelsList([]);
                  }}
                  >
                    ×
                  </button>
                )}
                {isDownloadSearching && (
                  <div className="search-spinner">⟳</div>
                )}
              </div>
              
              {/* Search results dropdown */}
              {showDownloadSearchResults && downloadSearchResults.length > 0 && (
                <div className="download-search-results">
                  {downloadSearchResults.slice(0, 10).map((parcel, index) => (
                    <div
                      key={parcel.id || index}
                      className="download-search-result-item"
                      onClick={() => addParcelToDownloadList(parcel)}
                    >
                      <div className="download-parcel-number">{parcel.brparcele}</div>
                      <div className="download-parcel-ko">{parcel.kat_opst_1}</div>
                      {parcel.opstina_im && (
                        <div className="download-parcel-opstina">{parcel.opstina_im}</div>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {showDownloadSearchResults && downloadSearchResults.length === 0 && downloadSearchQuery.length >= 2 && !isDownloadSearching && (
                <div className="download-search-results">
                  <div className="download-search-no-results">Nema rezultata</div>
                </div>
              )}
            </div>

            {/* Download parcels list */}
            {downloadParcelsList.length > 0 && (
              <div className="download-parcels-container">
                <div className="download-parcels-header">
                  <h4 className="download-parcels-title">
                    Parcele za preuzimanje ({downloadParcelsList.length})
                  </h4>
                  <button
                    onClick={clearDownloadParcelsList}
                    className="download-clear-all-btn"
                  >
                    Obriši sve
                  </button>
                </div>
                
                <div className="download-parcels-list">
                  {downloadParcelsList.map((parcel, index) => (
                    <div
                      key={`${parcel.brparcele}-${parcel.kat_opst_1}-${index}`}
                      className="download-parcel-item"
                    >
                      <div className="download-parcel-info">
                        <div><strong>{parcel.brparcele}</strong></div>
                        <div className="download-parcel-ko-text">{parcel.kat_opst_1}</div>
                      </div>
                      <button
                        onClick={() => removeParcelFromDownloadList(parcel)}
                        className="download-remove-btn"
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
                
                {/* Download buttons */}
                <div className="download-buttons-section">
                  <div className="download-buttons-description">
                    Kliknite dugme ispod da preuzmete fajl sa parcelom ili izveštaj sa selektovanim parcelama
                  </div>
                  <div className="download-buttons-container">
                    {/* DUGME ZA SKIDANJE PARCELE */}
                    <button
                      // onClick={createZipFile}
                      onClick={openMailVerificationDialog}
                      disabled={isCreatingZip}
                      className="download-action-btn"
                    >
                      {isCreatingZip ? (
                        <>
                          <div className="download-spinner" />
                          Kreiranje ZIP-a...
                        </>
                      ) : (
                        <>
                          <IconDownloads />
                          Preuzmi parcelu
                        </>
                      )}
                    </button>
                    {/* DUGME ZA SKIDANJE IZVESTAJA */}
                    <button
                      // onClick={downloadReport}
                      onClick={() => openMailVerificationDialog('report')}
                      className="download-action-btn"
                    >
                      <IconDownloads />
                      Preuzmi izveštaj
                    </button>
                  </div>
                </div>
              </div>
            )}
        </div>
      )}
    </div>


      {/* DUGME ZA IZBOR PODLOGE (bottom-left) */}
      <button className="bottom-left-toggle-button" onClick={() => setIsBottomLeftOpen(p => !p)}>
        Izbor podloge
      </button>

      <div className={`bottom-left-container ${isBottomLeftOpen ? 'open' : ''}`} aria-hidden={!isBottomLeftOpen}>
        <h3>Izbor Podloge ({viewMode === 'single' ? 'Mapa A' : `Mapa ${activeMap}`})</h3>
        <ul>
          <li onClick={() => { setBaseLayerForActive('osm'); setIsBottomLeftOpen(false); }}>
            <img src={process.env.PUBLIC_URL + "/slike/osm.png"} alt="OSM" />
            <span>OSM</span>
          </li>
          <li onClick={() => { setBaseLayerForActive('google'); setIsBottomLeftOpen(false); }}>
            <img src={process.env.PUBLIC_URL + "/slike/satellite.png"} alt="Ortofoto" />
            <span>Ortofoto</span>
          </li>
        </ul>
      </div>

      {/* TOGGLE "Alati" (dno-centar) */}
      <button
        className={`tools-toggle ${isToolsOpen ? 'open' : ''}`}
        onClick={() => setIsToolsOpen(v => !v)}
        aria-expanded={isToolsOpen}
        title={isToolsOpen ? 'Sakrij alate' : 'Otvori alate'}
        style={{ zIndex: 1000 }}
      >
        <span className="tools-caret">▲</span>
        <span className="tools-label">Alati</span>
      </button>

      {/* PANEL ALATA */}
      <div className={`tools-container ${isToolsOpen ? 'open' : ''}`} aria-hidden={!isToolsOpen}>
        <h3>Režimi prikaza</h3>
        <ul className="tools-grid">
          <li>
            <button
              className="btn-small"
              onClick={() => { setViewMode('single'); setIsToolsOpen(false); }}
              title="Jedna mapa"
              aria-label="Jedna mapa"
            >
              <IconSingleMap />
            </button>
          </li>

          <li>
            <button
              className="btn-small"
              onClick={() => { setViewMode('dual'); setIsToolsOpen(false); }}
              title="Dve mape"
              aria-label="Dve mape"
            >
              <IconDualMaps orientation={layout === 'row' ? 'vertical' : 'horizontal'} />
            </button>
          </li>

          <li>
            <button
              className="btn-small"
              onClick={() => {
                setViewMode('swipe');
                // Sa deljenim view-om, sinhronizacija je automatska
                requestAnimationFrame(() => { 
                  if (mapA.current) {
                    try {
                      mapA.current.updateSize();
                    } catch (e) {
                      console.warn('Failed to resize mapA:', e);
                    }
                  }
                  if (mapB.current) {
                    try {
                      mapB.current.updateSize();
                    } catch (e) {
                      console.warn('Failed to resize mapB:', e);
                    }
                  }
                });
                setIsToolsOpen(false);
              }}
              title="Slider podela"
              aria-label="Slider podela"
            >
              <IconSwipe orientation={sliderOrientation} />
            </button>
          </li>

          <li>
            <button
              className="btn-small"
              onClick={() => {
                setViewMode('lens');
                // Sa deljenim view-om, sinhronizacija je automatska
                requestAnimationFrame(() => { 
                  if (mapA.current) {
                    try {
                      mapA.current.updateSize();
                    } catch (e) {
                      console.warn('Failed to resize mapA:', e);
                    }
                  }
                  if (mapB.current) {
                    try {
                      mapB.current.updateSize();
                    } catch (e) {
                      console.warn('Failed to resize mapB:', e);
                    }
                  }
                });
                setIsToolsOpen(false);
              }}
              title="Lupa"
              aria-label="Lupa"
            >
              <IconLens />
            </button>
          </li>

          {viewMode === 'dual' && (
            <li>
              <button
                className="btn-small"
                onClick={() => setLayout(prev => prev === 'row' ? 'column' : 'row')}
                title={layout === 'row' ? 'Promeni u horizontalnu podelu' : 'Promeni u vertikalnu podelu'}
                aria-label="Promeni orijentaciju podele"
              >
                <IconDualMaps orientation={layout === 'row' ? 'horizontal' : 'vertical'} />
              </button>
            </li>
          )}

          {viewMode === 'swipe' && (
            <li>
              <button
                className="btn-small"
                onClick={() => setSliderOrientation(prev => prev === 'vertical' ? 'horizontal' : 'vertical')}
                title={sliderOrientation === 'vertical' ? 'Horizontalni kliznik' : 'Vertikalni kliznik'}
                aria-label="Promeni smer kliznika"
              >
                <IconSwipe orientation={sliderOrientation === 'vertical' ? 'horizontal' : 'vertical'} />
              </button>
            </li>
          )}

          {viewMode === 'lens' && (
            <li>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, justifyContent: 'center' }}>
                <button className="btn-small" title="Manja lupa" onClick={() => setLensRadius(r => Math.max(60, r - 20))}>–</button>
                <span style={{ fontSize: 12, opacity: 0.8 }}>Ø {lensRadius * 2}px</span>
                <button className="btn-small" title="Veća lupa" onClick={() => setLensRadius(r => Math.min(500, r + 20))}>+</button>
              </div>
            </li>
          )}
        </ul>
      </div>

      {/* PRIKAZI MAPA */}
      {viewMode === 'single' || viewMode === 'dual' ? (
        <div
          className="maps-wrapper"
          style={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            flexDirection: viewMode === 'dual' ? layout : 'column',
            gap: '4px',
            paddingBottom: '0'
          }}
        >
          {/* MAPA A */}
          <div style={{ flex: 1, position: 'relative' }}>
            <div ref={mapContainerA} className="map-container" style={{ width: '100%', height: '100%' }} />
            {viewMode === 'dual' && layout === 'row' && (
              <div
                className="map-badge large"
                style={{ position: 'absolute', top: 8, left: 'calc(100% - 60px)', pointerEvents: 'none', zIndex: 1000 }}
              >
                A
              </div>
            )}
            {viewMode === 'dual' && layout === 'column' && (
              <div
                className="map-badge large"
                style={{ position: 'absolute', left: 8, top: 'calc(100% - 60px)', pointerEvents: 'none', zIndex: 1000 }}
              >
                A
              </div>
            )}
          </div>

          {/* MAPA B (samo u dual) */}
          {viewMode === 'dual' && (
            <div style={{ flex: 1, position: 'relative' }}>
              <div ref={mapContainerB} className="map-container" style={{ width: '100%', height: '100%' }} />
              {layout === 'row' && (
                <div
                  className="map-badge large"
                  style={{ position: 'absolute', top: 8, left: '10px', pointerEvents: 'none', zIndex: 1000 }}
                >
                  B
                </div>
              )}
              {layout === 'column' && (
                <div
                  className="map-badge large"
                  style={{ position: 'absolute', left: 8, top: '10px', pointerEvents: 'none', zIndex: 1000 }}
                >
                  B
                </div>
              )}
            </div>
          )}
        </div>
      ) : viewMode === 'swipe' ? (
        // SWIPE MODE
        <div id="swipe-wrapper" style={{ position: 'absolute', inset: 0, overflow: 'hidden', paddingBottom: '0' }}>
          {/* Donja mapa (A) */}
          <div style={{ position: 'absolute', inset: 0 }}>
            <div ref={mapContainerA} className="map-container" style={{ width: '100%', height: '100%' }} />
          </div>

          {/* Gornja mapa (B) – isečena */}
          <div style={{ position: 'absolute', inset: 0, clipPath: clipForB_Swipe, WebkitClipPath: clipForB_Swipe, pointerEvents: 'auto' }}>
            <div ref={mapContainerB} className="map-container" style={{ width: '100%', height: '100%' }} />
          </div>

          {/* Ručka sa buffer zonom */}
          {sliderOrientation === 'vertical' ? (
            <div
              onMouseDown={startDrag}
              onTouchStart={startDrag}
              style={{ 
                position: 'absolute', 
                top: 0, 
                bottom: 0, 
                left: `calc(${percent}% - 15px)`, 
                width: '30px', 
                cursor: 'ew-resize', 
                zIndex: 999,
                willChange: 'left',
                transform: 'translateZ(0)', /* Force hardware acceleration */
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
              title={`Podela: ${percent}%`}
            >
              {/* Vidljiva linija slajdera */}
              <div
                style={{ 
                  width: '2px', 
                  height: '100%',
                  background: '#ffffff', 
                  boxShadow: '0 0 0 1px #00000088',
                  pointerEvents: 'none'
                }}
              />
            </div>
          ) : (
            <div
              onMouseDown={startDrag}
              onTouchStart={startDrag}
              style={{ 
                position: 'absolute', 
                left: 0, 
                right: 0, 
                top: `calc(${percent}% - 15px)`, 
                height: '30px', 
                cursor: 'ns-resize', 
                zIndex: 999,
                willChange: 'top',
                transform: 'translateZ(0)', /* Force hardware acceleration */
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
              title={`Podela: ${percent}%`}
            >
              {/* Vidljiva linija slajdera */}
              <div
                style={{ 
                  height: '2px', 
                  width: '100%',
                  background: '#ffffff', 
                  boxShadow: '0 0 0 1px #00000088',
                  pointerEvents: 'none'
                }}
              />
            </div>
          )}

          {/* Bedževi uz slider */}
          {sliderOrientation === 'vertical' ? (
            <>
              <div
                className="map-badge large"
                style={{ position: 'absolute', top: 8, left: `calc(${percent}% - 60px)`, pointerEvents: 'none' }}
              >
                A
              </div>
              <div
                className="map-badge large"
                style={{ position: 'absolute', top: 8, left: `calc(${percent}% + 10px)`, pointerEvents: 'none' }}
              >
                B
              </div>
            </>
          ) : (
            <>
              <div
                className="map-badge large"
                style={{ position: 'absolute', left: 8, top: `calc(${percent}% - 60px)`, pointerEvents: 'none' }}
              >
                A
              </div>
              <div
                className="map-badge large"
                style={{ position: 'absolute', left: 8, top: `calc(${percent}% + 10px)`, pointerEvents: 'none' }}
              >
                B
              </div>
            </>
          )}
        </div>
      ) : (
        // LENS MODE (Lupa)
        <div
          id="lens-wrapper"
          ref={lensWrapperRef}
          style={{ position: 'absolute', inset: 0, overflow: 'hidden', paddingBottom: '0', cursor: 'none' }}
        >
          {/* Donja mapa (A) */}
          <div style={{ position: 'absolute', inset: 0 }}>
            <div ref={mapContainerA} className="map-container" style={{ width: '100%', height: '100%' }} />
          </div>

          {/* Gornja mapa (B) isečena u krug */}
          <div style={{ position: 'absolute', inset: 0, clipPath: clipForB_Lens, WebkitClipPath: clipForB_Lens, pointerEvents: 'none' }}>
            <div ref={mapContainerB} className="map-container" style={{ width: '100%', height: '100%' }} />
          </div>

          {/* Bedževi uz ivicu */}
          <div className="map-badge" style={{ position: 'absolute', top: 8, left: 8 }}>
            A
          </div>

          {/* Vizuelni rub lupe */}
          <div
            style={{
              position: 'absolute',
              inset: 0,
              pointerEvents: 'none',
              borderRadius: '50%',
              boxShadow: `0 0 0 2px #ffffff, 0 0 0 3px #00000066`,
              width: `${lensRadius * 2}px`,
              height: `${lensRadius * 2}px`,
              transform: 'translate(-50%, -50%)',
              left: `${Math.round(lensPos.x * 100)}%`,
              top: `${Math.round(lensPos.y * 100)}%`,
            }}
          />

          {/* Custom cursor u centru lupe */}
          <div
            style={{
              position: 'absolute',
              left: `${Math.round(lensPos.x * 100)}%`,
              top: `${Math.round(lensPos.y * 100)}%`,
              transform: 'translate(-50%, -50%)',
              pointerEvents: 'none',
              zIndex: 1000,
              width: '20px',
              height: '20px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            {/* Glavni krug kursora */}
            <div
              style={{
                width: '12px',
                height: '12px',
                borderRadius: '50%',
                background: '#ffffff',
                border: '2px solid #000000',
                boxShadow: '0 0 4px rgba(0,0,0,0.5)'
              }}
            />
            {/* Unutrašnji krug za preciznost */}
            <div
              style={{
                position: 'absolute',
                width: '4px',
                height: '4px',
                borderRadius: '50%',
                background: '#000000',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)'
              }}
            />
          </div>
        </div>
      )}

      {/* LEGEND PANEL */}
      {legendOpen && legendEntries.length > 0 && !legendInMenu && (
        <div
          className="legend-panel"
          style={{
            position: 'absolute',
            left: 12,
            top: 80,
            // Dinamična visina: auto do maksimuma, posle skrol
            maxHeight: 'calc(100vh - 190px)',
            zIndex: 1200,
            borderRadius: 12,
            boxShadow: '0 6px 20px rgba(0,0,0,0.35)',
            width: 360,
            padding: 12,
            display: 'flex',
            flexDirection: 'column'
          }}
        >
          <div className="legend-scroll" style={{ flex: 1, overflowY: 'auto', padding: 6, borderRadius: 12 }}>
          {getSortedLegendEntries().map((entry, idx) => (
            <div key={entry.id} style={{ marginBottom: 12 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                <strong className="legend-title" style={{ lineHeight: 1.2, flex: 1 }}>
                  {entry.title}
                </strong>
                <button
                  onClick={() => removeLegendFor(entry.id)}
                  className="btn-small"
                  title="Ukloni legendu"
                  style={{
                    border: 'none',
                    background: '#fff',
                    color: '#111',
                    borderRadius: 8,
                    padding: '4px 8px',
                    cursor: 'pointer'
                  }}
                >
                  ✕
                </button>
              </div>

              {entry.items && Array.isArray(entry.items) ? (
                <div className="legend-entry" style={{ borderRadius: 12, padding: 8 }}>
                  <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'grid', gap: 6 }}>
                    {entry.items.map((it, idx) => (
                      <li key={idx} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span style={{ width: 14, height: 14, borderRadius: 2, background: it.color || '#ddd', boxShadow: 'inset 0 0 0 1px #888', ...(it.borderColor ? { border: `2px solid ${it.borderColor}`, boxShadow: 'none' } : {}) }} />
                        <span style={{ fontSize: 14, color: '#000' }}>{it.title}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ) : (
                <div className="legend-entry" style={{ borderRadius: 12, padding: 8 }}>
                  <img
                    src={entry.url}
                    alt={`Legenda: ${entry.title}`}
                    style={{ maxWidth: '100%', height: 'auto', display: 'block' }}
                    onError={(e) => { e.currentTarget.style.display = 'none'; }}
                  />
                </div>
              )}
              {idx < legendEntries.length - 1 && (
                <div style={{ height: 1, background: '#ffffff', opacity: 0.7, margin: '8px 0', borderRadius: 999 }} />
              )}
            </div>
          ))}
          </div>

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 8 }}>
            <button 
              className="btn-small"
              onClick={() => {
                setLegendInMenu(true);
                setLegendOpen(false);
              }}
            >
              Premesti u meni
            </button>
            {getSortedLegendEntries().length > 1 && (
              <button className="btn-small" onClick={() => setLegendOpen(false)}>Zatvori sve</button>
            )}
          </div>
        </div>
      )}

      {/* GETFEATUREINFO DISPLAY - Now handled by OpenLayers overlay */}

      {/* MAP ATTRIBUTION (bottom-right, above status bar) */}
      <div
        style={{
          position: 'absolute',
          bottom: '45px',
          right: isSecondaryMenuOpen ? 'calc(var(--w-secondary) + 10px)' : '10px',
          zIndex: 1002,
          background: 'rgba(255, 255, 255, 0.6)',
          padding: '2px 6px',
          borderRadius: '4px',
          fontSize: '11px',
          color: '#333',
          transition: 'right .28s var(--easing)',
        }}
      >
        {baseLayerA === 'osm'
          ? <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noopener noreferrer" style={{ color: '#333', textDecoration: 'none' }}>© OpenStreetMap contributors</a>
          : <a href="https://www.google.com/help/legalnotices_maps/" target="_blank" rel="noopener noreferrer" style={{ color: '#333', textDecoration: 'none' }}>© Google Maps</a>
        }
      </div>

      {/* STATUS BAR (dno mape) */}
      <div
        style={{
          position: 'absolute',
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 1001,
          display: 'inline-flex',
          alignItems: 'center',
          gap: 10,
          padding: '8px 12px',
          borderRadius: 0,
          background: 'rgba(255, 255, 255, 0.2)',
          backdropFilter: 'saturate(180%) blur(20px)',
          WebkitBackdropFilter: 'saturate(180%) blur(30px)',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
          color: '#1a1a1a',
        }}
      >
        <div style={{ fontSize: 14, fontWeight: 700, color: '#1a1a1a' }}>32634 UTM 34N</div>
        <div style={{ fontSize: 14, fontWeight: 700, color: '#1a1a1a' }}>
          {mouseUtm ? `N: ${mouseUtm.y.toLocaleString('sr-RS', { useGrouping: false })} m  |  E: ${mouseUtm.x.toLocaleString('sr-RS', { useGrouping: false })} m` : 'Pomeri miš preko mape'}
        </div>
        <div style={{ fontSize: 14, fontWeight: 700, color: '#1a1a1a' }}>{scaleText}</div>
      </div>

      {/* ZOOM CONTROLS - bottom right */}
      <div
        style={{
          position: 'absolute',
          bottom: '80px',
          right: isSecondaryMenuOpen ? 'calc(var(--w-secondary) + 50px)' : '50px',
          display: 'flex',
          flexDirection: 'row',
          gap: '8px',
          zIndex: 1000,
          transition: 'right .28s var(--easing)'
        }}
      >


        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          <div style={{ display: 'flex', flexDirection: 'row', gap: '8px' }}>
            <button
              onClick={() => {
                const activeMapObj = activeMap === 'A' ? mapA.current : mapB.current;
                
                if (isLocationActive) {
                  // If location is active, remove the marker from both maps
                  const existingMarkerA = mapA.current?.getLayers().getArray().find(layer => layer.get('id') === 'user-location-marker');
                  const existingMarkerB = mapB.current?.getLayers().getArray().find(layer => layer.get('id') === 'user-location-marker');
                  if (existingMarkerA) {
                    mapA.current.removeLayer(existingMarkerA);
                  }
                  if (existingMarkerB) {
                    mapB.current.removeLayer(existingMarkerB);
                  }
                  setIsLocationActive(false);
                } else {
                  // If location is not active, get location and add marker to active map
                  if (navigator.geolocation) {
                    navigator.geolocation.getCurrentPosition(
                      (position) => {
                        if (activeMapObj) {
                          const view = activeMapObj.getView();
                          const [lng, lat] = [position.coords.longitude, position.coords.latitude];
                          const coordinate = transform([lng, lat], 'EPSG:4326', 'EPSG:32634');
                          
                          // Remove existing location marker from both maps if any
                          const existingMarkerA = mapA.current?.getLayers().getArray().find(layer => layer.get('id') === 'user-location-marker');
                          const existingMarkerB = mapB.current?.getLayers().getArray().find(layer => layer.get('id') === 'user-location-marker');
                          if (existingMarkerA) {
                            mapA.current.removeLayer(existingMarkerA);
                          }
                          if (existingMarkerB) {
                            mapB.current.removeLayer(existingMarkerB);
                          }
                          
                          // Create red dot marker
                          const markerLayer = new VectorLayer({
                            id: 'user-location-marker',
                            source: new VectorSource({
                              features: [
                                new Feature({
                                  geometry: new Point(coordinate)
                                })
                              ]
                            }),
                            style: new Style({
                              image: new CircleStyle({
                                radius: 8,
                                fill: new Fill({
                                  color: '#ff0000'
                                }),
                                stroke: new Stroke({
                                  color: '#ffffff',
                                  width: 2
                                })
                              })
                            }),
                            zIndex: 1000
                          });
                          
                          activeMapObj.addLayer(markerLayer);
                          setIsLocationActive(true);
                          
                          // Animiraj pomeranje i zoomiranje sa glatkom animacijom
                          const targetScale = 500; // 1:500 za vrlo detaljnu lokaciju
                          const targetResolution = targetScale * 0.00028; // Konverzija scale-a u rezoluciju
                          
                          view.animate({
                            center: coordinate,
                            resolution: targetResolution,
                            duration: 1500,
                            easing: (t) => t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t // easeInOutQuad za glatku animaciju
                          });
                        }
                      },
                      (error) => {
                        console.error('Greška pri dobijanju lokacije:', error);
                        showErrorNotification('Nije moguće dobiti vašu lokaciju. Proverite dozvole za lokaciju.');
                      }
                    );
                  } else {
                    showErrorNotification('Vaš browser ne podržava geolokaciju.');
                  }
                }
              }}
              style={{
                width: '40px',
                height: '40px',
                border: '1px solid rgba(0, 0, 0, 0.2)',
                borderRadius: '8px',
                background: 'rgba(255, 255, 255, 0.5)',
                backdropFilter: 'saturate(180%) blur(20px)',
                WebkitBackdropFilter: 'saturate(180%) blur(20px)',
                color: '#000000',
                fontSize: '16px',
                fontWeight: 'bold',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'all 0.2s ease',
                boxShadow: '0 4px 16px rgba(0, 0, 0, 0.1)'
              }}
              onMouseEnter={(e) => {
                e.target.style.background = 'rgba(255, 255, 255, 0.7)';
                e.target.style.transform = 'translateY(-1px)';
              }}
              onMouseLeave={(e) => {
                e.target.style.background = 'rgba(255, 255, 255, 0.5)';
                e.target.style.transform = 'translateY(0)';
              }}
              title={isLocationActive ? "Sakrij lokaciju" : "Moja lokacija"}
            >
              📍
            </button>
            
            <button
              onClick={() => {
                const activeMapObj = activeMap === 'A' ? mapA.current : mapB.current;
                if (activeMapObj) {
                  const view = activeMapObj.getView();
                  const currentResolution = view.getResolution();
                  const newResolution = currentResolution * 0.5; // Uvećaj (manji resolution = veći zoom)
                  view.animate({
                    resolution: newResolution,
                    duration: 250
                  });
                }
              }}
              style={{
                width: '40px',
                height: '40px',
                border: '1px solid rgba(0, 0, 0, 0.2)',
                borderRadius: '8px',
                background: 'rgba(255, 255, 255, 0.5)',
                backdropFilter: 'saturate(180%) blur(20px)',
                WebkitBackdropFilter: 'saturate(180%) blur(20px)',
                color: '#000000',
                fontSize: '18px',
                fontWeight: 'bold',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'all 0.2s ease',
                boxShadow: '0 4px 16px rgba(0, 0, 0, 0.1)'
              }}
              onMouseEnter={(e) => {
                e.target.style.background = 'rgba(255, 255, 255, 0.7)';
                e.target.style.transform = 'translateY(-1px)';
              }}
              onMouseLeave={(e) => {
                e.target.style.background = 'rgba(255, 255, 255, 0.5)';
                e.target.style.transform = 'translateY(0)';
              }}
              title="Uvećaj (Zoom In)"
            >
              +
            </button>
          </div>
          
          <div style={{ display: 'flex', flexDirection: 'row', gap: '8px' }}>
            <button
              onClick={() => {
                const activeMapObj = activeMap === 'A' ? mapA.current : mapB.current;
                if (activeMapObj) {
                  const view = activeMapObj.getView();
                  const mapSize = activeMapObj.getSize();
                  // Dinamički fit na celu Srbiju prema trenutnoj veličini ekrana
                  view.fit(SERBIA_EXTENT_32634, {
                    size: mapSize,
                    padding: [20, 20, 60, 20],
                    duration: 1000,
                    easing: (t) => t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t
                  });
                }
              }}
              style={{
                width: '40px',
                height: '40px',
                border: '1px solid rgba(0, 0, 0, 0.2)',
                borderRadius: '8px',
                background: 'rgba(255, 255, 255, 0.5)',
                backdropFilter: 'saturate(180%) blur(20px)',
                WebkitBackdropFilter: 'saturate(180%) blur(20px)',
                color: '#000000',
                fontSize: '16px',
                fontWeight: 'bold',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'all 0.2s ease',
                boxShadow: '0 4px 16px rgba(0, 0, 0, 0.1)'
              }}
              onMouseEnter={(e) => {
                e.target.style.background = 'rgba(255, 255, 255, 0.7)';
                e.target.style.transform = 'translateY(-1px)';
              }}
              onMouseLeave={(e) => {
                e.target.style.background = 'rgba(255, 255, 255, 0.5)';
                e.target.style.transform = 'translateY(0)';
              }}
              title="Vrati na početnu poziciju"
            >
              <img
                src={process.env.PUBLIC_URL + "/slike/globe.png"}
                alt="Globe"
                style={{
                  width: '20px',
                  height: '20px',
                  filter: 'brightness(0)' // Makes the image black
                }}
              />
            </button>
            
            <button
              onClick={() => {
                const activeMapObj = activeMap === 'A' ? mapA.current : mapB.current;
                if (activeMapObj) {
                  const view = activeMapObj.getView();
                  const currentResolution = view.getResolution();
                  const newResolution = currentResolution * 2; // Umanji (veći resolution = manji zoom)
                  view.animate({
                    resolution: newResolution,
                    duration: 250
                  });
                }
              }}
              style={{
                width: '40px',
                height: '40px',
                border: '1px solid rgba(0, 0, 0, 0.2)',
                borderRadius: '8px',
                background: 'rgba(255, 255, 255, 0.5)',
                backdropFilter: 'saturate(180%) blur(20px)',
                WebkitBackdropFilter: 'saturate(180%) blur(20px)',
                color: '#000000',
                fontSize: '18px',
                fontWeight: 'bold',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'all 0.2s ease',
                boxShadow: '0 4px 16px rgba(0, 0, 0, 0.1)'
              }}
              onMouseEnter={(e) => {
                e.target.style.background = 'rgba(255, 255, 255, 0.7)';
                e.target.style.transform = 'translateY(-1px)';
              }}
              onMouseLeave={(e) => {
                e.target.style.background = 'rgba(255, 255, 255, 0.5)';
                e.target.style.transform = 'translateY(0)';
              }}
              title="Umanji (Zoom Out)"
            >
              −
            </button>
          </div>
        </div>
      </div>

      {/* Notification System */}
      <div className={`notification-container ${isSecondaryMenuOpen ? 'shifted' : ''}`}>
        {notifications.map((notification) => (
          <div
            key={notification.id}
            className={`notification-item ${notification.type}`}
            onClick={() => removeNotification(notification.id)}
          >
            <div className="notification-content">
              {notification.type === 'loading' && (
                <svg className="notification-spinner" width="18" height="18" viewBox="0 0 18 18" fill="none">
                  <circle cx="9" cy="9" r="7" stroke="rgba(255,255,255,0.3)" strokeWidth="2.5" />
                  <path d="M9 2a7 7 0 0 1 7 7" stroke="white" strokeWidth="2.5" strokeLinecap="round" />
                </svg>
              )}
              {notification.type === 'success' && (
                <span className="notification-icon">✓</span>
              )}
              {notification.type === 'error' && (
                <span className="notification-icon">✗</span>
              )}
              <span>{notification.message}</span>
            </div>
            <div className="notification-close">
              ×
            </div>
          </div>
        ))}
      </div>

      {/* Mail verification dialog (komponenta će biti implementirana naknadno) */}
      {/* {isMailDialogOpen && (
        <MailVerificationDialog
          mode={mailDialogMode}
          parcels={downloadParcelsList}
          onClose={() => setIsMailDialogOpen(false)}
        />
      )} */}
      {isMailDialogOpen && (
        <MailVerificationDialog
          mode={mailDialogMode}
          parcels={downloadParcelsList}
          onClose={() => setIsMailDialogOpen(false)}
          onConfirm={mailDialogMode === 'report' ? handleReportConfirm : handleZipConfirm}
        />
      )}

      {showUputstvo && (
        <div className="uputstvo-overlay">
          <button className="uputstvo-back-btn" onClick={() => setShowUputstvo(false)}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: '18px', height: '18px' }}>
              <line x1="19" y1="12" x2="5" y2="12"/>
              <polyline points="12 19 5 12 12 5"/>
            </svg>
            Nazad
          </button>
          <div className="uputstvo-overlay-content">
            <Uputstvo />
          </div>
        </div>
      )}

    </div>
  );
};

export default Geoportal;